window.AI_PET_GENERATOR = window.AI_PET_GENERATOR || {};

window.AI_PET_GENERATOR.init = function initAIPetGenerator(root = document) {
  const section = root.querySelector('[data-section-id="ai-pet-generator-block"]');
  if (!section) return;

  if (section.__aiController && typeof section.__aiController.abort === 'function') {
    try { section.__aiController.abort(); } catch (_) {}
  }
  const controller = new AbortController();
  const { signal } = controller;
  section.__aiController = controller;

  section.dataset.aiBound = '1';
  const on = (el, type, handler, opts = {}) => {
    if (!el) return;
    el.addEventListener(type, handler, { ...opts, signal });
  };

  const rafThrottle = (fn) => {
    let scheduled = false;
    return (...args) => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => { scheduled = false; fn(...args); });
    };
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const withRetries = async (fn, { interval = 250, timeout = 8000 } = {}) => {
    const start = performance.now();
    while (true) {
      const res = fn();
      if (res) return res;
      if (performance.now() - start > timeout) return null;
      await sleep(interval);
    }
  };

  const esc = (s) =>
    window.CSS?.escape ? CSS.escape(String(s)) : String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');

  const PRODUCT_SCOPE =
    section.closest('product-info') ||
    section.closest('product-form') ||
    section.closest('#MainContent') ||
    section.closest('main') ||
    document;

  const getAiAnchor = () =>
    section.querySelector('#ai-pet-generator-an') || document.getElementById('ai-pet-generator-an');

  const getProductId = () => {
    const an = getAiAnchor();
    return String(an?.getAttribute?.('product-id') || '').trim();
  };

  const getProductHandle = () => {
    const an = getAiAnchor();
    return String(an?.getAttribute?.('product-handle') || '').trim();
  };

  const PRODUCT_ID = getProductId() || 'unknown';
  const LS_KEY       = `ai-pet-generator:${PRODUCT_ID}`;

  /* **The photo had nowhere to live across a real page navigation, and now it does
     not have to cross one.** What stood here was the hand-off: a capture-only
     instance of this configurator on the home page that took a photograph and a name,
     wrote them to IndexedDB and redirected to the product page, which read them back
     and drew. `D-221` deletes it — the home page is a call to action now
     (`sections/label-cta.liquid`) and `/products/soul-dog-wine` is the only
     configurator, entered empty.

     **It is recorded here because the constraint it broke on is a property of the
     platform rather than of that design.** A camera photograph off a modern phone is
     3-12 MB. `localStorage` does not take a `Blob` and base64 would inflate it past
     the ~5-10MB ceiling, so the photo went to IndexedDB, which does take blobs
     natively — and the write has to *complete* before the navigation, because the
     document is replaced the moment it starts. On a real phone that write does not
     finish inside a budget any visitor will wait through, and the budget is not
     optional either: an `indexedDB.open()` that never answers fires no error, so
     nothing catches it and the `await` waits for the rest of the session. The
     measured result was the failure `D-218` then dressed a spinner on: the name
     arrived (it rides `localStorage`), the photograph did not, and the visitor landed
     on an empty form having just been shown "Getting your labels ready".

     Anything that reaches for this again is choosing the same fight. The photo cannot
     cross a navigation on a phone inside a budget worth having; the way out is not a
     longer timeout, it is not making it cross one. */

  /* `ai-pet-generator-email:<productId>` used to be a second, permanent key
     recording that the email gate had been satisfied. The gate is gone, nothing
     reads or writes that key any more, and copies already in a visitor's browser
     are inert — deliberately left rather than cleared, because clearing storage a
     visitor cannot see is a change with no benefit and one failure mode. */

const FILE_PROPERTY_KEY  = '_AI Image';
const PDF_PROPERTY_KEY   = 'Download your portrait';
const ORIGINAL_PHOTO_KEY = '_Original Photo';
/* **The pack composite gets its own key, and that is the point of it.**
   `_AI Image` is the **flat printable label** — the asset a human downloads from
   the order to print a bottle. A picture of six bottles standing on a floor is not
   a printable label, so the composite must sit beside it rather than take it over.
   `snippets/cart-products.liquid` prefers this one and falls back to `_AI Image`,
   which is also what a line added before this shipped will carry.
   Written by `assets/bottle-stage.js`, which owns the stage's geometry; attached
   here, because this file owns the forms. */
const PACK_PROPERTY_KEY  = '_Pack Image';

  /* ------------------------------------------------------------------ */
  /* D-113 — one upload, three styles                                     */
  /* ------------------------------------------------------------------ */
  /*
    **The visitor no longer chooses a style, so the flow draws all of them.**

    `style` is already a request field and the response's `images` is already an
    array (`docs/GEMINI_APP_HANDOVER_EMAIL.md`), so three concurrent `POST`s to the
    same unchanged endpoint, differing only in `style`, produce three labels. The
    app is a third party's and nobody here can change it (`HQ-106`); this ships
    against it exactly as it is.

    **The order is fixed and it is the order the pills used.** Display order is
    never arrival order: whichever style the API happens to answer first must not
    decide what the visitor sees first, or the flow shuffles itself between visits
    and a returning visitor meets a different sequence than the one they learned.
  */
  const STYLE_ORDER = ['bold_portrait', 'floral', 'colorfull'];

  /* The stage's art window has a ground per style and a fourth for "no label yet".
     Before this run the ground was driven by the style pills; with no pills there
     is nothing to drive it until a label exists. */
  const NEUTRAL_GROUND = 'neutral';

  const styleRank = (value) => {
    const i = STYLE_ORDER.indexOf(String(value || ''));
    return i === -1 ? STYLE_ORDER.length : i;
  };

  /* ------------------------------------------------------------------ */
  /* upload UI (dropzone)                                                 */
  /* ------------------------------------------------------------------ */
  const input     = section.querySelector('#ai-photo');
  const dropzone  = section.querySelector('#ai-photo-dropzone');
  const filenameEl = section.querySelector('#ai-photo-filename');
  const ctaEl     = section.querySelector('#ai-photo-cta');
  const previewEl = section.querySelector('#ai-photo-preview');
  const uploadActions = section.querySelector('[data-ai-upload-actions]');

  /* The empty-state string is the merchant's, read off the markup once rather
     than written here. `ctaEl` used to be reset to the literal 'Upload file' and
     set to the literal 'Change file', so two of the four strings a visitor reads
     on this control could not be edited and neither matched the theme setting
     three lines above them. */
  const UPLOAD_EMPTY_TEXT = (ctaEl?.textContent || '').trim() || 'Choose a photo';

  /* P7-01 — the constraints the control never stated.

     8 MB is not a number invented here: it is what the tips copy already in this
     theme tells a visitor ("File too large? Take a screenshot of your photo to
     reduce the file size to under 8MB", `templates/index.json`, the custom-tabs
     block). It was written down in a panel that is `disabled` and enforced
     nowhere, so an oversized file was uploaded, sent, and failed at the server
     with a generic message after the visitor had waited. Checking it here fails
     in a tenth of a second and says which file and how big.

     `D-231` — **the ceiling is 50 MB now, because 8 MB rejected the iPhone.** The
     8 MB figure was copy, not a measurement of anything downstream, and a 48 MP
     camera photograph — or any HEIC that iOS converts to JPEG on the way through
     the picker — lands at 8-15 MB, so the most common device this traffic arrives
     on was the one most likely to be refused. Nothing downstream needs the small
     number: the generate request carries `preparePhoto`'s 1600px downscale, not
     the original, and the cart line's copy is re-encoded above `CART_FILE_BYTES`
     (`prepareOriginalForCart`). 50 MB clears ProRAW-sized files without admitting
     something no phone decodes inside the deadline. */
  const MAX_FILE_BYTES = 50 * 1024 * 1024;

  /* `D-231` — **the cart's file upload is the real ceiling, so the cart gets a copy
     that fits under it.** `_Original Photo` rides the add-to-cart form as a
     line-item file property, and Shopify's cart upload is believed to stop at
     about 20 MB — assumed, not yet measured on this store; if a raw ~19 MB file
     fails at add-to-cart, this number comes down. Above it the line carries a
     4000px JPEG at 0.92 instead of the raw file: still print resolution for a
     label, and a few MB rather than thirty. At or under it, the original goes
     untouched, exactly as before. */
  const CART_FILE_BYTES = 20 * 1024 * 1024;
  const CART_MAX_EDGE = 4000;
  const CART_QUALITY = 0.92;
  const ACCEPTED_TYPES = /^image\/(jpe?g|png|webp|heic|heif)$/i;
  const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i;

  const formatBytes = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  };

  /* iOS hands over HEIC files with an empty `type` often enough that a MIME-only
     test rejects real photographs from the exact device most of this traffic is
     on. The extension is the fallback, not the primary. */
  const fileIsAccepted = (file) =>
    ACCEPTED_TYPES.test(file.type || '') || ACCEPTED_EXTENSIONS.test(file.name || '');

  const rejectFile = (file, message) => {
    if (input) input.value = '';
    setUIFromFile();
    try { applySameDogState(); } catch (_) {}
    showFieldError('photo', message);
    announce(message);
  };

  let lastObjectUrl = null;
  const revokePreviewUrl = () => {
    if (lastObjectUrl) { try { URL.revokeObjectURL(lastObjectUrl); } catch (_) {} lastObjectUrl = null; }
  };
  const clearPreview = () => { revokePreviewUrl(); previewEl?.removeAttribute?.('src'); };

  const setUIFromFile = () => {
    if (!input || !dropzone || !filenameEl || !ctaEl || !previewEl) return;
    const file = input.files?.[0];
    if (!file) {
      clearPreview();
      filenameEl.textContent = '';
      ctaEl.textContent = UPLOAD_EMPTY_TEXT;
      dropzone.classList.remove('has-file');
      if (uploadActions) uploadActions.hidden = true;
      return;
    }
    clearPreview();
    lastObjectUrl = URL.createObjectURL(file);
    previewEl.src = lastObjectUrl;
    /* The size beside the name is not decoration: it is the number the 50 MB
       ceiling is about, so a visitor who has just been told their last file was
       too big can see that this one is not. */
    filenameEl.textContent = `${file.name || 'Photo'} · ${formatBytes(file.size)}`;
    ctaEl.textContent = UPLOAD_EMPTY_TEXT;
    dropzone.classList.add('has-file');
    if (uploadActions) uploadActions.hidden = false;
  };

  const validateChosenFile = () => {
    const file = input?.files?.[0];
    if (!file) return true;

    if (!fileIsAccepted(file)) {
      rejectFile(file, `${file.name} is not an image we can read. Use a JPG, PNG, WEBP or HEIC.`);
      return false;
    }

    if (file.size > MAX_FILE_BYTES) {
      rejectFile(
        file,
        `${file.name} is ${formatBytes(file.size)}. The limit is ${MAX_FILE_BYTES / (1024 * 1024)} MB — a screenshot of the photo is usually small enough.`
      );
      return false;
    }

    return true;
  };

  if (input && dropzone && filenameEl && ctaEl && previewEl) {
    on(input, 'change', () => {
      if (!validateChosenFile()) return;
      setUIFromFile();
      /* `D-147`: the same-dog line is a claim about the photograph, so it is
         re-decided every time the photograph changes. Late-bound on purpose —
         `applySameDogState` is declared well below this and `setUIFromFile()` runs
         once at init, so calling it from *there* would be a temporal-dead-zone
         throw on load. Every call site here is a visitor's own action. */
      try { applySameDogState(); } catch (_) {}
      /* `D-231`: a photo over the cart's upload limit is re-encoded for the cart
         line now, while the visitor is still naming the dog, so the add-to-cart
         press does not wait on a 30 MB decode. Late-bound for the same reason. */
      try { warmOriginalForCart(); } catch (_) {}
    });
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    for (const ev of ['dragenter', 'dragover']) on(dropzone, ev, (e) => { prevent(e); dropzone.classList.add('is-dragover'); });
    for (const ev of ['dragleave', 'drop'])     on(dropzone, ev, (e) => { prevent(e); dropzone.classList.remove('is-dragover'); });
    on(dropzone, 'drop', (e) => {
      const files = e.dataTransfer?.files;
      if (!files?.length) return;
      const dt = new DataTransfer();
      /* One label, one photograph. Dropping a folder full of them used to load
         every file onto the input and silently send the first. */
      dt.items.add(files[0]);
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    signal.addEventListener('abort', revokePreviewUrl, { once: true });
    setUIFromFile();
  }

  /* P7-01: replace and remove. Neither existed — the dropzone re-opened the file
     picker and nothing said so, and there was no way at all to take a photograph
     back off the page. Both are inside the `<label>`'s sibling, not inside the
     label itself, because a button inside a label for a file input opens the
     picker on every click including the one meant to clear it. */
  if (uploadActions && input) {
    on(uploadActions, 'click', (e) => {
      const target = e.target?.closest?.('[data-ai-photo-replace], [data-ai-photo-remove]');
      if (!target) return;
      e.preventDefault();

      if (target.hasAttribute('data-ai-photo-replace')) {
        input.click();
        return;
      }

      input.value = '';
      setUIFromFile();
      try { applySameDogState(); } catch (_) {}
      showFieldError('photo', '');
      announce('Photo removed.');
      try { input.focus(); } catch (_) {}
    });
  }

  const hasSelectedImage = () => !!section.querySelector('.ai-option.selected');

  
  const isOurAiForm = (form) => {
    if (!form) return false;
    return !!(
      form.querySelector(`input[name="properties[Pet name]"]`) ||
      form.querySelector(`input[name="properties[${FILE_PROPERTY_KEY}]"]`) ||
      form.querySelector(`input[name="properties[${PDF_PROPERTY_KEY}]"]`)
    );
  };

  const findAddToCartForms = () => {
    const scope = PRODUCT_SCOPE === document ? document : PRODUCT_SCOPE;
    return Array.from(scope.querySelectorAll('form[action*="/cart/add"], form[action*="/cart/add.js"]'));
  };

  const buttonsForForm = (form) => {
    const btns = [
      form.querySelector('button[name="add"]'),
      form.querySelector('button[type="submit"]'),
      form.querySelector('input[name="add"]'),
      form.querySelector('input[type="submit"]'),
    ].filter(Boolean);
    if (form.id) btns.push(...Array.from(document.querySelectorAll(`button[form="${esc(form.id)}"], input[form="${esc(form.id)}"]`)));
    return btns;
  };

  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

  const findAddToCartButtons = () => {
    const forms = findAddToCartForms();
    let btns = forms.flatMap(buttonsForForm);
    if (!btns.length) {
      const scope = PRODUCT_SCOPE === document ? document : PRODUCT_SCOPE;
      btns = btns.concat(
        Array.from(scope.querySelectorAll('button, input[type="submit"], input[name="add"]')).filter((el) => {
          const label = `${el.getAttribute('aria-label') || ''} ${el.textContent || ''}`.trim();
          return /add to cart/i.test(label);
        })
      );
    }
    return uniq(btns);
  };

  let addButtons = [];

  /* ------------------------------------------------------------------ */
  /* P5-04: the buy button validates on click instead of going grey       */
  /* ------------------------------------------------------------------ */
  /*
    What stood here was `applyEnabled(hasSelectedImage())`, which set the
    `disabled` attribute on every add-to-cart button until a generated label
    had been chosen — so the button was grey from the moment the page loaded,
    with nothing on screen saying why. That is the review's finding verbatim:
    "In one state the 'Add to cart' button appears greyed out, likely because a
    required selection has not been made, but nothing tells the visitor what is
    missing. It should either stay active and validate on click with a clear
    message" (docs/soul-dog-wines-website-review.html:359).

    The button now stays enabled and the two guards below validate on click and
    on submit, naming the one thing still outstanding. Nothing here writes
    `disabled` any more, in either direction: the old code also force-*enabled*
    every button once a label was chosen, which would have overridden the
    `disabled` that theme/snippets/add-to-cart-button.liquid:38 renders for a
    genuinely unavailable variant. A sold-out variant keeps its disabled button,
    and that button says "Sold out", which is already an explanation.
  */

  const GUARDED_PRODUCT_ID = String(getProductId() || '').trim();
  const OWN_SECTION = section.closest('.shopify-section');

  /*
    A form is this configurator's to guard when it already carries the AI
    properties, or when it is the buy form of the very product the configurator
    configures — matched either by product id or by sitting in the same theme
    section, since the configurator and the buy button are two blocks of one
    section. Both tests are here because the id is read off an attribute the
    snippet renders and the section test needs none.

    Testing the form rather than only `isOurAiForm` matters on first load: the AI
    properties are injected the first time the visitor types a name, generates,
    or submits (`syncPropertiesToAllForms`), so on a fresh page `isOurAiForm` is
    false and on its own would let an unconfigured bottle straight into the cart
    — which until now only the `disabled` attribute was preventing. Narrowing to
    this product keeps the guard off every other cart form on the page.
  */
  const isGuardedForm = (form) => {
    if (!form || form.tagName !== 'FORM') return false;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return false;
    if (isOurAiForm(form)) return true;
    if (OWN_SECTION?.contains(form)) return true;
    const host = form.closest('product-form-component');
    return !!GUARDED_PRODUCT_ID && String(host?.dataset?.productId || '').trim() === GUARDED_PRODUCT_ID;
  };

  const prefersReducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  /* `generateButtonLabel` came out with the message that read it. The third
     requirement used to name the button by its own label, which was careful and is
     no longer what the flow wants: the recap in the review step states the same
     gap as "Draw your label.", and a guard that says something else about the same
     missing thing is a second voice rather than a more precise one. */

  /* ------------------------------------------------------------------ */
  /* the stage                                                          */
  /* ------------------------------------------------------------------ */
  /*
    `assets/bottle-stage.js` owns the left column and the review step's two
    filled-in rows. It reads the same `localStorage` bucket this file writes, so it
    can rebuild itself after a morph with no help; these calls are the *live* half —
    the name appearing letter by letter, the ground changing with the style, the
    label landing at the reveal — which storage alone cannot deliver because
    nothing writes to storage on every keystroke.

    Every call is optional-chained. The stage is a second column and a second
    file: if it is switched off in the theme editor, fails to load, or throws,
    this flow completes a purchase exactly as it did before it existed.
  */
  const stage = () => window.SDW_STAGE || null;

  /*
    The configurator's steps in flow order. Only the first unmet one is ever
    shown, so the visitor is told one thing at a time rather than handed a list.
    Each message names a single control, and `focus` puts the caret or the focus
    ring on that control so the message is followed by the way to act on it.
  */
  /*
    **The four messages are the review step's own sentences.** The recap in
    `blocks/buy-buttons.liquid` renders a name row and a label row that state their
    gap in words and link back to the control that closes it; pressing Add to cart
    with that gap open used to produce a *different* wording for the same fact —
    "Select “Draw my label” to create your label" against the recap's "Draw your
    label." One voice, two places: the strings match, so the button repeats what
    the visitor has already read instead of introducing a second phrasing of it.
    The verb is "make" since `D-114`, in both places and on the button itself.
    `assets/bottle-stage.js` paints the recap and carries the same two.
  */
  const addToCartRequirements = () => [
    {
      key: 'photo',
      field: 'photo',
      met: () => !!section.querySelector('#ai-photo')?.files?.[0],
      message: 'Add your dog’s photo.',
      focus: () => section.querySelector('#ai-photo'),
      scrollTo: () => section.querySelector('.ai-upload'),
    },
    {
      key: 'name',
      field: 'name',
      met: () => !!section.querySelector('#ai-pet-name')?.value?.trim(),
      message: 'Add your dog’s name.',
      focus: () => section.querySelector('#ai-pet-name'),
      scrollTo: () => section.querySelector('.ai-name-label'),
    },
    {
      key: 'generate',
      met: () => !!section.querySelector('.ai-option'),
      /* Plural since `D-113`, and "make" since `D-114`, when the client changed
         the button's verb. The recap row in `assets/bottle-stage.js` carries the
         identical string — one voice, two places — so changing it here without
         changing it there is the defect this comment exists to prevent. */
      message: 'Make your labels.',
      /* **Inside the stepped flow the control that makes the labels is Next, and
         this has to follow it.** Screen 1 hides `#ai-generate-btn` — the press is
         Next — and neither `focus()` nor `scrollIntoView()` does anything to a
         `display: none` element, so a guard pointing at the button would name a
         requirement and then drop the caret nowhere. That is exactly the defect
         `P5-04` fixed for this list the first time, one layer further in: then it
         was a control on a step the visitor was not on, now it is a control the
         screen deliberately does not carry.

         The bar's Next is outside `section` — it is fixed to the viewport and never
         hidden — so it is queried off the document. Above the breakpoint, and with
         the flow off, the button is the control and this falls through to it.

         `scrollTo` is unchanged and still resolves: `.product-ai-agent-buttons`
         renders on screen 1, so `sdw:reveal` finds step 3 and the screen comes up
         under the message. */
      focus: () =>
        (flowOn() && document.querySelector('[data-sdw-flow-next]')) ||
        section.querySelector('#ai-generate-btn'),
      scrollTo: () => section.querySelector('.product-ai-agent-buttons'),
    },
    {
      key: 'select',
      met: () => hasSelectedImage(),
      message: 'Choose one of your labels.',
      focus: () => section.querySelector('.ai-option'),
      scrollTo: () => section.querySelector('.ai-results'),
    },
  ];

  const firstUnmetRequirement = () => {
    /*
      A chosen label is the only thing the cart actually needs, and it can only
      exist because the photo, the name and the generate step all happened. So
      once it is there the upstream steps are never re-asked. This is also what
      keeps a reload working: localStorage restores the generated images and the
      chosen one, but no browser restores a file input, so an upstream test
      would block a visitor who had already finished.
    */
    if (hasSelectedImage()) return null;
    return addToCartRequirements().find((step) => !step.met()) || null;
  };

  /* The message surface is the theme's own one, the span that already sits
     directly above the Add to cart button and already carries the error icon
     (theme/blocks/buy-buttons.liquid:224–231). Its text node is the one
     theme/assets/product-form.js writes the quantity error into, so the two
     never render two messages at once. */
  const errorRootFor = (el) =>
    el?.closest?.('product-form-component') || (PRODUCT_SCOPE === document ? document : PRODUCT_SCOPE);

  const setAtcErrorText = (surface, message) => {
    const node = surface.childNodes[2];
    if (node && node.nodeType === 3) node.textContent = message;
    else surface.appendChild(document.createTextNode(message));
  };

  let shownRequirement = null;

  const hideAtcRequirementMessage = () => {
    if (!shownRequirement) return;
    if (shownRequirement.field) showFieldError(shownRequirement.field, '');
    shownRequirement = null;
    for (const surface of document.querySelectorAll('[ref="addToCartTextError"][data-ai-atc-guidance="1"]')) {
      setAtcErrorText(surface, '');
      surface.classList.add('hidden');
      surface.removeAttribute('data-ai-atc-guidance');
    }
  };

  const showAtcRequirementMessage = (step, origin) => {
    const root = errorRootFor(origin);
    const surface = root?.querySelector?.('[ref="addToCartTextError"]');
    if (surface) {
      setAtcErrorText(surface, step.message);
      surface.classList.remove('hidden');
      surface.setAttribute('data-ai-atc-guidance', '1');
    } else if (status) {
      status.hidden = false;
      status.textContent = step.message;
    }

    const live = root?.querySelector?.('[ref="liveRegion"]');
    if (live) { live.textContent = ''; live.textContent = step.message; }

    shownRequirement = step;

    /* `D-115` closes the photo and name fields once labels exist. Three of the
       four requirements name a control inside them, and neither `scrollIntoView`
       nor `focus` does anything to an element inside a `hidden` ancestor — so the
       fields are opened before the message points at one. Same defect and same
       shape as the `sdw:reveal` below, one container in. */
    if (step.field === 'photo' || step.field === 'name') revealFields();

    if (step.field) showFieldError(step.field, step.message);

    const anchor = step.scrollTo?.() || step.focus?.();
    /* P5-16: at 749px and below the buy box is a stepped flow, and three of the
       four controls this message can name are on a step the visitor is not on when
       they press Add to cart. Neither scrollIntoView nor focus does anything to a
       `display: none` element, so the message would name a control on a screen
       nobody can see. `assets/buy-flow-steps.js` listens for this and brings that
       control's step up first; above the breakpoint, or with the flow off, nothing
       listens and the event is inert. The four `scrollTo` targets above are the
       step roots themselves, so the listener always finds one. */
    try { anchor?.dispatchEvent?.(new CustomEvent('sdw:reveal', { bubbles: true })); } catch (_) {}
    try { anchor?.scrollIntoView?.({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' }); } catch (_) {}
    const focusEl = step.focus?.();
    if (focusEl) { try { focusEl.focus({ preventScroll: true }); } catch (_) { try { focusEl.focus(); } catch (_) {} } }
  };

  /* Clears the message once the step it named has been done, so it does not
     outlive the thing it was asking for. */
  const refreshAtcRequirementMessage = () => {
    if (!shownRequirement) return;
    const unmet = firstUnmetRequirement();
    if (!unmet || unmet.key !== shownRequirement.key) hideAtcRequirementMessage();
  };

  /* One press, one event. Both guards below run for the same click — the click
     listener fires and then the form's submit does — so an undebounced call here
     would double every add-to-cart in the funnel. */
  let lastAtcTrack = 0;
  const trackAddToCart = () => {
    const now = Date.now();
    if (now - lastAtcTrack < 800) return;
    lastAtcTrack = now;
    window.SDW_TRACK?.('add_to_cart', {
      /* The style is the *chosen label's*, not a control's — there is no style
         control any more. Same lookup the cart's `Style` property uses, so the
         funnel and the order line can never disagree. */
      style: styleOfSelected() || '',
      has_label: !!selectedImage,
      drawn: generatedImages.length,
    });
  };

  const guardAddToCart = (event, form) => {
    const unmet = firstUnmetRequirement();
    if (!unmet) { hideAtcRequirementMessage(); trackAddToCart(); return true; }
    event.preventDefault();
    event.stopPropagation();
    showAtcRequirementMessage(unmet, form);
    return false;
  };

  /*
    Both guards sit on `window` in the capture phase, and that is load-bearing
    rather than stylistic. Horizon delegates every `on:click` and `on:submit` at
    `document`, capture phase (theme/assets/component.js:216–279), so a listener
    on the form or on `document` runs *after* `AddToCartComponent.handleClick`
    and `ProductFormComponent.handleSubmit` and cannot stop them. `window` is the
    first node of the capture path, so it fires before `document` whatever order
    the two scripts registered in — and `stopPropagation()` there keeps the event
    from reaching the delegation listener at all. The pair that stood here was on
    the form and on `document`; it was only ever reached because the button was
    `disabled`, which does not dispatch a click in the first place.
  */
  const findGuardedForm = (trigger) => {
    const formAttr = trigger.getAttribute?.('form');
    return trigger.closest?.('form[action*="/cart/add"]') || (formAttr ? document.getElementById(formAttr) : null);
  };

  /* Narrower than the selector this replaced, which also took any
     `[type="submit"]` in the form. Every add-to-cart trigger the theme renders
     carries `name="add"` and the `add-to-cart-button` class
     (theme/snippets/add-to-cart-button.liquid:31–34), so matching on those two
     cannot intercept some other control that happens to submit the form. A
     keyboard submit reaches no button at all and is covered below. */
  on(window, 'click', (e) => {
    const trigger = e.target?.closest?.('button[name="add"], input[name="add"], .add-to-cart-button');
    if (!trigger) return;
    const form = findGuardedForm(trigger);
    if (!isGuardedForm(form)) return; // dejar pasar gift u otros formularios
    guardAddToCart(e, form);
  }, { capture: true });

  // El teclado envía el formulario sin pasar por el click.
  on(window, 'submit', (e) => {
    const form = e.target;
    if (!isGuardedForm(form)) return;
    guardAddToCart(e, form);
  }, { capture: true });

  const scheduleRefresh = rafThrottle(() => { addButtons = findAddToCartButtons(); refreshAtcRequirementMessage(); });

  scheduleRefresh();
  void withRetries(() => { scheduleRefresh(); return addButtons.length ? addButtons : null; }, { interval: 250, timeout: 8000 });

  on(section, 'click', (e) => { if (!e.target?.closest?.('.ai-option')) return; queueMicrotask(scheduleRefresh); });

  const resultsRootForGate = section.querySelector('.ai-results') || section;
  const moResultsGate = new MutationObserver(scheduleRefresh);
  moResultsGate.observe(resultsRootForGate, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  signal.addEventListener('abort', () => moResultsGate.disconnect(), { once: true });

  const scopeNode = PRODUCT_SCOPE === document ? document.body : PRODUCT_SCOPE;
  const moScopeGate = new MutationObserver(scheduleRefresh);
  moScopeGate.observe(scopeNode, { childList: true, subtree: true });
  signal.addEventListener('abort', () => moScopeGate.disconnect(), { once: true });

  const photoInput  = section.querySelector('#ai-photo');
  const nameInput   = section.querySelector('#ai-pet-name');

  /* **Declared here, beside the two inputs it reads, and not beside the press that
     asks the question.** It lived next to `pressGenerate` at the foot of the file
     and `hydrate()` calls `markInputsDrawn()` — so the call ran during init, before
     the `const` below it had been evaluated, and threw
     `Cannot access 'markInputsDrawn' before initialization`. That took the whole of
     `init` down with it: no `window.SDW_GENERATE`, so Next stopped being the
     generate press and silently became an ordinary advance. Caught in the rig,
     which is the only reason it is not in this paragraph as a shipped defect.
*/
  /* **What the labels on screen were drawn from.**

     `pending()` used to be `generatedImages.length === 0`, which read the brief's
     "coming back to screen 1 with labels already made: Next advances" and dropped
     the clause beside it — *only a changed photo or name, or an explicit redraw,
     draws again*. The dropped clause is the whole case: a visitor returning with
     labels in storage, attaching a **new** dog's photograph and pressing Next got
     the previous dog's labels and no generation at all. Reported from the flow.

     A signature rather than a dirty flag, because a flag cannot tell a real change
     from a keystroke that was typed and deleted, and a wrong `true` here spends a
     generation. The file's name, size and modification time identify a photograph
     without reading it; the name is its own trimmed value.

     **Set on hydrate as well as after a round, and that is what makes a reload
     work.** A file input cannot be restored, so a visitor returning to stored
     labels has an empty photo field — comparing against nothing would make
     `pending()` true and Next would fail validation on a screen whose labels are
     already made. Taking the signature at hydrate says "whatever is on screen now
     is what produced these", so an untouched return advances and an attached photo
     draws. */
  /* **The bar is inside the element the variant picker morphs**, so the attribute
     that dims its forward control cannot simply be written once and trusted —
     `D-50`. It is `assets/buy-flow-steps.js` that writes it, from the answer above,
     and that file re-applies it on the same resync it already runs after a morph.
     What this event does is cover the changes a `MutationObserver` cannot see: a
     file chosen, and every keystroke in the name field. */
  const announceGenState = () => {
    try { document.dispatchEvent(new CustomEvent('sdw:genstate')); } catch (_) {}
  };

  const inputSignature = () => {
    const file = photoInput?.files?.[0];
    const photo = file ? [file.name, file.size, file.lastModified].join('|') : '';
    return `${photo}::${(nameInput?.value || '').trim()}`;
  };

  let drawnSignature = null;
  const markInputsDrawn = () => { drawnSignature = inputSignature(); };

  const btn         = section.querySelector('#ai-generate-btn');

  const status      = section.querySelector('.ai-status');
  const fieldsBox   = section.querySelector('[data-ai-fields]');
  /* Every Try again, not one: the head copy and, since `D-265`, a foot copy under
     the carousel that only the single-column phone layout shows. They are one
     control in two places, so state, clicks and focus go to all of them;
     `expandBtn` stays the head copy for the code that names a single one. */
  const expandBtns  = [...section.querySelectorAll('[data-ai-expand-fields]')];
  const expandBtn   = expandBtns[0] || null;
  /* The copy a visitor can actually see, for focus. `offsetParent` is null under
     `display: none`, which is how the CSS chooses between them. */
  const shownExpandBtn = () => expandBtns.find((b) => b.offsetParent !== null) || expandBtn;
  const cancelBtn   = section.querySelector('[data-ai-cancel-redraw]');
  const results     = section.querySelector('.ai-results');
  const grid        = section.querySelector('.ai-grid');
  const loadingBox  = section.querySelector('.ai-loading');
  const chooseLabel = section.querySelector('.ai-choose-label');
  /* The results heading is the merchant's string and it is now the *whole* success
     sentence, so the announcement reads it off the markup rather than keeping a
     second copy in this file. `D-39`'s route, as ever: the fallback is here so a
     cleared setting cannot leave a screen-reader user with silence.

     It reads a `data-text-*` attribute rather than `textContent` because the visible
     copy is CSS-generated content now (`.ai-choose-label::before` in generator.css) —
     the mobile sentence names a swipe gesture the desktop grid doesn't have, so the
     element carries both variants and CSS picks one at the project's 750px breakpoint.
     `textContent` would read empty since there is no real text node any more, so the
     announcement mirrors that same breakpoint here instead. */
  /* Which of the results title's two wordings is true here: "Swipe through to
     pick" on a phone, where the designs are a strip, and the desktop sentence
     where they lie flat. `D-266` briefly gave the single-column phone layout the
     desktop sentence when its designs sat three across; `D-267` made them a strip
     again, so a phone is a phone. */
  const chooseLabelKey = () =>
    window.matchMedia?.('(min-width: 750px)')?.matches ? 'textDesktop' : 'textMobile';

  const chooseLabelText = () => {
    const attr = chooseLabelKey();
    return (chooseLabel?.dataset[attr] || '').trim() || 'Your labels are ready! Swipe through to pick your favorite.';
  };

  const uploadBox = photoInput ? photoInput.closest('.ai-upload') || photoInput.parentElement : null;

  if (loadingBox && !loadingBox.dataset.portalized) {
    loadingBox.dataset.portalized = '1';
    document.body.appendChild(loadingBox);
  }

  /* ------------------------------------------------------------------ */
  /* state                                                                */
  /* ------------------------------------------------------------------ */
  /* Entries are `{ url, style, round, createdAt }` since `D-113`. `style` is what
     `properties[Style]` is derived from — there is no style control left to read —
     and `round` is which press of Draw produced it, which is what makes "newest
     trio first" a property of the data rather than of the render order. */
  const generatedImages = [];

  /* **A style that failed is a thing the grid has to show, and it is not stored.**
     It holds its place in its trio so the visitor sees three cells for three
     requests, with the one that did not come back named and offered again. It is
     in memory only: a failure is about a request that has already ended, and
     restoring one after a reload would show a visitor a stale complaint about a
     draw they can no longer retry in context. Entries are
     `{ round, style, message, status }`. */
  const misses = [];

  let nextRound = 0;
  let selectedImage = null;
  let generationCount = 0;

  let _selectedImageFile        = null;
  let _selectedImageFileUrl     = null;
  let _selectedImageFilePromise = null;

  let _selectedPdfFile    = null;
  let _selectedPdfFileUrl = null;

  let dogPopupTimer = null;

  /* ------------------------------------------------------------------ */
  /* LS helpers                                                           */
  /* ------------------------------------------------------------------ */
  const safeParse = (json) => { try { return JSON.parse(json); } catch { return null; } };

  const readLS = () => {
    try { const raw = localStorage.getItem(LS_KEY); const data = raw ? safeParse(raw) : null; return data && typeof data === 'object' ? data : null; }
    catch { return null; }
  };

  const writeLS = (payload) => { try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch {} };

  /* **Nothing writes state while the restore is reading it.**

     `hydrateFromLS` below restores in an order that matters: it sets the style
     select and dispatches a `change` on it *before* it pushes the stored labels
     into `generatedImages`. That change fires this file's own style listener,
     which calls `saveStateToLS` — and at that instant `generatedImages` is still
     empty, so the write replaced the visitor's two stored labels with an empty
     list. The restore then rewrote the full state a few lines later, which is why
     the defect never showed: the bucket was empty for about a millisecond and the
     only reader was the function that had already taken a copy.

     It is not the only reader any more. `assets/bottle-stage.js` reads the same
     key to put the label back on the bottles, and it read it inside that window —
     measured: four reads of the full state, a write of an empty one, three reads
     of the empty one, then the full state restored. The bottles came back wearing
     the name and no label.

     A flag rather than a reordering of the restore, because the dispatch is load-
     bearing (`syncPillsFromSelect` and the stage's own ground both hang off it)
     and because this makes the invariant explicit: a restore is a read, and a read
     does not write. Any future listener that saves on an event the restore
     dispatches is covered by the same guard. */
  let hydrating = false;

  const saveStateToLS = () => {
    if (hydrating) return;
    writeLS({
      generatedImages: generatedImages,
      selectedImage:   selectedImage || null,
      petName:         nameInput?.value || '',
      /* **The top-level `style` is written for the readers, not for this file.**
         `assets/bottle-stage.js` reads this bucket independently and grounds the
         art window from it, and a version of that file older than this one would
         find the key missing. It is the *chosen label's* style now rather than a
         control's, which is what the key always meant. */
      style:           styleOfSelected() || '',
      generationCount: generationCount || 0,
      /* **`D-143`. Written for `assets/bottle-stage.js`, which is the only reader.**
         The stage rebuilds itself from this bucket on boot and after every morph,
         and the landed band has to come back on both paths: a buyer who reloads, or
         who comes back to the tab, should find the screen they left rather than a
         blank bottle and no account of the purchase; and `D-50`'s package morph
         returns the band to its shipped `hidden` while the line in the cart is
         still there. `null` rather than an absent key, so a stale `true` from a
         previous version cannot outlive a cleared state. */
      landed:          landedText ? { text: landedText, againLabel: AGAIN_LABEL } : null,
      sameDog:         !!sameDogActive,
      updatedAt:       Date.now(),
    });
  };

  /* ------------------------------------------------------------------ */
  /* The email gate is gone, and this is the record of what came out.     */
  /* ------------------------------------------------------------------ */
  /*
    Client direction, answering `docs/HUMAN_QUEUE.md` HQ-72 item 1: **remove it.**

    To draw a label a first-time visitor had to type an email address into the
    Shopify Forms app block. `P6-03` stopped that address being subscribed without
    asking, which left the gate collecting something the business never received —
    an ask with nothing behind it, standing in front of the one moment
    `PRODUCT.md` calls the emotional peak of the product. HQ-72 recorded the
    question rather than guessing at it. The answer is that email is asked at the
    review step and at checkout, and nowhere before the payoff.

    **The logic is removed, not just the block's placement, and that distinction is
    the whole reason this is careful.** With `gateEmailIfNoImagesStored` still in
    place and the Forms field unreachable, `validateEmailOrBlock` returned
    `{ ok: false, reason: 'email_input_not_found' }` and the Generate handler
    returned early — pressing Draw my label would have done nothing, with no error,
    on the one control the product exists for. Hiding the block without deleting
    the check is the version of this change that silently breaks the product.

    What came out: `shouldRequireEmailGate`, `isEmailAlreadySent`,
    `markEmailAsSent`, `validateEmailOrBlock`, `gateEmailIfNoImagesStored`,
    `ensureEmailErrorEl`, `toggleFieldError`, `getEmailRoot`, `isValidEmail`,
    `attachEmailInput`, `refreshEmailVisibility`, `showEmailBlock`,
    `hideEmailBlock`, `tweakShopifyFormsAfterDelay`, and `AI_NEWSLETTER`'s
    `click`, `capture`, `readEmail` and `hide`.

    **`AI_NEWSLETTER` itself, and the D-148 wait-capture ask that fed it, are gone
    too** — see the decision superseding D-52/P6-03/D-148 in
    `docs/DECISIONS.md`. Nothing in the theme subscribes anybody any more.

    The permanent `ai-pet-generator-email:<productId>` key is vestigial. It is not
    read and not written any more; copies already in a visitor's browser are inert
    and are deliberately left rather than cleared, because clearing storage a
    visitor cannot see is a change with no benefit and one failure mode.
  */

  /* ------------------------------------------------------------------ */
  /* status helpers                                                       */
  /* ------------------------------------------------------------------ */
  const clearStatus = () => {
    if (!status) return;
    status.hidden = true;
    status.textContent = '';
    status.classList.remove('ai-status--error');
    status.classList.remove('ai-status--quiet');
  };

  /* P7-01 — one live region, and everything the visitor is told goes through it.

     `.ai-status` is `role="status" aria-live="polite"` in the markup now, so a
     screen reader hears the same sentences a sighted visitor reads: an oversized
     file, a removed photo, generation starting and finishing, and every
     failure. None of it was announced before — the element was a
     bare `<p>` — which meant the entire generation state, the one part of this
     product that takes half a minute, was silent to assistive technology.

     Clearing before writing forces a re-announcement when the same sentence is
     shown twice, which a polite live region otherwise swallows. */
  /* `quiet` leaves the message in the live region and takes it off the page — for
     the one case where what it says is already printed an inch above it by
     something that is not a live region. It is not `hidden`: a hidden live region
     announces nothing, which is the whole trap. `.ai-status--quiet` is the theme's
     visually-hidden geometry, written on this element rather than by swapping in
     the utility class, because `clearStatus` and the error path both need to be
     able to take it off again. */
  const announce = (msg, { error = false, quiet = false } = {}) => {
    if (!status) return;
    const text = String(msg || '').trim();
    if (!text) { clearStatus(); return; }
    status.hidden = false;
    status.classList.toggle('ai-status--error', !!error);
    status.classList.toggle('ai-status--quiet', !!quiet && !error);
    status.textContent = '';
    status.textContent = text;
  };

  const showError = (msg) => announce(msg || 'Something went wrong. Try again.', { error: true });

  /* ------------------------------------------------------------------ */
  /* P7-01 — the wait                                                     */
  /* ------------------------------------------------------------------ */
  /*
    Generation takes twenty to forty seconds, and what stood on screen for all of
    it was a spinning bottle on a gradient and the words "Generating your
    portrait…". Nothing moved except the bottle, so there was no way to tell a
    slow request from a dead one, and on a phone on a poor connection the correct
    reading of a spinner that has been going for forty seconds is "this is
    broken".

    So the overlay carries a determinate bar and an elapsed readout.

    **`D-113` made the bar a measurement rather than an animation.** It used to run
    against the thirty seconds the copy promised and stop at 92% — honest about not
    knowing, but still an animation pretending to be a gauge. A round is now three
    requests, so the bar has three real units: it advances a third each time a style
    lands, whether that style came back or failed, because both are the same amount
    of "one of three is settled". Nothing about it is inferred from the clock.

    The elapsed readout stays, and it is the half that still runs on time, because
    three in flight can still be slow and a visitor still needs to tell a slow round
    from a dead one.

    `setInterval` rather than `requestAnimationFrame`: this runs during a network
    wait where rAF is throttled on a backgrounded tab, and a readout that freezes
    when someone checks their messages mid-generation is the failure mode this
    exists to remove. 200ms is well under the threshold where motion reads as
    stepped.
  */
  const SLOW_MS = 60000;
  const progressFill = document.querySelector('[data-ai-progress-fill]') || loadingBox?.querySelector?.('[data-ai-progress-fill]');
  const progressElapsed = document.querySelector('[data-ai-progress-elapsed]') || loadingBox?.querySelector?.('[data-ai-progress-elapsed]');

  let progressTimer = null;
  let slowAnnounced = false;
  /* `D-273`: each silent retry moves the "taking longer than usual" line out by the
     time it adds, so a retry that is going to plan is not described as slow. */
  let slowExtraMs = 0;

  /* **The estimate grows with each silent retry. `D-273`.** The overlay's second
     line is the merchant's "About 20 seconds for all three."; a retry adds about ten
     seconds to the wait, so the first number in that sentence goes up by ten per
     retry, and the line goes back to the merchant's own words when the overlay next
     opens. A sentence with no number in it is left alone. Nothing else says a retry
     happened: the bar and the clock keep going, which is the point. */
  const RETRY_ESTIMATE_STEP_S = 10;
  const loadingSub = loadingBox?.querySelector?.('.ai-loading-sub') || document.querySelector('.ai-loading-sub');
  const loadingSubBase = loadingSub ? loadingSub.textContent : '';
  const resetEstimate = () => {
    slowExtraMs = 0;
    if (loadingSub && loadingSub.textContent !== loadingSubBase) loadingSub.textContent = loadingSubBase;
  };
  const extendEstimate = (retries) => {
    slowExtraMs = retries * RETRY_ESTIMATE_STEP_S * 1000;
    if (!loadingSub) return;
    loadingSub.textContent = loadingSubBase.replace(/\d+/, (n) => String(Number(n) + retries * RETRY_ESTIMATE_STEP_S));
  };
  let progressTotal = 1;
  let progressDone = 0;

  const stopProgress = () => {
    if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
  };

  const paintProgress = () => {
    if (!progressFill) return;
    const ratio = progressTotal > 0 ? Math.min(1, progressDone / progressTotal) : 0;
    progressFill.style.inlineSize = `${Math.round(ratio * 100)}%`;
  };

  /* One unit of the round has settled. Called once per style, from both the
     success and the failure path, which is what keeps the bar from stalling at
     two-thirds on a round where one style 500s. */
  const markProgress = () => { progressDone += 1; paintProgress(); };

  const startProgress = (total) => {
    stopProgress();
    slowAnnounced = false;
    resetEstimate();
    progressTotal = Math.max(1, Number(total) || 1);
    progressDone = 0;
    paintProgress();
    const started = Date.now();

    const tick = () => {
      const elapsed = Date.now() - started;

      if (progressElapsed) {
        const seconds = Math.floor(elapsed / 1000);
        progressElapsed.textContent =
          elapsed > SLOW_MS + slowExtraMs
            ? `${seconds}s — taking longer than usual, still working`
            : `${seconds}s`;
      }

      if (elapsed > SLOW_MS + slowExtraMs && !slowAnnounced) {
        slowAnnounced = true;
        announce('Still going. This set is taking longer than usual.');
      }
    };

    tick();
    progressTimer = setInterval(tick, 200);
  };

  const showLoading = (total) => {
    if (!loadingBox) return;
    loadingBox.hidden = false; loadingBox.style.display = 'grid';
    loadingBox.style.pointerEvents = 'auto'; loadingBox.setAttribute('aria-hidden', 'false');
    startProgress(total);
  };
  const hideLoading = () => {
    stopProgress();
    if (!loadingBox) return;
    loadingBox.hidden = true; loadingBox.style.display = 'none';
    loadingBox.style.pointerEvents = 'none'; loadingBox.setAttribute('aria-hidden', 'true');
  };

  signal.addEventListener('abort', stopProgress, { once: true });

  /* **There is no draw budget any more.** This generator used to stop a visitor
     at two labels: a `MAX_GENERATIONS` constant checked in four places, a
     terminal sentence in `.ai-status` ("That is both of your labels"), a
     countdown line under the button, and a Generate that disabled itself.
     Client direction removes the cap outright — a visitor draws as many labels
     as they want, and the only thing that ends the run is choosing one and
     adding it to the cart.

     Removed with it: `showLimitMessage`, `disableGenerateDueToLimit`,
     `DRAWS_COPY`, the `[data-ai-draws]` line in `snippets/ai-generator.liquid`
     and its `.ai-generate__draws` rule. Nothing counts, nothing is trimmed on
     save or restore, and the button is never disabled for any reason but a
     draw already in flight.

     What survives is the button's change of weight, which was never about the
     cap — hence the rename. */
  /* **The wording is a span inside the button, not the button's own text.** The
     button carries a pencil beside its label, and reading or writing
     `btn.textContent` would take the icon with it — the first swap to the redraw
     wording would have deleted it. `[data-ai-generate-text]` is where both
     strings live; the fallback to the button itself is for the theme editor's
     benefit while a merchant is on an older render of the snippet. */
  const btnText = btn?.querySelector?.('[data-ai-generate-text]') || btn;
  const DRAW_LABEL = (btnText?.textContent || '').trim();
  const REDRAW_LABEL = (btn?.getAttribute?.('data-ai-redraw-label') || '').trim() || DRAW_LABEL;
  /* There were three strings here for one commit. The third was `D-218`'s busy label,
     what the button said while a capture-only press wrote the photo away and
     navigated; it went with the hand-off (`D-221`). Two strings, and both of them are
     about what a press *draws*, which is the only thing this button does now. */

  /* ------------------------------------------------------------------ */
  /* D-143 — the four strings the landed state reads                      */
  /* ------------------------------------------------------------------ */
  /*
    All four are theme settings with a `| default:` fallback in the Liquid, which
    is the pattern `D-39` set and `D-55` repeated: a merchant-typed string is a
    merchant's choice, and the fallback makes the affirmation a property of the
    theme rather than of a JSON value one cleared field can delete. They arrive as
    attributes on the step, the same route `data-ai-redraw-label` already takes, so
    no sentence a buyer reads is written in this file.

    `{name}` is the only placeholder, and only in the named affirmation. The
    anonymous one is the string the retired fourteen-second note used to carry,
    kept rather than replaced — a bottle can be bought with no name typed.
  */
  const landedStrings = section.querySelector('[data-ai-landed-strings]');
  const landedString = (key, fallback) =>
    (landedStrings?.getAttribute?.(`data-ai-${key}`) || '').trim() || fallback;

  const LANDED_NAMED   = landedString('landed-named', '{name} is in your cart.');
  const LANDED_ANON    = landedString('landed-anon', 'That one’s in your cart.');
  const AGAIN_LABEL    = landedString('landed-again', 'Create another bottle');
  const SAME_DOG_HINT  = landedString('same-dog-hint', 'Still {name}.');

  /* The sentence currently on the stage, or '' when nothing has been added. Held
     here because `saveStateToLS` writes it and two paths set it. */
  let landedText = '';

  /* **"Still Firulais." is a claim about history, not about the fields.** It is
     true only after a bottle has actually been bought and the buyer has pressed for
     the next one — which is why this is a flag rather than a test of whether a name
     is present. A first-time visitor who types a name, reloads, and finds the photo
     gone (a file input cannot be restored) has a filled name field and has bought
     nothing; telling them a dog is still on file would be the same false claim this
     row exists to remove, pointed the other way. */
  let sameDogActive = false;

  /* ------------------------------------------------------------------ */
  /* D-143 — the same-dog state                                           */
  /* ------------------------------------------------------------------ */
  /*
    **The photograph and the name surviving the add is not new; saying so is.**
    They have always survived it — the reset never touched either — but with the
    stage cleared and the grid emptied around them, the step read as a fresh form
    that happened to be pre-filled, which is the thing the client reported as
    awkward. Two readings of one screen, and no way to tell which was true.

    So the step states it: "Still Firulais.", with the way out beside it. The
    default hint is what a first-time visitor needs ("Photo and name, less than
    20 seconds") and it is exactly wrong here — the photo is chosen and the seconds
    are spent — so the two swap rather than stack.
  */
  /* Queried on each call rather than cached, because `D-50`'s morph re-renders this
     column from server HTML and a cached node would be detached after the first
     package change. */
  const defaultHint  = () => section.querySelector('[data-ai-default-hint]');
  const sameDogLine  = () => section.querySelector('[data-ai-same-dog]');
  const sameDogText  = () => section.querySelector('[data-ai-same-dog-text]');

  const clearSameDogState = () => {
    const line = sameDogLine(); const hint = defaultHint();
    if (line) line.hidden = true;
    /* Not an unconditional reveal. `applySameDogState` falls back here on a
       restore whose `sameDogActive` came back true but whose file input came
       back empty (`D-143` — a file input can never be restored), which is a
       combination hydration produces every time it restores a same-dog flag.
       That fallback means "the same-dog line isn't right for this screen," not
       "no label exists" — `syncFieldsCollapse` already decided the latter, and
       repeating its own check here keeps this from overturning that decision
       for a returning visitor whose labels are already on screen. */
    if (hint) hint.hidden = generatedImages.length > 0 && !fieldsExpanded;
  };

  const applySameDogState = () => {
    const line = sameDogLine();
    /* Nothing held means nothing to say — a buyer who removed the photo and cleared
       the name is starting from the top, and the default hint is true again. */
    const petName = (nameInput?.value || '').trim();
    if (!line || !sameDogActive) { clearSameDogState(); return; }

    /* With no name the sentence has no subject, so it is not written. The
       photograph is visible in the dropzone either way, and inventing "Still your
       dog." would be a fourth phrasing for the sake of filling a line. */
    if (!petName) { clearSameDogState(); return; }

    /* **And with no photograph it is not written either, because it would be
       false.** The flag says a bottle was bought and the next one was asked for;
       it does not say the file is still in the field, and on two ordinary paths it
       is not. A reload cannot restore a file input at all — the name comes back
       from storage, the photograph cannot — and `D-50`'s package morph re-renders
       this column from server HTML, which arrives with an empty field. Either way
       the step was claiming "Still Firulais." over an empty drop target and a
       placeholder label on the bottles, which is the awkward two-readings screen
       `D-143` set out to remove, pointed the other way.

       Read off the file input rather than off a flag of our own: it is the thing
       the sentence is about, and it cannot drift from what the visitor can see. A
       returning visitor now meets the ordinary empty step — the default hint, an
       empty drop target — which is the truth, and a shorter read than a claim plus
       the link that undoes it. */
    if (!input?.files?.length) { clearSameDogState(); return; }

    const text = sameDogText(); const hint = defaultHint();
    if (text) text.textContent = SAME_DOG_HINT.replace('{name}', petName);
    line.hidden = false;
    if (hint) hint.hidden = true;
  };

  /* "Use a different dog" — the one control that clears what the press keeps. It
     does exactly what the photo's own Remove does, plus the name, plus the line
     about them, so a buyer starting on a second dog meets the same empty step a
     first-time visitor does. */
  /* **Two controls clear the dog, so the clearing is one function.** *Use a
     different dog* has always been one of them (`D-143`); *Try again* on the
     results screen is the other, and it is the same act — put the step back to
     the empty state a first-time visitor meets. A second copy of these nine lines
     would be two answers to "what does clearing mean" waiting to disagree. */
  const clearDogInputs = ({ focusPhoto = true, message = 'Photo and name cleared.' } = {}) => {
    sameDogActive = false;
    if (input) input.value = '';
    if (nameInput) nameInput.value = '';
    try { setUIFromFile(); } catch (_) {}
    showFieldError('photo', '');
    showFieldError('name', '');
    clearSameDogState();
    try { stage()?.setName(''); } catch (_) {}
    try { nameInput?.dispatchEvent(new Event('input', { bubbles: true })); } catch (_) {}
    saveStateToLS();
    /* **Quiet, always.** `D-216` (2026-09-18) — "Photo and name cleared." has no
       sighted reader who needs it: the fields going empty already shows the
       clearing, and Try again's version of this call fires before `revealFields`
       even opens them, so the sentence had nothing to sit under and was left on
       screen — under "Your labels are ready!" once a visitor backed out again,
       since nothing else ever re-hid `.ai-status`. A screen reader still needs
       the words; `quiet` is exactly the geometry P7-01 built for saying something
       without printing it. */
    if (message) announce(message, { quiet: true });
    announceGenState();
    if (focusPhoto) { try { input?.focus(); } catch (_) {} }
  };

  on(section, 'click', (e) => {
    const target = e.target?.closest?.('[data-ai-different-dog]');
    if (!target) return;
    e.preventDefault();
    clearDogInputs();
  });

  /* ------------------------------------------------------------------ */
  /* D-115 — once labels exist, the fields close and the choice takes the step */
  /* ------------------------------------------------------------------ */
  /*
    **The question changes the moment three labels land.** Up to then the step is
    asking for a photograph and a name; after them it is asking which one, and the
    two fields that produced the answer are a form sitting above the answer.
    Client-directed: hide them, put the choice at the top of the step, and leave
    one quiet control at the foot of it that opens them again.

    **It is a disclosure, not a toggle, and only one of the two controls is ever
    on screen.** The control at the foot of the results and the button inside the
    fields carry the same setting — they are the same intention — so showing both
    at once would be the same words twice. Opening the fields takes the disclosure
    away; the button inside it is then the action.

    **A new round closes it again**, because the visitor has just used the fields
    and what they are waiting for is the three labels. A miss card's retry does
    not, because it never opened them.

    `hidden` rather than a class, so the fields leave the tab order and the
    accessibility tree with the eye rather than only leaving the eye.
  */
  let fieldsExpanded = false;

  /* The hint moved inside `.sdw-step__head` (a grid now, not a `<p>`) so it can
     take `grid-column: 2` and align under the title exactly the way the variant
     picker's own answer does — see `snippets/ai-generator.liquid`. One level
     deeper than it used to be, hence `.sdw-step__head >` rather than
     `.ai-label-step >`. */
  const stepHint = section.querySelector('.ai-label-step > .sdw-step__head > .sdw-step__hint');

  /* **The step's own title swaps in for the results heading, once there is one.**
     "Your label" (`block.settings.generate_title`) describes the work ahead of the
     fields above; once labels exist it is describing a step that already happened,
     next to three finished cards. `#ai-choose-label` already carries the sentence
     that belongs here instead — `results_title`, split into the same mobile/desktop
     pair `generator.css` reads off `.ai-choose-label::before` — so this reads its
     `data-text-*` rather than holding a third copy of the merchant's string.
     `stepTitleDefault` is captured once, from the server-rendered text, so the
     un-swap (fields reopened, or `Try again` clears the dog) has the original word
     to put back rather than a hardcoded guess of it. */
  const stepTitle = section.querySelector('.ai-label-step > .sdw-step__head > .sdw-step__title');
  const stepTitleDefault = stepTitle?.textContent || '';
  /* **A third title, for the one state neither of the other two describes.**
     "Your label" is the fields waiting for a photo; the results heading is the
     three cards already drawn. Reopening the fields over a finished set is
     neither — the photo and the name are not blank, and there is nothing on
     screen to choose between while they are hidden behind the fields — so it
     gets `retry_title`, read off the head's own attribute the way the results
     heading reads `data-text-*` a few lines below. `D-203`. */
  const stepHead = section.querySelector('.ai-label-step > .sdw-step__head');
  const retryTitleDefault = stepHead?.dataset.aiRetryTitle || 'Designing three more';

  /* **Inside the stepped flow the fields are a screen, not a disclosure, and that
     is the whole of this branch.** Above the breakpoint the rail is one column, so
     three finished labels arriving under the two fields that made them is a long
     scroll and the fields close to prevent it — `D-115`'s argument, unchanged.

     On a phone the fields already have a screen to themselves and the labels have
     another, so closing them would hide a screen inside a screen: a visitor stepping
     back to *your dog* would land on an empty step with a disclosure on it. The flow
     is doing the hiding, and one mechanism hiding a thing is enough.

     The disclosure stays, and it changes job rather than going away. It is still
     the only way back to the photograph once labels exist — but it navigates now
     instead of expanding, so `aria-expanded` comes off: a button reporting a
     collapsed state it does not have would be telling a screen reader something
     untrue, and the honest description of this control is a plain button that takes
     you somewhere. `aria-controls` stays, because the thing it points at is still
     the thing the press leads to. */
  const flowOn = () => document.documentElement.hasAttribute('data-sdw-flow');

  /* **Shared with `renderGridIfNeeded`, and that sharing is the fix `D-203` records.**
     A resync — `variant:update`, the carousel's own re-sync, a restore from
     storage — used to write `results.hidden = false` unconditionally the moment
     it found cards to show, which fought this function's `retrying` branch: the
     fields reopened over three finished labels stayed open, but the labels crept
     back onto screen underneath them on the very next resync. One predicate, read
     by both writers, is what keeps a visitor's "try again" hidden until they
     actually leave that state. */
  const isRetrying = () => !flowOn() && generatedImages.length > 0 && fieldsExpanded;

  const syncFieldsCollapse = () => {
    const hasLabels = generatedImages.length > 0;
    const stepped = flowOn();
    /* `D-220` carried a `!HANDOFF_URL` here, exempting the home page's capture-only
       instance from `D-115`'s collapse. There is no second instance to exempt
       (`D-221`), so the expression is `D-115`'s own again — every section this runs in
       is the configurator, which is the page that rule was written for. */
    const collapsed = !stepped && hasLabels && !fieldsExpanded;

    if (fieldsBox) fieldsBox.hidden = collapsed;
    /* The hint describes the work the fields ask for — "Upload photo and name,
       get 3 styles..." — so it goes with them. Left up, it sits over three
       finished labels promising seconds that have already passed. Deliberately
       *not* `collapsed`: `collapsed` is gated `!stepped` because the fields box
       and the step title only need collapsing on the desktop rail, where the
       fields and the results share a column — but the hint describes the fields
       themselves, and "there are labels the visitor has not reopened the fields
       for" is true in the stepped flow exactly as it is on desktop. Reusing
       `collapsed` left this hint permanently visible on a phone once labels
       existed, sitting under "Your labels are ready!" and promising a 20-second
       job already finished. */
    if (stepHint) stepHint.hidden = hasLabels && !fieldsExpanded;

    if (stepTitle) {
      if (collapsed) {
        stepTitle.textContent = chooseLabel?.dataset[chooseLabelKey()] || stepTitleDefault;
      } else if (isRetrying()) {
        stepTitle.textContent = retryTitleDefault;
      } else {
        stepTitle.textContent = stepTitleDefault;
      }
    }

    for (const b of expandBtns) {
      const offered = stepped ? hasLabels : collapsed;
      b.hidden = !offered;
      if (stepped) b.removeAttribute('aria-expanded');
      else b.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    /* **"Trying again" hides the labels it would otherwise be drawing over.**
       Scoped to `!stepped`: in the mobile flow the fields and the results are
       already two different screens, one on screen at a time through the step
       rail, so nothing here needs to hide either — forcing `.ai-results[hidden]`
       there would also drop it from `liveSteps()`'s count while a visitor is
       simply looking at a different step. Above the breakpoint they share one
       column, and a photo or a name changed while three labels of the *old* one
       sit visible below is the thing this prevents: `retrying` is that state,
       and it is `collapsed`'s inverse over the same `hasLabels` and `stepped`. */
    const retrying = isRetrying();

    if (results) {
      if (retrying) results.hidden = true;
      else if (hasLabels) results.hidden = false;
    }

    if (cancelBtn) cancelBtn.hidden = !retrying;
  };

  /* `assets/buy-flow-steps.js` raises this when it turns the flow on or off — at
     first paint, and again at every crossing of the breakpoint. Re-deciding the
     collapse is the whole reason it exists: a visitor who rotates a phone into
     landscape crosses 749px, and the fields have to close behind the disclosure on
     the way out of the flow and open again on the way back in. */
  document.addEventListener('sdw:flow', () => {
    try { syncFieldsCollapse(); } catch (_) {}
  });

  /* Anything that has to name a control inside the fields opens them first —
     otherwise the add-to-cart guard scrolls to and focuses an element that is not
     in the document's flow, which is the same defect `P5-04` fixed for the mobile
     stepped flow and for the same reason. */
  const revealFields = ({ focus = false, scroll = true } = {}) => {
    const wasCollapsed = !!fieldsBox?.hidden;
    fieldsExpanded = true;
    syncFieldsCollapse();
    /* **The focus move is not conditional on the fields having been closed inside
       the flow**, where they never are. The press changed which screen is on the
       page, so the caret has to follow it or the next Tab starts from a control the
       visitor can no longer see. Above the breakpoint the old test still holds:
       nothing moved, so nothing needs to. */
    if (!focus || (!wasCollapsed && !flowOn())) return;
    /* Focus goes to the first field rather than to the wrapper: the disclosure
       that was carrying focus has just been removed from the document, so leaving
       it where it was would drop the caret to the top of the page. */
    const first = section.querySelector('#ai-photo') || fieldsBox;
    try { first?.focus?.({ preventScroll: true }); } catch (_) { try { first?.focus?.(); } catch (_) {} }
    if (!scroll) return;
    try {
      fieldsBox?.scrollIntoView?.({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } catch (_) {}
  };

  /* **"Try again" is a way back to a screen now, not a disclosure.**

     Above the breakpoint it does what it always did: opens the fields in place and
     puts the caret in the photo control.

     Inside the flow it sends the visitor to screen 1, because that is where the
     fields are. `sdw:reveal` is the hook that already exists for bringing a step
     forward and it is what the add-to-cart guard and the recap's goto links use, so
     this adds no second route. `scroll` is asked for: the destination is the whole
     screen, and `revealFields` is then told not to scroll as well — two scrolls on
     one press fight, and the step change is the one that knows where the screen
     starts.

     `revealFields` is still called on both paths. In the flow the fields are never
     collapsed, so it is close to a no-op — but it is what moves the focus, and a
     press that changes the screen under a keyboard visitor without moving their
     caret leaves them tabbing from wherever they were. */
  for (const pressed of expandBtns) {
    on(pressed, 'click', () => {
      let stepped = false;
      if (flowOn()) {
        try {
          pressed
            .closest('[data-sdw-step]')
            ?.dispatchEvent(new CustomEvent('sdw:reveal', { bubbles: true, detail: { scroll: true } }));
          stepped = true;
        } catch (_) {}
      }
      /* **Try again reopens to an empty step, not the last one** (client
         request, 2026-09-18, `D-215`). `clearDogInputs` was already written for
         exactly this — the comment above it (`D-143`) names Try again as its
         second, never-wired caller — so this is the wiring, not a new function.
         `focusPhoto: false` because `revealFields` right below does its own
         focus-and-scroll once the fields are actually on screen; focusing
         `#ai-photo` here, while `fieldsBox` may still be `hidden`, would move
         focus nowhere a sighted visitor or a screen reader could follow. */
      clearDogInputs({ focusPhoto: false });
      revealFields({ focus: true, scroll: !stepped });
    });
  }

  /* **The way back out, without drawing.** `expandBtn` above is the only thing
     that sets `fieldsExpanded = true`, and this is its undo: the fields close,
     `syncFieldsCollapse` re-hides them and un-hides the three labels this button
     was standing in front of. Focus goes to `expandBtn` itself — it is the
     control that opened this state and the one now back in the step's head row,
     so a keyboard visitor's next Tab continues from where the redraw disclosure
     sits rather than from wherever the fields happened to leave the caret. */
  if (cancelBtn) {
    on(cancelBtn, 'click', () => {
      fieldsExpanded = false;
      syncFieldsCollapse();
      const back = shownExpandBtn();
      try { back?.focus?.({ preventScroll: true }); } catch (_) { try { back?.focus?.(); } catch (_) {} }
    });
  }

  const updateRedrawState = () => {
    /* Every caller of this function is a moment the label count may have changed,
       which is exactly when the collapse has to be re-decided — so it is settled
       here rather than left for eight call sites to remember. */
    syncFieldsCollapse();

    /* **This button stays the page's primary action even once a label exists**
       (`D-203`, superseding the step-down this comment used to argue for). The
       step-down read right in the results screen this button no longer appears
       on — full-width, alone, the one thing to press — but the one place it
       actually renders once a label exists is beside `Back to my labels` in the
       reopened-fields row, and there the visitor has just pressed *Try again* to
       get here: the press they are about to make is this one. Downgrading the
       control a visitor is mid-gesture toward is the wrong moment for the
       system's second style, whatever the merits elsewhere. */
    if (!btn) return;

    /* **And it says what a second press does, because that changed.** A press
       returns three labels, so "Make my label" on a button that appends a trio is
       a promise the control breaks. Both strings are theme settings — the resting
       one is rendered by Liquid and read off the button once, the redraw one comes
       down as an attribute — so neither is a sentence written in this file. */
    /* `D-219` carried a `!HANDOFF_URL` here, because a capture-only instance was
       never the button that appends and so could not honestly wear the word for it.
       There is no such instance (`D-221`): every press this button makes is a press
       that draws, so the count is the whole test again. */
    const redraw = generatedImages.length > 0;
    const next = redraw ? REDRAW_LABEL : DRAW_LABEL;
    if (next && btnText && btnText.textContent.trim() !== next) btnText.textContent = next;
  };

  /* ------------------------------------------------------------------ */
  /* field errors                                                         */
  /* ------------------------------------------------------------------ */
  const ensureFieldErrorEl = (field) => {
    let el = section.querySelector(`[data-ai-error="${field}"]`);
    if (el) return el;
    el = document.createElement('small');
    el.dataset.aiError = field; el.className = 'ai-field-error'; el.hidden = true;
    if (field === 'photo') (uploadBox || photoInput?.parentElement || section).appendChild(el);
    else if (field === 'name') (nameInput?.parentElement || section).appendChild(el);
    return el;
  };

  const showFieldError = (field, message) => {
    const hasMsg = Boolean(message);
    if (field === 'photo') {
      const err = ensureFieldErrorEl('photo');
      err.textContent = message || 'This field is required'; err.hidden = !hasMsg;
      uploadBox?.classList?.toggle('ai-invalid', hasMsg);
      photoInput?.setAttribute?.('aria-invalid', hasMsg ? 'true' : 'false');
    }
    if (field === 'name') {
      const err = ensureFieldErrorEl('name');
      err.textContent = message || 'This field is required'; err.hidden = !hasMsg;
      nameInput?.classList?.toggle('ai-invalid', hasMsg);
      nameInput?.setAttribute?.('aria-invalid', hasMsg ? 'true' : 'false');
    }
  };

  const validateBeforeGenerate = () => {
    let ok = true;
    if (!photoInput?.files?.[0]) { showFieldError('photo', 'This field is required'); ok = false; } else showFieldError('photo', '');
    if (!nameInput?.value?.trim()) { showFieldError('name', 'This field is required'); ok = false; } else showFieldError('name', '');
    return ok;
  };

  /* A capture-only press stood here: it validated the same two fields a draw does,
     wrote the photograph to IndexedDB, set a spinner and a busy string on the button
     and assigned `location.href`. All of it is gone with the hand-off (`D-221`), and
     with it `HANDOFF_STUCK_MS` — the ten-second backstop that took the busy state off
     a navigation that never happened — and `setHandoffBusy`, which owned the four
     things that had to come off together. This button has one job again: it draws. */

  /* P5-04: `refreshAtcRequirementMessage` retires the buy button's message as
     soon as the step it named is done — typing a name mutates no node, so the
     scope observer that drives `scheduleRefresh` would never see it. */
  /* **Only clear the photo's error when there is a photo.** Two listeners answer
     this input's `change`: the first validates the file and, on a rejection, writes
     the reason into the field's own error and empties the input; this one then ran
     unconditionally and wiped that reason a microtask later. Measured with a 9 MB
     file: the field error rendered "This field is required" and was `hidden`, and
     the only surviving message was the live region's. The visitor was told, but not
     where they were looking, and the drop target never turned red.

     A rejection leaves `files` empty — `rejectFile` clears the input — so testing
     for a file is exactly the test for "this change was a success".

     **The live region gets the same test, for the same reason.** `[data-ai-photo-
     remove]`'s own handler (above) calls `announce('Photo removed.')` and leaves it
     there — there is no next state for that action to land on, the visitor is
     looking at an empty dropzone. But `.ai-status` is one region shared by every
     message this control writes, and a visitor who removes a photo and then
     chooses another was left reading "Photo removed." under a filled-in dropzone,
     the thumbnail and filename both current and correct, because nothing had told
     the region a photo existed again. `clearStatus()` on a successful change is
     that telling: not a new announcement, since the dropzone itself already shows
     what changed, just the old one taken down. */
  if (photoInput) {
    on(photoInput, 'change', () => {
      if (photoInput.files?.[0]) { showFieldError('photo', ''); clearStatus(); }
      refreshAtcRequirementMessage();
      announceGenState();
    });
  }
  if (nameInput)  on(nameInput,  'input',  () => { showFieldError('name', '');  refreshAtcRequirementMessage(); try { applySameDogState(); } catch (_) {} announceGenState(); });

  /* P7-01 — the character budget, counted down rather than asserted.

     The field's `maxlength` is 10 and the hint said 12, and neither said anything
     once a visitor had typed nine. A `maxlength` that silently stops accepting
     keystrokes on the one field whose value gets printed on a bottle is the kind
     of thing a visitor reads as a broken keyboard.

     **It counts from the first character now, and it sits in the corner of the
     field** (client request, 2026-08-25). It used to appear at seven and read as a
     sentence under the control; in the corner it is two words at caption size, out
     of the reading line entirely, so showing it earlier costs nothing and a visitor
     never meets the cap without having watched it approach.

     **Two words, and the guidance is spoken rather than printed.** "That is all 10.
     A short version of their name fits the label best." is the right thing to say
     and the wrong thing to put in a corner — so the corner carries the number and
     the sentence goes to the live region once, on the keystroke that fills the
     field. Once, not on every key: `announcedFull` is what keeps a visitor holding
     a key down from hearing it ten times.

     The element is no longer `aria-live`. It is the field's `aria-describedby`
     instead (`snippets/ai-generator.liquid`), because a live region that now
     updates on every keystroke would announce every keystroke. */
  const nameCountEl = section.querySelector('[data-ai-name-count]');
  if (nameInput && nameCountEl) {
    const limit = Number.parseInt(nameInput.getAttribute('maxlength') || '10', 10) || 10;
    let announcedFull = false;
    const updateNameCount = () => {
      const used = (nameInput.value || '').length;
      const left = limit - used;
      /* A resting field carries no mark. The limit is not news before the first
         character, and an empty field with "10 left" in the corner is a constraint
         announcing itself before anyone has asked anything of it. */
      if (!used) { nameCountEl.textContent = ''; announcedFull = false; return; }
      nameCountEl.textContent = `${left} left`;
      nameCountEl.classList.toggle('is-full', left <= 0);
      if (left <= 0 && !announcedFull) {
        announcedFull = true;
        announce(`That is all ${limit}. A short version of their name fits the label best.`);
      }
      if (left > 0) announcedFull = false;
    };
    on(nameInput, 'input', updateNameCount);
    updateNameCount();
  }

  /* ------------------------------------------------------------------ */
  /* dog-only popup                                                       */
  /* ------------------------------------------------------------------ */
  /* **It stays until it is dismissed. `D-260`.** It closed itself after a few
     seconds and had nothing to press; the client asked for a button. A dialog
     that has one and also vanishes on a timer is two answers to "how does this
     close", so the timer went — `ms` is still accepted and ignored, so the one
     caller reads as it did. `dogPopupTimer` is cleared for a popup opened by a
     build that still had it. */
  const closeDogOnlyPopup = () => {
    const el = document.getElementById('dog-only-popup');
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sdw-dog-popup-open');
    clearStatus();
    /* Back to the photograph, which the no-dog path has just emptied: the next
       act is choosing another one. */
    try { (photoInput || input)?.focus?.({ preventScroll: true }); } catch (_) {}
  };

  const showDogOnlyPopup = (ms = 2000) => {
    const el = document.getElementById('dog-only-popup');
    if (!el) return;
    if (dogPopupTimer) { clearTimeout(dogPopupTimer); dogPopupTimer = null; }
    el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sdw-dog-popup-open');
    try { el.querySelector('[data-dog-only-dismiss]')?.focus?.({ preventScroll: true }); } catch (_) {}
  };

  /* Bound on `document`, because the popup lives in `layout/theme.liquid` rather
     than in this section, and through `on` so a re-initialised section does not
     stack a second set. The backdrop closes it only when the press lands on the
     backdrop itself, not on the card inside it. */
  on(document, 'click', (e) => {
    if (e.target?.closest?.('[data-dog-only-dismiss]')) { e.preventDefault(); closeDogOnlyPopup(); return; }
    if (e.target?.matches?.('[data-dog-only-backdrop]')) closeDogOnlyPopup();
  });
  on(document, 'keydown', (e) => { if (e.key === 'Escape') closeDogOnlyPopup(); });

  /* ------------------------------------------------------------------ */
  /* Toast post-generation                                                */
  /* ------------------------------------------------------------------ */
  const ensureToastEl = () => {
    let el = document.getElementById('ai-gen-toast');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'ai-gen-toast'; el.className = 'ai-toast';
    /* P7-02: no `role="status"` and no `aria-live`. `.ai-status` in the buy rail
       already announces "Your label is ready" and focus has already moved to the
       first result, so a live region here announced the same event a third time.
       The toast is a visual confirmation of something assistive technology has
       already been told. */
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
    return el;
  };

  let _toastTimer = null;

  /* P7-02 — the toast, styled and reworded.

     Nothing in the theme had ever styled `.ai-toast`, so this rendered as
     unstyled static content below the footer: a paw emoji, a run-on sentence and
     a bare `×`, arriving after the one moment `PRODUCT.md` calls the emotional
     peak of the product. `assets/generator.css` gives it a surface; this gives it
     words.

     "Image generated! How does it look? If you're not happy with it, contact us."
     opened by congratulating itself, then asked a question it could not receive
     an answer to, then raised the possibility of disappointment — three moves, in
     the wrong order, at the moment a visitor is looking at their own dog. It
     names what happened and leaves the escape hatch as a quiet second line.

     `aria-live` is deliberately absent: `.ai-status` already announces "Your
     label is ready" and focus has already moved to the first result, so a live
     region here would say the same thing twice. */
  /* **Not inside the stepped flow, where the same news has already been delivered
     twice.** On a phone the round ends by taking the full-viewport overlay down and
     moving the visitor to the results screen; a toast sliding in over the labels to
     announce that the labels are ready is a third telling of a thing the screen is
     already showing — and it lands on top of the row the visitor is now choosing
     from. Client direction from the flow.

     The toast stays above the breakpoint, where it is the only announcement there
     is: the desktop rail has no overlay to close and no screen to change, so the
     grid simply fills in further down a long column and the toast is what says so.

     Guarded here rather than at the call site so a later caller inherits it. */
  const showGenerationToast = ({ contactUrl = '/pages/contact' } = {}) => {
    if (flowOn()) return;
    const el = ensureToastEl();
    const petName = (nameInput?.value || '').trim();
    el.innerHTML = `
      <span class="ai-toast__icon" aria-hidden="true">🐾</span>
      <div class="ai-toast__body">
        <span class="ai-toast__title">${petName ? `${petName}’s label is ready.` : 'Your label is ready.'}</span>
        <span class="ai-toast__sub">
          It is at the top of the gallery. Not quite them?
          <a class="ai-toast__link" href="${contactUrl}" target="_blank" rel="noopener">Tell us</a>.
        </span>
      </div>
      <button type="button" class="ai-toast__close" aria-label="Dismiss">&times;</button>
    `;
    const hide = () => { el.classList.remove('is-visible'); el.setAttribute('aria-hidden', 'true'); if (_toastTimer) { clearTimeout(_toastTimer); _toastTimer = null; } };
    el.querySelector('.ai-toast__close')?.addEventListener('click', hide, { once: true });
    el.getBoundingClientRect();
    el.classList.add('is-visible'); el.setAttribute('aria-hidden', 'false');
    if (_toastTimer) clearTimeout(_toastTimer);
    _toastTimer = setTimeout(hide, 7000);
  };

  /* ------------------------------------------------------------------ */
  /* add-to-cart reset                                                    */
  /* ------------------------------------------------------------------ */
  const clearLSState = () => { try { localStorage.removeItem(LS_KEY); } catch {} };

  let syncPropertiesToAllForms     = () => {};
  let renderGridIfNeeded           = () => {};
  let syncAiImagesToHorizonCarousel = () => {};
  /* Declared here for the same reason as the three above: the grid's miss cards
     bind it as a click handler and are built well before the round machinery that
     defines it. */
  let redrawStyle                  = async () => {};

  /* **The add stopped resetting anything. `D-143`.**

     What this function did on every successful `/cart/add` now happens on one press
     of one button, and what the add does instead is `markLandedAfterAddToCart`
     below: it says the bottle is bought and offers the way to the next one. The
     buyer's own report of the old behaviour was that adding to the cart looked like
     losing the work — the stage went blank while the photograph and the name stayed
     filled, so the form claimed a dog was on file beside a bottle wearing nothing.

     Deferring it also deletes a race rather than repairing one; the reasoning is in
     `assets/bottle-stage.js` where the drawer-deferral code used to be. */
  const startNextBottle = () => {
    clearLSState();
    /* The sentence goes with the composition it was about. Leaving it set would
       re-land the stage on the next reload, from the state written at the foot of
       this function. */
    landedText = '';
    generatedImages.length = 0;
    misses.length = 0;
    /* The next bottle's labels are new labels and should arrive as such. */
    enteredUrls.clear();
    nextRound = 0;
    selectedImage = null; generationCount = 0;
    _selectedImageFile = null; _selectedImageFileUrl = null; _selectedImageFilePromise = null;
    _selectedPdfFile   = null; _selectedPdfFileUrl   = null;
    clearStatus(); if (status) status.hidden = true;
    try { renderGridIfNeeded(); } catch (_) {}
    try { syncAiImagesToHorizonCarousel([], null); } catch (_) {}
    if (btn) btn.disabled = false;
    try { syncPropertiesToAllForms(); } catch (_) {}
    /* With the grid empty this puts the draw button back to its resting label and
       re-opens the collapsed photo-and-name fields, both of which the visitor is
       about to need. The resting label is the right one of the two the button has:
       "Make three more" means *append to the trio on screen*, and there is no trio
       on screen any more. That is why the next bottle needs no third string. */
    try { updateRedrawState(); } catch (_) {}

    /* **The photograph and the typed name go too. `D-259` (2026-10-08) reverses
       this half of `D-143`.** They used to survive, on the reasoning that most
       second bottles are for the same dog and re-uploading the photograph is a
       toll on the path the business wants taken; the step then said "Still
       Firulais." with *Use a different dog* beside it. The client watched that
       from the buyer's side on an iPhone and asked for the opposite: "Create
       another bottle" should be the beginning of the process, not the previous
       label. So the press now does what *Use a different dog* does, through the
       same function, and the buyer meets the empty step a first-time visitor
       does.

       `focusPhoto: false` because the click handler below owns focus and scroll
       for this press, and a focus here, without `preventScroll`, would jump the
       page before the smooth scroll starts. `message: ''` because "Photo and name
       cleared." would be a second announcement on top of "Ready for the next
       bottle.", about a clearing the buyer asked for by name.

       The same-dog machinery (`applySameDogState`, the hint and its settings) is
       left in place and simply never switched on: nothing sets `sameDogActive`
       true any more except a restore of state saved before this row, and
       `applySameDogState` already falls back to the default hint when the file
       input is empty, which after a reload it always is. Reinstating `D-143` is
       putting the two removed lines back. */
    try { clearDogInputs({ focusPhoto: false, message: '' }); } catch (_) {}

    /* **Add to cart goes back to saying Add to cart.**

       `assets/product-form.js` puts `data-added` on the button for the checkmark
       burst and takes it off again 800ms later — but that timer is chained behind
       `onAnimationEnd(addToCartButton)`, and on this button the animation runs on
       the two spans inside it rather than on the button itself, so on the paths
       where nothing animates the element it waits for a transitionend that never
       comes and the checkmark stays. A buyer starting their second bottle then
       meets a black button with a tick on it and no word on it, which reads as
       *already added* on a stage that has just been emptied.

       Clearing it here rather than repairing the theme's timer: this press is the
       moment the previous add stops being the current state, and it is the one
       moment the configurator knows that. Every add-to-cart button on the page,
       because `D-50`'s package morph and the sticky buy bar can each hold one. */
    try {
      document.querySelectorAll('[data-added="true"]').forEach((el) => el.removeAttribute('data-added'));
    } catch (_) {}

    /* The stage clears last, and un-lands on its way through. */
    try { stage()?.reset(); } catch (_) {}
    saveStateToLS();

    /* **And the phone goes back to screen 1. `D-259`, reported from the flow.**
       Clearing the dog emptied screen 1 but nothing told `assets/buy-flow-steps.js`,
       so the step bar stayed on the last screen — the review — and the visitor had
       to walk Back through screens that no longer had anything on them. `sdw:reveal`
       on the photo-and-name step is the existing way to bring a step up (the
       add-to-cart guard and the recap's links use it); above the breakpoint, or with
       the flow off, nothing listens. No `scroll`: the again-click handler scrolls to
       the anchor itself, and the cart link's `#product-AI-agent` has already been
       scrolled to by the browser. */
    try {
      section.querySelector('.ai-label-step[data-sdw-step]')
        ?.dispatchEvent(new CustomEvent('sdw:reveal', { bubbles: true, detail: { scroll: false } }));
    } catch (_) {}
  };

  /* What actually fires on a successful add. It writes no cart state and takes
     nothing off the screen: one attribute on the stage, one sentence, one button,
     and a flag in storage so a reload tells the same story. */
  const markLandedAfterAddToCart = () => {
    const petName = (nameInput?.value || '').trim();
    landedText = petName ? LANDED_NAMED.replace('{name}', petName) : LANDED_ANON;
    try { stage()?.markLanded({ text: landedText, againLabel: AGAIN_LABEL }); } catch (_) {}
    saveStateToLS();
  };
  /* **Only on a successful add, and only from the response.** The one caller is
     `hookResetAfterAjaxAddToCartOnce` below, which runs this when a `/cart/add`
     carrying this configurator's properties comes back `ok`.

     There used to be a second, earlier caller: `scheduleResetAfterSubmit`, a 50ms
     timer started by the submit guard, before any answer had arrived. It came with
     the production theme (`P0-03`) and it landed the stage on *every* press. On
     2026-09-28 the store had no shipping rate, so Shopify refused every add as
     "already sold out", and the page said both "Oscar is in your cart." and that
     refusal, one above the other. Shopify answers a refused add with a 422
     (measured against the store the same day), so `res.ok` is the whole test. */
  window.__AI_ACTIVE_RESET_AFTER_ATC = markLandedAfterAddToCart;

  /* **The press that clears the stage, and the only one.** Delegated on `document`
     rather than bound to the button, because the button is inside the element
     `assets/variant-picker.js` morphs on every package change (`D-50`) — a bound
     listener would be thrown away with the first re-render and the control would
     go dead exactly when a buyer changed their mind about the package.

     It scrolls the rail's label step into view afterwards and puts focus on the
     draw button, because the stage and the step are in two columns on desktop and
     the thing to do next is in the other one. `D-39`'s offset is on the anchor
     already, so this reuses it rather than measuring the sticky header again. */
  const beginNextBottleHere = () => {
    startNextBottle();
    try {
      const anchor = document.getElementById('product-AI-agent') || section;
      anchor?.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } catch (_) {}
    /* The photograph, not the draw button, since `D-259`: with the fields empty
       the draw button can only answer with "add a photo", so focus goes where the
       next act is — the same place *Use a different dog* has always put it. */
    try { input?.focus?.({ preventScroll: true }); } catch (_) {}
    announce('Ready for the next bottle.');
  };

  document.addEventListener('click', (e) => {
    const again = e.target?.closest?.('[data-sdw-stage-again]');
    if (!again) return;
    e.preventDefault();
    beginNextBottleHere();
  });

  /* **The cart drawer's "Create a second bottle", taken on this page. `D-272`.**
     Reported by the operator: the press did nothing. Measured on staging: the
     theme handled the link as a same-page jump — the address became
     `?country=US#product-AI-agent`, the drawer stayed open, the name, the labels
     and "Pepe is in your cart." all stayed — so `sdw_next` never reached a page
     load to act on. Here, on the configurator's own product page, the press is
     answered before anything else sees it (`window`, capture): the drawer closes,
     the next bottle starts, and the page scrolls to the first step once the drawer
     has let go of the scroll. On any other page the link is left alone and is the
     route here. */
  window.addEventListener('click', (e) => {
    const link = e.target?.closest?.('a[data-sdw-next-bottle]');
    if (!link || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    let target = null;
    try { target = new URL(link.href, location.href); } catch (_) { return; }
    if (target.pathname !== location.pathname) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    try { (link.closest('cart-drawer-component') || document.querySelector('cart-drawer-component'))?.close?.(); } catch (_) {}
    setTimeout(() => { try { beginNextBottleHere(); } catch (_) {} }, 200);
  }, true);


  (function hookResetAfterAjaxAddToCartOnce() {
    if (window.__AI_RESET_AFTER_ATC_HOOKED) return;
    window.__AI_RESET_AFTER_ATC_HOOKED = true;
    let lastResetAt = 0;
    const debounceReset = () => {
      const now = Date.now();
      if (now - lastResetAt < 1200) return;
      lastResetAt = now;
      setTimeout(() => { try { const r = window.__AI_ACTIVE_RESET_AFTER_ATC; if (typeof r === 'function') r(); } catch (e) { console.warn('[AI] reset failed:', e); } }, 0);
    };
    const isCartAddUrl   = (url)  => !!url && (url.includes('/cart/add') || url.includes('/cart/add.js'));
    const bodyHasAiProps = (body) => {
      try {
        const keyFile = `properties[${FILE_PROPERTY_KEY}]`; const keyPet = 'properties[Pet name]'; const keyStyle = 'properties[Style]';
        if (!body) return false;
        if (body instanceof FormData)        return body.has(keyFile) || body.has(keyPet) || body.has(keyStyle);
        if (body instanceof URLSearchParams) return body.has(keyFile) || body.has(keyPet) || body.has(keyStyle);
        if (typeof body === 'string') return body.includes(keyFile) || body.includes(keyPet) || body.includes(keyStyle) || body.includes(encodeURIComponent(keyFile)) || body.includes(encodeURIComponent(keyPet)) || body.includes(encodeURIComponent(keyStyle)) || body.includes('properties%5BAI%20Image%5D') || body.includes('properties%5BPet%20name%5D') || body.includes('properties%5BStyle%5D');
      } catch (_) {}
      return false;
    };
    let lastAiAtcIntentAt = 0;
    document.addEventListener('click', (e) => {
      const btn = e.target?.closest?.('button[name="add"], button[type="submit"], input[name="add"], input[type="submit"]');
      if (!btn) return;
      const form = btn.closest('form');
      if (!form) return;
      if (form.querySelector(`input[name="properties[Pet name]"]`) || form.querySelector(`input[name="properties[Style]"]`) || form.querySelector(`input[name="properties[${FILE_PROPERTY_KEY}]"]`)) lastAiAtcIntentAt = Date.now();
    }, true);
    const shouldResetForRequest = (url, body) => { if (!isCartAddUrl(url)) return false; if (bodyHasAiProps(body)) return true; return Date.now() - lastAiAtcIntentAt < 5000; };
    if (typeof window.fetch === 'function' && !window.__AI_FETCH_PATCHED) {
      window.__AI_FETCH_PATCHED = true;
      const origFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const input = args[0]; const init = args[1] || {};
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const relevant = shouldResetForRequest(url, init?.body);
        const res = await origFetch(...args);
        if (relevant && res?.ok) debounceReset();
        return res;
      };
    }
    if (window.XMLHttpRequest && XMLHttpRequest.prototype && !window.__AI_XHR_PATCHED) {
      window.__AI_XHR_PATCHED = true;
      const XHROpen = XMLHttpRequest.prototype.open;
      const XHRSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) { this.__ai_url = url; return XHROpen.call(this, method, url, ...rest); };
      XMLHttpRequest.prototype.send = function (body) {
        try { if (shouldResetForRequest(this.__ai_url || '', body)) this.addEventListener('loadend', () => { if (this.status >= 200 && this.status < 300) debounceReset(); }, { once: true }); } catch (_) {}
        return XHRSend.call(this, body);
      };
    }
  })();

  /* ------------------------------------------------------------------ */
  /* PDF helpers                                                          */
  /* ------------------------------------------------------------------ */
  const loadPdfLib = () => {
    if (window.PDFLib) return Promise.resolve(window.PDFLib);
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js';
      script.onload = () => resolve(window.PDFLib);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const normalizeImageBlobForPdf = (blob) =>
    new Promise((resolve) => {
      const mime = blob.type || '';
      if (mime.includes('png')) return resolve({ blob, format: 'png' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((jpegBlob) => resolve({ blob: jpegBlob, format: 'jpg' }), 'image/jpeg', 0.95);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve({ blob, format: 'jpg' }); };
      img.src = url;
    });

  const imageBlobToPdfBlob = async (imageBlob) => {
    const PDFLib = await loadPdfLib();
    const { PDFDocument } = PDFLib;
    const { blob: nb, format } = await normalizeImageBlobForPdf(imageBlob);
    const uint8Array = new Uint8Array(await nb.arrayBuffer());
    const pdfDoc = await PDFDocument.create();
    const embeddedImage = format === 'png' ? await pdfDoc.embedPng(uint8Array) : await pdfDoc.embedJpg(uint8Array);
    const { width, height } = embeddedImage;
    const page = pdfDoc.addPage([width, height]);
    page.drawImage(embeddedImage, { x: 0, y: 0, width, height });
    return new Blob([await pdfDoc.save()], { type: 'application/pdf' });
  };

  /* ------------------------------------------------------------------ */
  /* form sync helpers                                                    */
  /* ------------------------------------------------------------------ */
  const getAllProductForms = () => Array.from(document.querySelectorAll('form[action^="/cart/add"]'));

  /*
    The one place the three styles have display names. It writes the cart line's
    `Style`, captions every card in the grid, and names a style in a failure
    message.

    **The merchant's own strings win, and that is a correction rather than a
    convenience.** The names were hard-coded here as "Bold portrait" and "Colorful"
    while `snippets/ai-generator.liquid` rendered the theme settings `bold_text`
    and `sd_text` — "Bold Portrait" and "Soul Dog" — a few hundred pixels above.
    Nobody noticed while the settings named a *control* and this named a cart
    property. `D-113` puts a caption on every card, so the two now sit in the same
    step: measured on the rendered page, an example captioned "Bold Portrait" over
    a result captioned "Bold portrait", and a checkout line that would have read
    "Colorful" for a style the visitor only ever saw called "Soul Dog".

    So the map is read off the illustration's `data-ai-style-name` attributes, and
    the literals below are the fallback for when the merchant has set no example
    images and that block does not render. A style with neither shows its raw
    request value, which is the failure mode to look for.
  */
  const STYLE_NAME_FALLBACK = {
    bold_portrait: 'Bold portrait',
    cubist: 'Cubist',
    floral: 'Floral',
    colorfull: 'Soul Dog',
  };

  const styleNames = (() => {
    const map = { ...STYLE_NAME_FALLBACK };
    for (const el of section.querySelectorAll('[data-ai-style][data-ai-style-name]')) {
      const key = el.getAttribute('data-ai-style');
      const name = (el.getAttribute('data-ai-style-name') || '').trim();
      if (key && name) map[key] = name;
    }
    return map;
  })();

  const styleLabel = (value) => styleNames[value] || value;

  /* **`properties[Style]` comes from the chosen label now.** It used to be read off
     `styleSelect.value`, and there is no select: with no replacement every order
     would ship with an empty `Style`, which is one of the two line-item properties
     the business deliberately does not hide behind an underscore — the customer
     reads it at checkout.

     `find` on the url rather than an index, because the grid holds two trios after
     a redraw and the selected one can be any of the six. */
  const entryForUrl = (url) => (url ? generatedImages.find((item) => item?.url === url) || null : null);
  const styleOfSelected = () => entryForUrl(selectedImage)?.style || '';

  const setOrUpdateHiddenProperty = (form, key, value) => {
    if (!form) return;
    const name = `properties[${key}]`;
    let inp = form.querySelector(`input[name="${name}"]`);
    if (!inp) { inp = document.createElement('input'); inp.type = 'hidden'; inp.name = name; form.appendChild(inp); }
    inp.value = value || '';
  };

  const ensureFilePropertyInput = (form, key, accept = 'image/*,application/pdf') => {
    if (!form) return null;
    const name = `properties[${key}]`;
    let inp = form.querySelector(`input[name="${name}"]`);
    if (!inp || inp.type !== 'file') {
      if (inp && inp.type !== 'file') inp.remove();
      inp = document.createElement('input');
      inp.type = 'file'; inp.name = name; inp.accept = accept;
      inp.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;';
      inp.tabIndex = -1;
      form.appendChild(inp);
    }
    return inp;
  };

  const setFileOnInput = (fileInput, file) => {
    if (!fileInput || !file) return false;
    try { const dt = new DataTransfer(); dt.items.add(file); fileInput.files = dt.files; return true; } catch (_) { return false; }
  };

  const getExtFromMime = (mime) => {
    if (!mime) return 'png';
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    if (mime.includes('png'))  return 'png';
    if (mime.includes('webp')) return 'webp';
    return 'png';
  };

  const dataURLToBlob = (dataUrl) => {
    const [header, data] = dataUrl.split(',');
    const mime = (header.match(/data:(.*?);base64/) || [])[1] || 'image/png';
    const binary = atob(data); const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  const fetchImageAsBlob = async (url) => {
    if (!url) throw new Error('Empty URL');
    if (url.startsWith('data:image/')) return dataURLToBlob(url);
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('The image could not be downloaded.');
    return await res.blob();
  };

  const prepareSelectedImageFile = async () => {
    if (!selectedImage) return null;
    if (_selectedImageFile && _selectedImageFileUrl === selectedImage) return _selectedImageFile;
    if (_selectedImageFilePromise && _selectedImageFileUrl === selectedImage) return await _selectedImageFilePromise;
    _selectedImageFileUrl = selectedImage;
    _selectedImageFilePromise = (async () => {
      const blob = await fetchImageAsBlob(selectedImage);
      const ext  = getExtFromMime(blob.type);
      const file = new File([blob], `ai-image-${PRODUCT_ID}.${ext}`, { type: blob.type });
      _selectedImageFile = file;
      return file;
    })();
    try { return await _selectedImageFilePromise; } finally { _selectedImageFilePromise = null; }
  };

  const prepareSelectedPdfFile = async () => {
    if (!selectedImage) return null;
    if (_selectedPdfFile && _selectedPdfFileUrl === selectedImage) return _selectedPdfFile;
    _selectedPdfFileUrl = selectedImage;
    try {
      const imageBlob = await fetchImageAsBlob(selectedImage);
      const pdfBlob   = await imageBlobToPdfBlob(imageBlob);
      _selectedPdfFile = new File([pdfBlob], `ai-image-${PRODUCT_ID}.pdf`, { type: 'application/pdf' });
      return _selectedPdfFile;
    } catch (err) {
      console.warn('[AI] PDF generation failed:', err);
      return null;
    }
  };

  const syncFileToAllForms = (file) => {
    for (const form of getAllProductForms()) setFileOnInput(ensureFilePropertyInput(form, FILE_PROPERTY_KEY, 'image/*'), file);
  };

  const syncPdfToAllForms = (pdfFile) => {
    for (const form of getAllProductForms()) setFileOnInput(ensureFilePropertyInput(form, PDF_PROPERTY_KEY, 'application/pdf'), pdfFile);
  };

  const decodeViaImage = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
      img.src = url;
    });

  /* **The downscale is bounded, and it has to be.**

     Every await in `reencodeJpeg` is a decode, and a decode is not a network
     request: there is no abort signal on it and nothing else in this file would
     ever time it out. A decoder that takes an unbounded time — an image format a
     build half supports, a very large source on a slow phone — would leave the
     visitor under a full-viewport overlay with a disabled button and no way out but
     a reload, which also throws away the photograph and the name.

     Not hypothetical, if only as a shape: under Chrome's virtual time
     `createImageBitmap` on a 2400×1800 JPEG took long enough that this deadline
     fired first and the round went out carrying the original file — which is
     exactly the degradation this is for, observed working.

     That is the exact failure the 90s request timeout exists to prevent, moved one
     step earlier where nothing was watching for it. Six seconds is far longer than
     any real decode and the fallback is the original file, so the worst this
     costs is one larger upload. */
  const DOWNSCALE_TIMEOUT_MS = 6000;

  /* The cart copy gets a longer deadline, because it is decoding the largest files
     this control admits and nobody is waiting on it: it starts the moment the photo
     is chosen (`warmOriginalForCart`), a round of labels later is when it is
     needed, and the add-to-cart only waits if it is somehow still running. A
     deadline that fires here costs more than a larger upload — the raw file goes to
     the cart and may be over its limit — so it errs long. */
  const CART_REENCODE_TIMEOUT_MS = 20000;

  const withDeadline = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('decode timed out')), ms)),
    ]);

  /* One decode, one scale, one JPEG — shared by the generate request's downscale
     (`preparePhoto`) and the cart's copy (`prepareOriginalForCart`), which differ
     only in how large and how good. Never throws: `null` means "could not", and each
     caller's answer to that is the original file. Never upscales. */
  const reencodeJpeg = async (file, maxEdge, quality, timeoutMs) => {
    try {
      const source = await withDeadline(
        typeof createImageBitmap === 'function' ? createImageBitmap(file) : decodeViaImage(file),
        timeoutMs
      );
      const w = source.naturalWidth || source.width;
      const h = source.naturalHeight || source.height;
      if (!w || !h) return null;

      const scale = Math.min(1, maxEdge / Math.max(w, h));
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));

      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(source, 0, 0, cw, ch);
      source.close?.();

      return await withDeadline(
        new Promise((resolve) => {
          try { canvas.toBlob(resolve, 'image/jpeg', quality); }
          catch (_) { resolve(null); }
        }),
        timeoutMs
      );
    } catch (_) {
      return null;
    }
  };

  /* `D-231` — what `_Original Photo` carries. At or under `CART_FILE_BYTES` it is
     the visitor's file, byte for byte, which is what the fulfilment side has always
     had. Above it the raw file would pass this control's 50 MB check and then fail
     at add-to-cart, so the line gets a 4000px JPEG instead: print resolution for a
     label, and the name keeps the visitor's so an order still reads as theirs.

     **A failed re-encode sends the original anyway.** It may then be over the cart's
     limit — that is the one case this does not rescue (HEIC in a browser that cannot
     decode it, over 20 MB), and it fails exactly as it would have before `D-231`
     rather than dropping the photograph silently.

     Cached against the `File` itself, the way the PDF is cached against the image
     URL, so the warm-up at pick time and the sync at the press share one encode. */
  let _cartOriginalSource = null;
  let _cartOriginalPromise = null;

  const prepareOriginalForCart = (file) => {
    if (!file) return Promise.resolve(null);
    if (file.size <= CART_FILE_BYTES) return Promise.resolve(file);
    if (_cartOriginalSource === file && _cartOriginalPromise) return _cartOriginalPromise;
    _cartOriginalSource = file;
    _cartOriginalPromise = (async () => {
      const blob = await reencodeJpeg(file, CART_MAX_EDGE, CART_QUALITY, CART_REENCODE_TIMEOUT_MS);
      if (!blob || blob.size >= file.size) return file;
      const base = String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
      return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
    })();
    return _cartOriginalPromise;
  };

  /* Late-bound from the photo input's `change` handler near the top of this file,
     the same way `applySameDogState` is: every call is a visitor's action, after
     init, so the declaration has long been reached. */
  const warmOriginalForCart = () => { prepareOriginalForCart(photoInput?.files?.[0]).catch(() => {}); };

  const syncOriginalPhotoToAllForms = async () => {
    const originalFile = await prepareOriginalForCart(photoInput?.files?.[0]);
    if (!originalFile) return;
    for (const form of getAllProductForms()) setFileOnInput(ensureFilePropertyInput(form, ORIGINAL_PHOTO_KEY, 'image/*'), originalFile);
  };

  /* **The pack composite, held here and re-synced at the press.** `bottle-stage.js`
     draws it and announces it; this file is the only writer of form state, so it
     lands on the forms from here. It is kept in a variable as well as written
     immediately because the forms are re-read on submit — a composite that arrives
     while the visitor is mid-flow must still be on the line item, and a form that
     was created after the composite landed must still get it. */
  let _packImageFile = null;

  const syncPackImageToAllForms = (file) => {
    if (!file) return;
    for (const form of getAllProductForms()) setFileOnInput(ensureFilePropertyInput(form, PACK_PROPERTY_KEY, 'image/*'), file);
  };

  /* **The same composite, shown in Review on a phone in the single-column layout.
     `D-264`.** `blocks/buy-buttons.liquid` renders an empty, `hidden` figure
     there; this puts the latest composite in it, or hides it again when the stage
     says there is no label. One object URL at a time, revoked when replaced. Called
     from the listener below and from `syncPropertiesToAllForms`, because the
     package morph (`D-50`) re-renders the block from server HTML — empty figure
     and all — and the composite for the new package lands a moment later. Off the
     single layout the figure stays `display: none` in CSS, so painting it there
     costs one attribute write. */
  let _packPreviewUrl = null;

  /* **The composite is the stage's whole box, and the bottles are a strip down
     its middle. `D-266`.** It is drawn square-ish with the group centred and
     transparent air either side, so at the column's full width the bottles came
     out about 290px tall with a third of the picture empty — making the image
     "bigger" could not make them bigger. This crops it to what is painted (alpha
     above a whisker, so the cast shadow counts as painted), with a 2% margin, and
     the CSS then sizes the crop to the column and the viewport. A 3-bottle crop
     is roughly half as wide as it is tall, so the bottles grow by the empty air
     they no longer share the width with; a 6-bottle group is wider and gains
     less, which is right. Only the preview is cropped: the cart's `_Pack Image`
     is the untouched file. Returns null — keep the original — when there is
     nothing worth trimming or the browser cannot do it. */
  const trimTransparent = async (file) => {
    try {
      if (typeof createImageBitmap !== 'function') return null;
      const bmp = await createImageBitmap(file);
      const w = bmp.width; const h = bmp.height;
      const src = document.createElement('canvas');
      src.width = w; src.height = h;
      const ctx = src.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(bmp, 0, 0);
      const data = ctx.getImageData(0, 0, w, h).data;
      let minX = w; let minY = h; let maxX = -1; let maxY = -1;
      for (let y = 0; y < h; y++) {
        const row = y * w * 4;
        for (let x = 0; x < w; x++) {
          if (data[row + x * 4 + 3] > 8) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return null;
      const pad = Math.round(Math.max(w, h) * 0.02);
      minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
      maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
      const cw = maxX - minX + 1; const ch = maxY - minY + 1;
      if (cw > w * 0.95 && ch > h * 0.95) return null;
      const out = document.createElement('canvas');
      out.width = cw; out.height = ch;
      out.getContext('2d').drawImage(src, minX, minY, cw, ch, 0, 0, cw, ch);
      const blob = await new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/webp', 0.9));
      return blob || (await new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png')));
    } catch (_) {
      return null;
    }
  };

  const singlePhoneLayout = () =>
    document.documentElement.getAttribute('data-sdw-mobile-layout') === 'single' &&
    !!window.matchMedia?.('(max-width: 749px)')?.matches;

  /* **One picture at a time, swapped only when the next is ready. `D-268`.**
     The first version put the untrimmed composite up at once and the crop over it
     a moment later, so every rebuild — the first label, each different design
     chosen, each package — showed the bottles small, then large: a size flicker
     the client saw while previewing. Now nothing changes on screen until the
     picture that will stay is decoded: the crop in the single phone layout, the
     file itself anywhere else or if cropping fails. Until the first one is ready
     the figure stays `hidden`; after that the previous bottles stay up until the
     new ones replace them in one frame. `_packPreviewUrl` is only ever the picture
     on screen, and a newer composite arriving mid-work wins (the `file` checks). */
  /* **The picture shown is the stage's own, lit and bent, not the cart's. `D-270`.**
     The composite is drawn for a 120px cart thumbnail, flat and at 600px, and the
     client saw the label stop wrapping round the bottle when it was blown up here.
     `assets/bottle-stage.js` now follows each composite with a larger drawing that
     carries the warp, the wrap shading and the sheen, tied to the file it belongs
     to. On the single phone layout the preview waits for it (bounded, so a dead
     stage still gets the flat picture) and one that arrives late replaces the flat
     one. Only the latest is kept. */
  const _packRich = new Map();
  const _packRichWaiters = new Map();
  const richPreviewFor = (file, ms) =>
    new Promise((resolve) => {
      if (_packRich.has(file)) return resolve(_packRich.get(file));
      const list = _packRichWaiters.get(file) || [];
      list.push(resolve);
      _packRichWaiters.set(file, list);
      setTimeout(() => resolve(null), ms);
    });
  document.addEventListener('sdw:pack-preview', (event) => {
    const file = event?.detail?.file || null;
    const blob = event?.detail?.blob || null;
    if (!file) return;
    _packRich.clear();
    _packRich.set(file, blob);
    for (const resolve of _packRichWaiters.get(file) || []) resolve(blob);
    _packRichWaiters.clear();
    if (blob && file === _packImageFile && _packPreviewUrl?.file === file && !_packPreviewUrl.rich) {
      _packPreviewUrl = { ..._packPreviewUrl, file: null };
      try { paintReviewPack(); } catch (_) {}
    }
  });

  let _packPending = null;
  const paintReviewPack = () => {
    const figures = document.querySelectorAll('[data-sdw-review-pack]');
    if (!figures.length) return;
    if (!_packImageFile) {
      for (const fig of figures) fig.hidden = true;
      return;
    }
    const show = () => {
      if (!_packPreviewUrl) return;
      for (const fig of figures) {
        const img = fig.querySelector('[data-sdw-review-pack-img]');
        if (img && img.getAttribute('src') !== _packPreviewUrl.url) img.src = _packPreviewUrl.url;
        fig.hidden = false;
      }
    };
    /* Up to date, or already being prepared: just make sure what is ready is
       shown (the package morph re-renders the figure empty). */
    if ((_packPreviewUrl && _packPreviewUrl.file === _packImageFile) || _packPending === _packImageFile) {
      show();
      return;
    }
    const file = _packImageFile;
    _packPending = file;
    show();
    (async () => {
      const single = singlePhoneLayout();
      const rich = single ? await richPreviewFor(file, 4000) : null;
      const blob = single ? await trimTransparent(rich || file) : null;
      const url = URL.createObjectURL(blob || rich || file);
      /* Decoded off screen first, so the swap below cannot paint an empty frame. */
      try { const probe = new Image(); probe.src = url; await probe.decode(); } catch (_) {}
      if (_packImageFile !== file) { try { URL.revokeObjectURL(url); } catch (_) {} return; }
      const previous = _packPreviewUrl;
      _packPreviewUrl = { file, url, rich: !!rich };
      _packPending = null;
      paintReviewPack();
      /* Revoked after the swap, never before: the old picture is on screen until
         the line above replaces it. */
      if (previous) setTimeout(() => { try { URL.revokeObjectURL(previous.url); } catch (_) {} }, 1000);
    })();
  };

  document.addEventListener('sdw:pack-composite', (event) => {
    const file = event?.detail?.file || null;
    _packImageFile = file;
    if (file) syncPackImageToAllForms(file);
    try { paintReviewPack(); } catch (_) {}
  });

  syncPropertiesToAllForms = () => {
    for (const form of getAllProductForms()) {
      setOrUpdateHiddenProperty(form, 'Pet name', nameInput?.value || '');
      setOrUpdateHiddenProperty(form, 'Style', styleLabel(styleOfSelected()));
      ensureFilePropertyInput(form, FILE_PROPERTY_KEY,  'image/*');
      ensureFilePropertyInput(form, PDF_PROPERTY_KEY,   'application/pdf');
      ensureFilePropertyInput(form, ORIGINAL_PHOTO_KEY, 'image/*');
    }
    if (_packImageFile) syncPackImageToAllForms(_packImageFile);
    try { paintReviewPack(); } catch (_) {}
  };

  /* **Waits for a composite that is mid-flight, and never for long.** The stage
     rebuilds the pack picture when the package changes, and the form's own variant
     settles on a different schedule — so without this a press landing in that window
     sends one package's picture with another package's variant. Bounded, because a
     stage that never answers must not be able to hold the only button that matters:
     the press goes through with whatever composite exists, and the cart falls back
     to the flat label if that is none. */
  const COMPOSITE_WAIT_MS = 1200;

  const awaitPackComposite = async () => {
    const ready = window.SDW_STAGE?.compositeReady;
    if (typeof ready !== 'function') return;
    try {
      await Promise.race([
        ready(),
        new Promise((resolve) => setTimeout(resolve, COMPOSITE_WAIT_MS)),
      ]);
    } catch (_) {}
  };

  const syncAllFilesToForms = async () => {
    try {
      await awaitPackComposite();
      const [imageFile, pdfFile] = await Promise.all([prepareSelectedImageFile(), prepareSelectedPdfFile()]);
      if (imageFile) syncFileToAllForms(imageFile);
      if (pdfFile)   syncPdfToAllForms(pdfFile);
      await syncOriginalPhotoToAllForms();
      syncPackImageToAllForms(_packImageFile);
    } catch (err) {
      console.warn('[AI] Could not sync files to forms:', err);
    }
  };

  /* **The submit guard runs on `window`, in the capture phase, because that is the
     only place that runs before Horizon.**

     Horizon's `on:submit="/handleSubmit"` is not a listener on the form: it is one
     delegated listener on `document`, capture phase (`assets/component.js`,
     `registerEventListeners`), and capture runs from the top down — `window`, then
     `document`, then the form. This guard used to sit on the form itself (it came
     with the production theme, `P0-03`), so Horizon had already built its
     `FormData` and sent `/cart/add` before the guard ever saw the press. Its
     `preventDefault()` only stopped a navigation Horizon had stopped already.

     Measured 2026-09-28 against Horizon's own `component.js` with this guard as it
     then stood, pressing before the files had synced:
       113ms  Horizon POST /cart/add, `_AI Image` attached: false
       417ms  files synced; the guard resubmits
       418ms  Horizon POST /cart/add, `_AI Image` attached: true
     Two adds from one press — the first a line with no label on it, which is what
     fulfilment would have printed from. The window only opens when the press comes
     before the label files finish syncing (a label chosen, or a package changed, a
     moment earlier; a slow PDF or a large photo's cart copy, `D-231`, widen it),
     which is why it went unseen.

     Here the guard stops the event *before* it reaches `document`, so the press
     that has to wait for files never reaches Horizon at all; the resubmit is the
     one Horizon handles. One listener per init rather than one per form also ends
     a quieter fault: the per-form version marked each form `data-ai-bound` and
     skipped marked forms, but its listener died with this init's abort signal while
     the mark stayed on the form — so after a re-init, no form was guarded. */
  const guardProductFormSubmit = (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action^="/cart/add"]')) return;
    if (form.__aiResubmitting) { form.__aiResubmitting = false; return; }

    /* A second press while the first is still waiting on files would otherwise be a
       second intercept and a second resubmit — the same double add by another road. */
    if (form.__aiPreparing) { e.preventDefault(); e.stopImmediatePropagation(); return; }

    syncPropertiesToAllForms();
    if (!selectedImage) return;
    const fileInput = ensureFilePropertyInput(form, FILE_PROPERTY_KEY, 'image/*');
    /* A composite still drawing is treated exactly like a file not yet prepared:
       the press is intercepted, the work is awaited, and the form is resubmitted.
       Reusing that path rather than adding a second one is what keeps this to a
       condition instead of a mechanism. */
    const compositeBusy = (() => {
      try { return window.SDW_STAGE?.compositeBusy?.() === true; } catch (_) { return false; }
    })();
    /* `D-261`: a round still in flight counts as not ready. Its file sync now runs
       after the results screen is up, so for that moment the input can still hold
       the *previous* round's label — attached, so the test above would pass it. */
    if (fileInput?.files?.length && !compositeBusy && !roundInFlight) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    form.__aiPreparing = true;
    (async () => {
      try { await syncAllFilesToForms(); } catch (err) { console.warn('[AI] Could not prepare files (CORS?):', err); }
      finally {
        form.__aiPreparing = false;
        form.__aiResubmitting = true;
        const submitBtn = form.querySelector('[type="submit"]');
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitBtn || undefined);
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    })();
  };

  on(window, 'submit', guardProductFormSubmit, { capture: true });

  if (nameInput) {
    on(nameInput, 'input', () => {
      syncPropertiesToAllForms();
      saveStateToLS();
      /* The name is on the bottle as it is typed. This is the one call in this
         file that runs on every keystroke, which is why it does nothing but write
         text into elements that already exist. */
      stage()?.setName(nameInput.value);
    });
  }

  /* **The style select and its `change` listener are gone with the style step.**

     What stood here synced the cart properties, saved state, grounded the stage's
     art window and fired `SDW_TRACK_START('style')`. Each has an answer:

     - the properties and the save now happen when a *label* is chosen, which is
       where the style comes from;
     - the stage's ground follows the chosen label's style, and is `neutral` until
       there is one (`stage().setStyle`, and `assets/generator.css`'s fourth
       ground);
     - the tracking event is **removed**, not replaced. `configurator_start` still
       fires — `assets/bottle-stage.js` raises it on the first pointerdown or
       keydown inside any step — so the guardrail's four events are all intact, and
       `docs/HUMAN_QUEUE.md` records that a pre/post funnel comparison must not
       expect a style event on the post side. */

  /* **The pills' radio-group wiring came out with the pills.**

     `syncPillsFromSelect`, `selectStyle`, the roving tabindex and the arrow-key
     traversal all existed to make one required choice keyboard-operable. The
     choice is gone, so the pattern has nothing to operate: what is left is three
     example pictures that are `aria-hidden` illustration in
     `snippets/ai-generator.liquid`, and the real radio group on this rail is now
     the results grid — which has carried `role="radio"`, `aria-checked` and a
     roving tabindex since `P7-01` and is where the visitor's choice actually is.
     `docs/DECISIONS.md` D-113. */

  /* ------------------------------------------------------------------ */
  /* Gallery (Horizon carousel)                                           */
  /* ------------------------------------------------------------------ */
  const getHorizonGallery = () => {
    const mediaGallery = document.querySelector('media-gallery[data-presentation="carousel"]') || document.querySelector('media-gallery');
    if (!mediaGallery) return null;
    const slideshow = mediaGallery.querySelector('slideshow-component'); if (!slideshow) return null;
    const firstSlide = slideshow.querySelector('slideshow-slide'); if (!firstSlide || !firstSlide.parentElement) return null;
    const rail = firstSlide.parentElement;
    const dotsWrap = mediaGallery.querySelector('ol.slideshow-controls__dots') || slideshow.querySelector('ol.slideshow-controls__dots');
    const thumbsWrap = mediaGallery.querySelector('.slideshow-controls__thumbnails');
    return { mediaGallery, slideshow, rail, dotsWrap, thumbsWrap };
  };

  /* P7-02 — the badge that says a picture is the visitor's own.

     Six of the gallery's slides are photographs of somebody else's dog, and the
     visitor's generated label is dropped in among them. Without a mark there is
     nothing distinguishing "here is what you made" from "here is what we sell",
     which is the whole point of putting it in the gallery. */
  const AI_BADGE_TEXT = 'Your label';

  const makeAiBadge = (extraClass = '') => {
    const badge = document.createElement('span');
    badge.className = `ai-media-badge${extraClass ? ` ${extraClass}` : ''}`;
    badge.dataset.ai = '1';
    badge.textContent = AI_BADGE_TEXT;
    return badge;
  };

  const hzGetSlides = (g) => Array.from(g.rail.querySelectorAll('slideshow-slide'));
  const hzGetDots   = (g) => g.dotsWrap ? Array.from(g.dotsWrap.querySelectorAll('button.slideshow-control')) : [];
  const hzCurrentAiUrls = (g) => hzGetSlides(g).filter((s) => s.getAttribute('data-ai') === '1').map((s) => s.getAttribute('data-main-src') || '').filter(Boolean);
  const hzRemoveAiItems = (g) => {
    hzGetSlides(g).forEach((s) => { if (s.getAttribute('data-ai') === '1') s.remove(); });
    g.thumbsWrap?.querySelectorAll('[data-ai="1"]').forEach((t) => t.remove());
  };

  /* P7-02 — the thumbnail strip, kept in step with the slides.

     The strip is server-rendered with `on:click="/select/N"` written into each
     button, so every index shifts the moment a generated label is inserted at the
     front. Horizon's delegation reads that attribute literally, so a thumbnail
     whose index was not rewritten selects the wrong slide — silently, and on the
     one control whose whole job is picking which picture you are looking at.

     So the strip is rebuilt from the slides rather than patched: one thumbnail per
     slide, in slide order, indices rewritten, and a real click listener bound as
     well as the attribute — the same belt-and-braces `hzRebuildMobileDots` uses,
     for the same reason (a cloned node carries the attribute but not whatever the
     component bound at connect time). */
  const hzRebuildThumbnails = (g) => {
    if (!g.thumbsWrap) return;

    const slides = hzGetSlides(g);
    const base = g.thumbsWrap.querySelector('.slideshow-controls__thumbnail:not([data-ai])');
    if (!base) return;

    for (const slide of slides) {
      if (slide.getAttribute('data-ai') !== '1') continue;
      const src = slide.getAttribute('data-main-src') || '';
      if (!src) continue;
      if (g.thumbsWrap.querySelector(`[data-ai="1"][data-main-src="${CSS.escape(src)}"]`)) continue;

      const thumb = base.cloneNode(true);
      thumb.setAttribute('data-ai', '1');
      thumb.setAttribute('data-main-src', src);
      thumb.removeAttribute('id');
      thumb.setAttribute('aria-selected', 'false');
      thumb.querySelectorAll('.slideshow-controls__thumbnail-badge').forEach((b) => b.remove());

      const img = thumb.querySelector('img');
      if (img) {
        img.src = src;
        img.setAttribute('src', src);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.alt = '';
        img.loading = 'lazy';
      }

      thumb.appendChild(makeAiBadge('ai-media-badge--thumb'));
      g.thumbsWrap.insertBefore(thumb, g.thumbsWrap.firstElementChild);
    }

    /* Order the strip to match the rail exactly, then renumber every button. */
    const order = slides.map((s) => (s.getAttribute('data-ai') === '1' ? s.getAttribute('data-main-src') : null));
    const aiThumbs = Array.from(g.thumbsWrap.querySelectorAll('[data-ai="1"]'));
    /* **Moved only when out of place. `D-271`.** `insertBefore` on a node that is
       already where it is asked to go is still a removal and an insertion, two
       `childList` records — and `watchNode` below answers a `childList` change in
       the product block by calling `resyncAfterRerender`, which syncs the carousel,
       which lands here again. Every frame, for as long as the page was open: each
       pass also ran `syncAllFilesToForms`, which re-fetches the label and rebuilds
       the PDF when the PDF has not been cached. Measured on staging (Android Chrome
       emulation, stubbed generator): about 230 label fetches in 20s; at 6x CPU
       throttling the pack composite never finished and Review showed no bottles. */
    order.filter(Boolean).forEach((src, i) => {
      const t = aiThumbs.find((el) => el.getAttribute('data-main-src') === src);
      if (t && g.thumbsWrap.children[i] !== t) g.thumbsWrap.insertBefore(t, g.thumbsWrap.children[i] || null);
    });

    Array.from(g.thumbsWrap.children).forEach((thumb, i) => {
      thumb.setAttribute('on:click', `/select/${i}`);
      thumb.setAttribute('aria-label', `Slide ${i + 1} of ${slides.length}`);
      thumb.setAttribute('aria-selected', slides[i]?.classList.contains('is-active') ? 'true' : 'false');
      if (thumb.dataset.aiBound === '1') return;
      thumb.dataset.aiBound = '1';
      thumb.addEventListener('click', () => {
        const idx = Array.from(g.thumbsWrap.children).indexOf(thumb);
        if (idx >= 0) hzMarkActive(g, idx, true);
      });
    });
  };

  const hzSyncSlideMeta = (g) => {
    const slides = hzGetSlides(g); if (!g.slideshow) return;
    const tl = slides.map((_, i) => `--slide-${i + 1}`).join(', ');
    const cs = (g.slideshow.getAttribute('style') || '').replace(/--slideshow-timeline:\s*[^;]+;?/g, '').trim();
    g.slideshow.setAttribute('style', `${cs}${cs && !cs.endsWith(';') ? ';' : ''}--slideshow-timeline: ${tl};`);
    slides.forEach((slide, i) => {
      const ss = (slide.getAttribute('style') || '').replace(/--slideshow-timeline:\s*[^;]+;?/g, '').trim();
      slide.setAttribute('style', `${ss}${ss && !ss.endsWith(';') ? ';' : ''} --slideshow-timeline: --slide-${i + 1};`);
    });
  };

  const hzUpdateCounter = (g, activeIdx = null) => {
    const total = hzGetSlides(g).length;
    Array.from(g.mediaGallery.querySelectorAll('.slideshow-controls__counter')).forEach((counter) => {
      const currentEl = counter.querySelector('[ref="current"]') || counter.querySelector('span');
      if (currentEl && activeIdx !== null) currentEl.textContent = String(activeIdx + 1);
      const slash = counter.querySelector('.slash'); if (!slash) return;
      if (slash.nextElementSibling) { slash.nextElementSibling.textContent = String(total); return; }
      const cns = Array.from(counter.childNodes); const si = cns.indexOf(slash);
      if (si >= 0) { for (let i = si + 1; i < cns.length; i++) { if (cns[i].nodeType === Node.TEXT_NODE && cns[i].textContent.trim()) { cns[i].textContent = String(total); return; } } slash.after(document.createTextNode(String(total))); return; }
      const spans = counter.querySelectorAll('span'); if (spans[2]) spans[2].textContent = String(total);
    });
  };

  const hzRebuildMobileDots = (g) => {
    if (!g.dotsWrap) return;
    const slides = hzGetSlides(g); const tc = slides.length;
    const lis = Array.from(g.dotsWrap.querySelectorAll(':scope > li'));
    if (lis.length === tc) { lis.forEach((li, i) => { const b = li.querySelector('button.slideshow-control'); if (!b) return; b.setAttribute('aria-label', `Slide ${i + 1} of ${tc}`); b.setAttribute('aria-selected', slides[i]?.classList.contains('is-active') ? 'true' : 'false'); b.style.animationTimeline = `--slide-${i + 1}`; }); return; }
    while (g.dotsWrap.children.length > tc) g.dotsWrap.lastElementChild?.remove();
    while (g.dotsWrap.children.length < tc) {
      const i = g.dotsWrap.children.length; const li = document.createElement('li'); const b = document.createElement('button');
      b.type = 'button'; b.className = 'slideshow-control button button-unstyled'; b.setAttribute('aria-label', `Slide ${i + 1} of ${tc}`); b.setAttribute('aria-selected', 'false'); b.style.animationTimeline = `--slide-${i + 1}`; b.textContent = String(i + 1);
      b.addEventListener('click', () => { hzMarkActive(g, i, true); hzUpdateCounter(g, i); });
      li.appendChild(b); g.dotsWrap.appendChild(li);
    }
    Array.from(g.dotsWrap.querySelectorAll(':scope > li')).forEach((li, i) => { const b = li.querySelector('button.slideshow-control'); if (!b) return; b.setAttribute('aria-label', `Slide ${i + 1} of ${tc}`); b.setAttribute('aria-selected', slides[i]?.classList.contains('is-active') ? 'true' : 'false'); b.style.animationTimeline = `--slide-${i + 1}`; });
  };

  const hzMarkActive = (g, idx, shouldScroll = true) => {
    const slides = hzGetSlides(g); if (!slides[idx]) return false;
    slides.forEach((s, i) => { const a = i === idx; s.setAttribute('aria-hidden', a ? 'false' : 'true'); s.classList.toggle('is-active', a); });
    hzGetDots(g).forEach((d, i) => d.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
    if (g.thumbsWrap) Array.from(g.thumbsWrap.children).forEach((t, i) => t.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
    hzUpdateCounter(g, idx);
    try { g.slideshow.setAttribute('initial-slide', String(idx)); } catch (_) {}
    if (shouldScroll) {
      const ts = slides[idx]; let usedNative = false;
      try { const c = g.slideshow; if (typeof c.goToSlide === 'function') { c.goToSlide(idx); usedNative = true; } else if (typeof c.select === 'function') { c.select(idx); usedNative = true; } else if (typeof c.scrollToSlide === 'function') { c.scrollToSlide(idx); usedNative = true; } } catch (_) {}
      if (!usedNative) { try { const rr = g.rail.getBoundingClientRect(); const sr = ts.getBoundingClientRect(); g.rail.scrollTo({ left: sr.left - rr.left + g.rail.scrollLeft }); } catch (_) { try { g.rail.scrollLeft = ts.offsetLeft; } catch (_) {} } }
    }
    return true;
  };

  const hzCreateAiSlideFromBase = (baseSlide, src, index = 0) => {
    const clone = baseSlide.cloneNode(true); const uid = `ai-slide-${Date.now()}-${index}`;
    clone.setAttribute('data-ai', '1'); clone.setAttribute('data-main-src', src); clone.setAttribute('aria-hidden', 'true'); clone.classList.remove('is-active'); clone.setAttribute('ref', 'slides[]');
    clone.removeAttribute('id'); clone.removeAttribute('on:click'); clone.removeAttribute('data-view-transition-type');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('deferred-media').forEach((el) => el.remove());
    const media = clone.querySelector('.product-media'); if (media) media.setAttribute('data-media-id', uid);
    const img = clone.querySelector('img.product-media__image') || clone.querySelector('img');
    if (img) { img.src = src; img.setAttribute('src', src); img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.setAttribute('data_max_resolution', src); img.setAttribute('loading', 'lazy'); img.setAttribute('fetchpriority', 'auto'); img.setAttribute('transitiontoproduct', 'false'); img.classList.add('ai-generated'); }
    clone.querySelectorAll('.ai-media-badge').forEach((b) => b.remove());
    (media || clone).appendChild(makeAiBadge());
    return clone;
  };

  const hzSyncAiToZoomDialog = (g, urls) => {
    const zoomDialog = g.mediaGallery.parentElement?.querySelector('zoom-dialog') || g.mediaGallery.closest('[data-section-id]')?.querySelector('zoom-dialog') || document.querySelector('zoom-dialog');
    if (!zoomDialog) return;
    const galleryList = zoomDialog.querySelector('ul.dialog-zoomed-gallery') || zoomDialog.querySelector('.dialog-zoomed-gallery'); if (!galleryList) return;
    const thumbnailsContainer = zoomDialog.querySelector('scroll-hint.dialog-thumbnails-list') || zoomDialog.querySelector('.dialog-thumbnails-list');
    galleryList.querySelectorAll('[data-ai="1"]').forEach((el) => el.remove());
    if (thumbnailsContainer) thumbnailsContainer.querySelectorAll('[data-ai="1"]').forEach((el) => el.remove());
    if (!urls.length) return;
    const baseLi = galleryList.querySelector('li:not([data-ai])'); const baseThumb = thumbnailsContainer?.querySelector('button:not([data-ai])');
    urls.forEach((src, i) => {
      let li;
      if (baseLi) {
        li = baseLi.cloneNode(true); li.setAttribute('data-ai', '1'); li.removeAttribute('id');
        li.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id')); li.querySelectorAll('deferred-media, video, model-viewer').forEach((el) => el.remove());
        const img = li.querySelector('img'); if (img) { img.src = src; img.setAttribute('src', src); img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.setAttribute('loading', 'lazy'); img.classList.add('ai-generated'); img.alt = `AI Generated Image ${i + 1}`; }
        const md = li.querySelector('.product-media, [data-media-id]'); if (md) md.setAttribute('data-media-id', `ai-zoom-${Date.now()}-${i}`);
      } else {
        li = document.createElement('li'); li.setAttribute('data-ai', '1'); li.className = 'product-media-container constrain-height product-media-container--image';
        li.innerHTML = `<div class="product-media" style="--ratio:0.6666666666666666" data-media-id="ai-zoom-${Date.now()}-${i}"><img src="${src}" alt="AI Generated Image ${i + 1}" loading="lazy" class="product-media__image ai-generated" style="--focal-point:50.0% 50.0%;"></div>`;
      }
      /* P7-02: front of the zoomed gallery too, for the same reason as the rail. */
      galleryList.insertBefore(li, galleryList.children[i] || null);
      if (thumbnailsContainer) {
        let tb;
        if (baseThumb) {
          tb = baseThumb.cloneNode(true); tb.setAttribute('data-ai', '1'); tb.removeAttribute('id'); tb.setAttribute('aria-label', `AI Generated Image ${i + 1}`);
          const ti = tb.querySelector('img'); if (ti) { ti.src = src; ti.setAttribute('src', src); ti.removeAttribute('srcset'); ti.alt = `AI Generated ${i + 1}`; ti.classList.add('ai-generated'); }
        } else {
          tb = document.createElement('button'); tb.type = 'button'; tb.setAttribute('data-ai', '1'); tb.className = 'button button-unstyled dialog-thumbnails-list__thumbnail'; tb.setAttribute('aria-label', `AI Generated Image ${i + 1}`); tb.style.cssText = '--aspect-ratio:1/1.25;--gallery-aspect-ratio:1/1.25;';
          tb.innerHTML = `<img src="${src}" alt="AI Generated ${i + 1}" loading="lazy" style="object-position:50.0% 50.0%;">`;
        }
        thumbnailsContainer.insertBefore(tb, thumbnailsContainer.children[i] || null);
      }
    });
  };

  let _aiSyncing = false;

  syncAiImagesToHorizonCarousel = (urls, activeUrl, shouldScroll = false) => {
    if (_aiSyncing) return false;
    _aiSyncing = true;
    try { return _doSyncAiImages(urls, activeUrl, shouldScroll); } finally { queueMicrotask(() => { _aiSyncing = false; }); }
  };

  const _doSyncAiImages = (urls, activeUrl, shouldScroll) => {
    const g = getHorizonGallery(); if (!g) return false;
    const cleanUrls = [...new Set((Array.isArray(urls) ? urls : []).filter(Boolean))];
    const existing  = hzCurrentAiUrls(g);
    if (existing.length === cleanUrls.length && existing.every((url, i) => url === cleanUrls[i])) {
      hzSyncSlideMeta(g); hzRebuildMobileDots(g); hzRebuildThumbnails(g);
      if (activeUrl) { const idx = hzGetSlides(g).findIndex((s) => (s.getAttribute('data-main-src') || '') === activeUrl); if (idx >= 0) { hzMarkActive(g, idx, shouldScroll); return true; } }
      const ai = hzGetSlides(g).findIndex((s) => s.classList.contains('is-active')); hzUpdateCounter(g, ai >= 0 ? ai : 0); return true;
    }
    hzRemoveAiItems(g);
    if (!cleanUrls.length) { hzSyncSlideMeta(g); hzRebuildMobileDots(g); hzRebuildThumbnails(g); hzUpdateCounter(g, 0); hzSyncAiToZoomDialog(g, []); return true; }
    const baseSlide = hzGetSlides(g).filter((s) => s.getAttribute('data-ai') !== '1')[0]; if (!baseSlide) return false;
    /* P7-02: generated labels go to the **front** of the rail, not the end.
       Appended, the visitor's own bottle was slide 7 of 7 — behind six arrow
       presses, on the one image the whole flow exists to produce. Inserted before
       the first stock slide they are 1 and 2, and `hzMarkActive` below still
       selects the chosen one explicitly, so the order is what changes rather than
       what is shown. Reversed on insert because each goes in front of the last. */
    [...cleanUrls].reverse().forEach((src, i) => {
      g.rail.insertBefore(hzCreateAiSlideFromBase(baseSlide, src, i), g.rail.firstElementChild);
    });
    hzSyncAiToZoomDialog(g, cleanUrls);
    _aiSyncing = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        _aiSyncing = true;
        try {
          hzSyncSlideMeta(g); hzRebuildMobileDots(g); hzRebuildThumbnails(g);
          const slides = hzGetSlides(g);
          if (activeUrl) { const idx = slides.findIndex((s) => (s.getAttribute('data-main-src') || '') === activeUrl); if (idx >= 0) { hzMarkActive(g, idx, shouldScroll); return; } }
          const fi = slides.findIndex((s) => s.getAttribute('data-ai') === '1');
          if (fi >= 0) { hzMarkActive(g, fi, shouldScroll); return; }
          hzUpdateCounter(g, 0);
        } finally { queueMicrotask(() => { _aiSyncing = false; }); }
      });
    });
    return true;
  };

  (() => {
    const zoomDialog = document.querySelector('zoom-dialog'); if (!zoomDialog) return;
    const dialogEl = zoomDialog.querySelector('dialog') || zoomDialog;
    const zo = new MutationObserver(() => { if ((dialogEl.hasAttribute('open') || dialogEl.open) && generatedImages.length) { const g = getHorizonGallery(); if (g) setTimeout(() => hzSyncAiToZoomDialog(g, generatedImages.map((x) => x.url)), 50); } });
    zo.observe(dialogEl, { attributes: true, attributeFilter: ['open'] });
    signal.addEventListener('abort', () => zo.disconnect(), { once: true });
  })();

  /* ------------------------------------------------------------------ */
  /* Grid render                                                          */
  /* ------------------------------------------------------------------ */
  /*
    **The grid is the decision now, and that is the whole change.**

    It used to be a consolation surface: somewhere the second label went if a
    visitor spent a second draw. Since `D-113` a press returns three labels of the
    visitor's own dog and choosing between them is the only question left, so the
    grid is where the flow's substance is.

    Three things follow from that and each is load-bearing.

    **Every card is captioned with its style name.** The visitor never chose a
    style, so an uncaptioned card is an unnamed picture and the `Style` line on
    their order confirmation arrives from nowhere. `styleLabel()` is the one place
    those names live and it is what writes the cart property, so caption and
    checkout cannot disagree.

    **Cells are laid out in trios, newest trio first, and inside a trio in the fixed
    style order.** Not arrival order: whichever style the API answers first must not
    decide what the visitor sees first. A redraw appends a trio rather than
    replacing one, because a visitor who liked the second label and wanted to see
    other takes should not lose it by asking.

    **A style that failed keeps its cell.** It is rendered as a named miss with a
    retry that redraws only that style, so a round where one of three 500s is a
    round that produced two labels rather than a round that failed. The miss is not
    a radio and is wrapped in `role="none"`, which takes the wrapper out of the
    accessibility tree and leaves its retry button in it — so the radiogroup owns
    only radios and the retry is still reachable.
  */

  /* ====================================================================
     D-114 — SETS: THE NEWEST AT FULL SIZE, EVERY EARLIER ONE AS A STRIP

     A second press appended a trio and the grid grew — six cards, then nine, in
     one flat list with nothing saying which press produced what.

     **Tabs are the wrong structure and were rejected outright.** They put content
     behind a control, which is the family `PRODUCT.md` names as an anti-reference
     and which `D-113`'s own anti-goal rules out. So nothing is hidden: the newest
     set renders at full size and every earlier set collapses to one row of small
     thumbnails under a quiet label, still on the page and still selectable in one
     click. Making a new set demotes the previous one to the strip.

     **Selecting from a strip selects that label and does nothing else.** It does
     not promote its set, reorder the page or move anything under the pointer,
     because the visitor asked for a label, not for a rearrangement.

     **The set number is the round's position in the list, not the round itself.**
     Misses are not persisted (`D-113` §6), so a press that produced only misses
     leaves no round behind after a reload — and raw round numbers would then count
     1, 3 with nothing in between, which reads as a set the visitor has lost rather
     than as one that never landed. Position is dense by construction, and it needs
     no arithmetic anywhere in the markup.
     ==================================================================== */

  /* Images and misses interleaved, styles in `STYLE_ORDER` inside the round. Not
     arrival order: whichever style the API answers first must not decide what the
     visitor sees first. `sort` is stable in every engine this ships to, so two
     entries with the same rank — which is what state written before `D-113` looks
     like, all of it landing on the same fallback style — keep the order they were
     pushed in. */
  const cellsOfRound = (round) => {
    const inRound = [
      ...generatedImages.filter((item) => (Number(item?.round) || 0) === round).map((item) => ({ kind: 'image', item })),
      ...misses.filter((miss) => (Number(miss?.round) || 0) === round).map((item) => ({ kind: 'miss', item })),
    ];
    inRound.sort((a, b) => styleRank(a.item.style) - styleRank(b.item.style));
    return inRound;
  };

  const gridGroups = () => {
    const rounds = new Set();
    for (const item of generatedImages) rounds.add(Number(item?.round) || 0);
    for (const miss of misses) rounds.add(Number(miss?.round) || 0);

    const ascending = [...rounds].sort((a, b) => a - b);
    const groups = [];
    for (let i = ascending.length - 1; i >= 0; i -= 1) {
      groups.push({
        round: ascending[i],
        set: i + 1,
        cells: cellsOfRound(ascending[i]),
      });
    }
    return groups;
  };

  /* One flat list in render order, which is also visual order and therefore the
     order the arrow keys walk: the newest set, then each earlier set. Since
     `D-162` it is also the *only* order — there is one row, so this list and the
     row are the same sequence rather than two views of it. */
  const gridCells = () =>
    gridGroups().flatMap((group) => group.cells.map((cell) => ({ ...cell, set: group.set })));

  const getGridRenderKey = () =>
    JSON.stringify(
      /* `set` is in the key because a redraw changes neither the urls nor the
         selection, and the set number is what a card's accessible name carries
         once there is more than one of them. `tier` used to be here too, to catch
         the previous set being demoted to a strip; `D-162` put every card in one
         row at one size, so there is no demotion left to catch. */
      /* **Which card is checked is deliberately not in this key any more.** It was,
         and that made every selection a full teardown of the row — `innerHTML = ''`
         and three cards rebuilt to move a ring, with `carouselScroll` existing only
         to put the scroll offset back afterwards.

         That was tolerable while a selection could only come from a tap or an arrow
         key. It is not once a *swipe* selects: the rebuild destroys the very
         scroll container the gesture is happening in, and restoring an offset onto
         a fresh element mid-momentum is a race. Traced on a real touch swipe, one
         flick produced two selections and the card left in view was not always the
         card left checked.

         Nothing is lost by taking it out. The checked state is three attributes and
         a class, and `paintCheckedState` below writes them in place — which is
         cheaper than a rebuild on every path, not only on this one. The key still
         holds everything that changes the row's *contents*, so "Make three more"
         and a retry landing still rebuild. */
      gridCells().map((cell) =>
        cell.kind === 'image'
          ? ['i', cell.set, cell.item.url, cell.item.style || '']
          : ['m', cell.set, cell.item.style || '', cell.item.message || '']
      )
    );

  /* **The selection, written onto the cards that are already there.**

     Exactly one card is checked and exactly one is tabbable, and the two are the
     same card whenever anything is selected — a radiogroup has one tab stop, and it
     belongs on the answer. With nothing selected the stop falls to the first card,
     or the group is unreachable by keyboard.

     `markedSelected` reproduces the render's own first-match-wins rule: selection is
     held as a *url* and two entries can carry the same one, because the app is a
     third party's and nothing stops it returning an identical URL for two draws of
     the same photograph and style. Two checked radios is an invalid group and two
     answers to a single-answer question, so the first card with that url takes it. */
  const paintCheckedState = () => {
    if (!grid) return;
    const cards = grid.querySelectorAll('.ai-option');
    let marked = false;
    cards.forEach((card, i) => {
      const checked = !marked && !!selectedImage && card.getAttribute('data-ai-url') === selectedImage;
      if (checked) marked = true;
      card.setAttribute('aria-checked', checked ? 'true' : 'false');
      card.classList.toggle('selected', checked);
      card.setAttribute('tabindex', checked || (!selectedImage && i === 0) ? '0' : '-1');
    });
    /* A selection that matched nothing — a stale url from storage whose card is no
       longer rendered — would otherwise leave the group with no tab stop at all. */
    if (!marked && cards.length) cards[0].setAttribute('tabindex', '0');
  };

  /* **`focusAfterRender` used to live here and is deleted.** The arrow-key handler
     set it because choosing a card rebuilt the grid and destroyed the focused
     element; the selection is painted in place now, so the card survives its own
     selection and the handler focuses it directly. A mechanism with no setter left
     is something the next reader has to disprove. */

  /* **Which cards have already been on screen.** The grid's children are rebuilt on
     every selection change, so an entrance animation keyed to "a card was rendered"
     would replay on all of them every time the visitor compares two labels — motion
     as a twitch rather than as meaning. A card animates in once, the first time it
     exists, and never again. */
  const enteredUrls = new Set();

  const chooseLabelCard = async (item) => {
    selectedImage = item.url;
    _selectedImageFile = null; _selectedImageFileUrl = null;
    _selectedPdfFile   = null; _selectedPdfFileUrl   = null;
    syncPropertiesToAllForms();
    syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, true);
    /* Choosing between labels that have already landed is a comparison, not a
       reveal: the swap is instant and the settle does not play. It plays once, when
       the label first arrives. The ground follows the chosen label's style, which
       is what replaced the style pills as its driver. */
    stage()?.setStyle(item.style || NEUTRAL_GROUND);
    stage()?.setLabel(item.url, { settle: false });

    /* **The selection paints before the files are prepared, not after.**

       This used to `await syncAllFilesToForms()` and only then re-render, so the
       tick, the Ink ring and the caption's weight all waited on a fetch of the
       label plus a PDF build — a second or more on a phone, during which a tap on
       a card looked like a tap that missed. The state is already correct
       synchronously; only the paint was late.

       It matters more now than it did. The grid was a secondary surface when the
       style pills were the decision; since `D-113` it *is* the decision, and it is
       also driven by the arrow keys, where a lagging repaint reads as a dead key.

       The files still have to reach the forms, so the slow half simply runs after
       the fast half instead of in front of it, and saves again when it lands. The
       add-to-cart path does not depend on this finishing: the form's own submit
       guard prepares the files if they are not there yet. */
    saveStateToLS(); renderGridIfNeeded(); scheduleRefresh(); updateRedrawState();

    await syncAllFilesToForms();
    saveStateToLS();
  };

  const buildLabelCard = (item, { checked, tabbable, set = 1, showSet = false }) => {
    const wrap = document.createElement('button');
    wrap.type = 'button';
    wrap.className = 'ai-option';

    /* P7-01: these are mutually exclusive options where exactly one is always
       chosen, so they are radios, not unlabelled buttons. The selected state used
       to be a border colour and nothing else, which is meaning carried by colour
       alone on the control that decides what gets printed. `aria-checked` states
       it, and the accessible name is the style rather than an index — "Label 2" told
       a screen-reader user nothing about which of the three it was. */
    const petName = (nameInput?.value || '').trim();
    const name = styleLabel(item.style || '') || 'Label';
    wrap.setAttribute('role', 'radio');
    /* **The set is in the accessible name once there is more than one of them**,
       and `D-162` changed what that is for. It used to tell the strips apart from
       the full-size row without a nested group — a `radiogroup`'s owned elements
       are radios, so wrapping a strip in `role="group"` to carry a label was
       structure ARIA does not allow there.

       There are no strips now: one row, one size, and the only thing separating
       the third label from the fourth is where it sits. A sighted visitor reads
       that off the order. A screen-reader visitor gets no order cue at all, and
       three presses of "Try again" would otherwise announce nine
       radios of which three are called "Bold Portrait, Feuchón" — so the set
       number is what stands in for position, and it is added exactly when there
       is a second set to be confused with. */
    const label = petName ? `${name}, ${petName}` : name;
    wrap.setAttribute('aria-label', showSet ? `${label}, set ${set}` : label);
    wrap.setAttribute('aria-checked', checked ? 'true' : 'false');
    wrap.setAttribute('tabindex', tabbable ? '0' : '-1');
    if (checked) wrap.classList.add('selected');

    /* The card's identity across a re-render, so keyboard focus can be put back on
       the same card after the grid is rebuilt under it. */
    wrap.setAttribute('data-ai-url', item.url);

    const img = document.createElement('img');
    img.src = item.url;
    /* The button already carries the name; an alt that repeats it makes a screen
       reader say it twice. */
    img.alt = '';
    img.loading = 'lazy';
    wrap.appendChild(img);

    const check = document.createElement('span');
    check.className = 'ai-option__mark';
    check.setAttribute('aria-hidden', 'true');
    wrap.appendChild(check);

    /* The caption is inside the card and `aria-hidden`, because the button's own
       accessible name already says it. Two announcements of one word is noise.

       Every card carries one since `D-162`. The strip thumbnails used to go
       without, because a 14px word under a picture a third the linear size would
       have been the loudest thing in a deliberately quiet row — but there is no
       quiet row now. Every card is the same card, so every card says what style
       it is. */
    const caption = document.createElement('span');
    caption.className = 'ai-option__caption';
    caption.setAttribute('aria-hidden', 'true');
    caption.textContent = name;
    wrap.appendChild(caption);

    wrap.addEventListener('click', () => { void chooseLabelCard(item); });
    return wrap;
  };

  const buildMissCard = (miss) => {
    const wrap = document.createElement('div');
    wrap.className = 'ai-miss';
    /* `role="none"` removes the wrapper from the accessibility tree and keeps its
       children in it — so the radiogroup owns radios only, and the retry button
       below is still a button a screen reader and a keyboard both reach. */
    wrap.setAttribute('role', 'none');

    const name = styleLabel(miss.style || '') || 'That style';

    const head = document.createElement('p');
    head.className = 'ai-miss__head';
    head.textContent = `${name} didn’t come back.`;
    wrap.appendChild(head);

    /* **Every miss keeps its retry now.** `D-114` dropped it from the strips —
       a 44px control in a row of thumbnails was a full-size ask sitting in a
       footnote's space, and it bought one more thumbnail in a set the visitor had
       already moved past. `D-162` put every card in one row at one size, so there
       is no footnote to sit in and no set that has been moved past: a miss three
       positions along the row is as retryable as a miss in first place, and
       hiding the control there would now be the arbitrary half of the rule rather
       than the considered one. */
    const body = document.createElement('p');
    body.className = 'ai-miss__body';
    body.textContent = miss.message || 'Something went wrong on our end.';
    wrap.appendChild(body);

    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'ai-miss__retry';
    retry.textContent = `Try ${name} again`;
    retry.addEventListener('click', () => { void redrawStyle(miss); });
    wrap.appendChild(retry);

    return wrap;
  };

  /* ====================================================================
     THE RESULTS ARE A SCROLLER — client-directed, and it reverses a standing
     anti-goal, so the reversal is written down rather than absorbed.

     `D-113` and `D-114` both refused a carousel here, citing `PRODUCT.md`'s
     anti-reference and `D-62`'s test for it: *a carousel puts content behind a
     control and asks the visitor to wait for it.* The client asked for one row at
     every width, with floating arrows for the older sets and a swipe on touch.

     **What is built passes `D-62`'s test, and that is the whole argument.**
     Nothing here waits: there is no timer, no auto-advance and no state a visitor
     has to sit through. Every card is one gesture away, the next one is visibly
     peeking at the edge of the track at the width where they do not all fit, and
     the scroll position is preserved across the rebuild that a selection
     triggers, so choosing a card never moves the row under the finger that chose
     it. A surface that hid a card behind a control until a machine decided to
     reveal it would still be a carousel; this shows the edge of what is next and
     lets the visitor take it whenever they like.

     What it is *not* is the old grid at a narrow width. That grid stacked to one
     column below 749px and the three labels became three screens, which is the
     failure `D-113`'s own note names — "the visitor would be scrolling between
     candidates from memory, which is the failure the single column was supposed
     to prevent". A row you swipe keeps them adjacent. `D-162`.
     ==================================================================== */

  /* The track's scroll offset, kept across the rebuild that every selection
     causes. `getGridRenderKey` includes which card is checked, so tapping a card
     rebuilds the whole grid — and without this the row would snap back to its
     first card at the moment the visitor chose a later one, moving the thing they
     just touched out from under them. Keyed by set rather than by element,
     because the elements do not survive. */
  const carouselScroll = new Map();

  /* **Settle suppression, and it has to outlive the track that raised it.**

     A *rebuild* — "Make three more", or a retry landing — replaces the row wholesale
     with `innerHTML = ''`, so the element the scroll listener was bound to is
     destroyed, its offset resets to 0, and `wireCarousel` restores the remembered
     offset onto the replacement. Each of those steps produces scroll events, and the
     restore's own would otherwise settle onto whatever card it lands on and choose
     it — a redraw would silently change the visitor's answer.

     Held per track the guard would be thrown away with the element that set it, so
     it is a timestamp in the closure instead, spanning the whole cycle.

     **Choosing a card does not go through here**, because choosing no longer
     rebuilds anything: `getGridRenderKey` stopped depending on the selection and
     `paintCheckedState` writes it onto the cards in place. That is what made
     swipe-to-select possible at all — a gesture cannot happen inside a scroll
     container that the gesture itself destroys. */
  let suppressSettleUntil = 0;
  const suppressSettle = (ms = 500) => { suppressSettleUntil = Date.now() + ms; };

  const rememberCarouselScroll = () => {
    if (!grid) return;
    grid.querySelectorAll('.ai-carousel__track[data-carousel-key]').forEach((track) => {
      carouselScroll.set(track.getAttribute('data-carousel-key'), track.scrollLeft);
    });
  };

  /* **The arrows are `aria-hidden` and never take a tab stop**, which is the one
     decision in this component worth arguing about.

     `.ai-grid` is a `radiogroup`, and a radiogroup's owned elements are radios —
     the reason `.ai-miss` and the carousel's own wrapper carry `role="none"`. A
     pair of real buttons inside it would be two non-radio owned elements and two extra
     tab stops inside a group whose whole pattern is *one* tab stop and arrow keys
     between the cards.

     They are not needed there either. The arrow keys already move the selection
     card by card, and moving focus to a card inside an `overflow: auto` box
     scrolls it into view on its own, so a keyboard visitor reaches every card in
     the row without ever meeting these. Touch swipes. What is left is the
     pointer, which is exactly who these are for. Hidden from the tree, they are
     not a radiogroup violation at all rather than an excused one.

     No visible text on them, by instruction — the direction is drawn, and the
     row it sits on is already titled. */
  const buildCarouselArrow = (dir) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `ai-carousel__arrow ai-carousel__arrow--${dir}`;
    button.setAttribute('aria-hidden', 'true');
    button.setAttribute('tabindex', '-1');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">' +
      `<path d="${dir === 'prev' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7'}" ` +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  };

  const buildCarousel = (key, trackClass) => {
    const root = document.createElement('div');
    root.className = 'ai-carousel';
    root.setAttribute('role', 'none');
    root.setAttribute('data-overflow', 'none');

    const track = document.createElement('div');
    track.className = trackClass;
    track.setAttribute('role', 'none');
    track.setAttribute('data-carousel-key', key);

    root.appendChild(track);
    root.appendChild(buildCarouselArrow('prev'));
    root.appendChild(buildCarouselArrow('next'));
    return { root, track };
  };

  /* One observer's worth of bookkeeping per track, torn down with the track: the
     grid is rebuilt wholesale, so anything that outlived a render would pile up
     one copy per press of "Make three more". */
  const wireCarousel = (root) => {
    const track = root.querySelector('.ai-carousel__track');
    const prev = root.querySelector('.ai-carousel__arrow--prev');
    const next = root.querySelector('.ai-carousel__arrow--next');
    if (!track || !prev || !next) return;

    /* `data-overflow` drives both the arrows and the edge fade, and it says which
       *side* has more rather than merely that something does — a fade on the left
       when there is nothing to the left is an affordance that lies. */
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const x = track.scrollLeft;
      const atStart = x <= 1;
      const atEnd = x >= max - 1;
      root.setAttribute(
        'data-overflow',
        max <= 1 ? 'none' : atStart ? 'end' : atEnd ? 'start' : 'both'
      );
      prev.disabled = atStart;
      next.disabled = atEnd;
    };

    /* A page is one card plus its gap, measured off the track rather than
       declared, so the CSS stays the only place the card width is decided. Falls
       back to most of the track if the row is somehow empty. */
    const page = (direction) => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      track.scrollBy({
        left: direction * step,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    /* ---- swiping to a card is choosing it -----------------------------

       **The keyboard has always worked this way and touch did not.** The arrow-key
       handler further down says it in as many words — *moving is selecting, as it
       is in a native radio group* — and calls the same click path a pointer does,
       so arrowing through the three previews them on the bottle. On touch the same
       row asked for two gestures: swipe the card into view, then tap it. The tap
       carried no information the scroll position did not already have.

       **Only where exactly one card is in view**, which is what makes "the card you
       swiped to" a well-defined thing at all. Above that the row shows two or three
       at once and there is no single card the scroll is about, so the tap stays the
       only way to choose. `--sdw-per-view` is read off the element rather than
       guessed from a breakpoint, because it is set by container queries against the
       rail's own width and the rail is not the window (`D-114` §6).

       **On settle, never during.** `getGridRenderKey` includes which card is
       checked, so every selection rebuilds the whole grid — selecting mid-flick
       would rebuild the DOM under a moving thumb, and each intermediate card would
       also cross-fade the stage and queue a composite. `scrollend` where it exists,
       a short debounce where it does not.

       The peek stays. A quarter of the next card is what says there is one, and
       `D-162` argues it is a truer indicator than a chevron because it is the thing
       itself. What it is not, now, is something you have to tap after reaching. */
    const perView = () => parseFloat(getComputedStyle(root).getPropertyValue('--sdw-per-view')) || 1;

    /* **The card in view is the one whose centre is nearest the track's centre**,
       not the one whose start edge is nearest the track's start.

       `scroll-snap-align: start` makes the start edge the obvious measure and it is
       wrong at the end of the row: the last card cannot be scrolled to the start
       when doing so would exceed the maximum offset, so at full scroll every card's
       start edge is some distance away and the nearest one is not the one filling
       the view. Centres have no such asymmetry — whatever is on screen is what is
       closest to the middle of it, at both ends and in between.

       Measured off rendered boxes rather than computed from a card width, for the
       same reason `page()` above is: the width is decided in the stylesheet and
       this must not hold a second copy of it. */
    const snappedChild = () => {
      const box = track.getBoundingClientRect();
      const middle = box.left + box.width / 2;
      let best = null;
      let bestGap = Infinity;
      for (const child of track.children) {
        const r = child.getBoundingClientRect();
        const gap = Math.abs(r.left + r.width / 2 - middle);
        if (gap < bestGap) { bestGap = gap; best = child; }
      }
      return best;
    };

    const selectSnapped = () => {
      if (Date.now() < suppressSettleUntil) return;
      if (perView() > 1) return;
      const card = snappedChild();
      /* A miss cell is a child of the same track and is not a radio. Swiping onto
         one leaves the choice alone rather than clearing it: the visitor moved
         past a failure, they did not un-choose a label. */
      if (!card || !card.classList.contains('ai-option')) return;
      /* Already the answer. This is also the second half of the restore guard: a
         settle that lands back on the checked card is a no-op rather than a
         re-selection, so nothing here can feed itself. */
      if (card.getAttribute('aria-checked') === 'true') return;
      /* **Nothing here moves the caret, and that is the point.** A swipe is not a
         keyboard gesture and must not steal focus; and since a selection no longer
         rebuilds the row, there is no destroyed element to put focus back onto
         either. A card that had the caret keeps it. */
      card.click();
    };

    /* **A debounce on `scroll`, and deliberately not `scrollend`.**

       `scrollend` looks like the right event and is the wrong one here. With
       `scroll-snap-type: x mandatory` the browser fires it at the end of the
       *momentum*, before the snap animation that follows — so it reports a position
       that is not a snap position at all. Traced on a real touch swipe at 390px:
       the flick ran to 495 against a maximum of 494, `scrollend` fired there, the
       card nearest the start edge was the third one, and the row then snapped back
       to 283 showing the second. One swipe chose a card the visitor never landed
       on, and the correction fired a second selection behind it — which card won
       depended on timing.

       The debounce has no such gap, because the snap animation is itself a stream
       of scroll events: each one restarts the timer, so it can only elapse once the
       row has actually stopped. 160ms is comfortably longer than a frame and
       shorter than a visitor's next gesture. */
    const SETTLE_MS = 160;
    let settleTimer = null;

    prev.addEventListener('click', () => page(-1));
    next.addEventListener('click', () => page(1));
    track.addEventListener(
      'scroll',
      () => {
        update();
        clearTimeout(settleTimer);
        settleTimer = setTimeout(selectSnapped, SETTLE_MS);
      },
      { passive: true }
    );
    if (typeof ResizeObserver === 'function') new ResizeObserver(update).observe(track);

    const remembered = carouselScroll.get(track.getAttribute('data-carousel-key'));
    if (remembered) {
      /* The restore and the snap it provokes are this file's own scrolling, not the
         visitor's, and must not be read as a choice. */
      suppressSettle();
      track.scrollLeft = remembered;
    }
    update();
  };

  renderGridIfNeeded = () => {
    if (!results || !grid) return;
    const groups = gridGroups();
    const cells = gridCells();
    if (!cells.length) {
      if (chooseLabel) chooseLabel.hidden = true;
      results.hidden = true; grid.hidden = true;
      grid.removeAttribute('data-ai-render-key'); grid.innerHTML = '';
      return;
    }
    /* Not unconditional — see `isRetrying` above `syncFieldsCollapse`. A resync
       finding cards to draw is not, on its own, permission to show them: the
       fields may be open over an existing, finished set on purpose. */
    if (!isRetrying()) results.hidden = false;
    /* The instruction only makes sense when there is something to pick between.
       A round where two of three failed leaves one label and two misses, and
       "Pick the one that looks like them" over a single card is an instruction to
       do nothing. */
    if (chooseLabel) chooseLabel.hidden = generatedImages.length < 2;
    grid.hidden = false;

    const renderKey = getGridRenderKey();
    /* The child count is no longer the cell count — a strip is one child holding
       three — so the key alone decides whether a rebuild is needed.

       **The common case now leaves through here.** Choosing a card does not change
       the key, so this returns early and the selection is painted onto the cards
       that already exist — no teardown, no rebuilt track, and the scroll offset
       untouched because nothing was replaced. */
    if (grid.getAttribute('data-ai-render-key') === renderKey) {
      paintCheckedState();
      return;
    }
    rememberCarouselScroll();
    grid.setAttribute('data-ai-render-key', renderKey); grid.innerHTML = '';

    /* A radiogroup with nothing checked still needs exactly one tab stop, or the
       group is unreachable by keyboard. It falls to the first image cell — which
       is in the newest set, because that is what renders first. */
    const firstImageIndex = cells.findIndex((cell) => cell.kind === 'image');

    /* **Exactly one card is ever checked, and this is what guarantees it.**

       Selection is held as a *url*, and two entries can carry the same one: the
       app is a third party's and nothing stops it returning an identical URL for
       two draws of the same photograph and style — a cache hit, a deterministic
       path. Measured with a redraw against a stub that does exactly that: six
       cards, `aria-checked="true"` on two of them and the Ink selection ring on
       both, which is an invalid radiogroup and a visitor being shown two answers
       to a single-answer question.

       The first match wins. Both cards are the same picture, so nothing about the
       visitor's choice is lost by only marking one of them. */
    let markedSelected = false;

    /* The entrance stagger's rank counts cells within this render, so a trio
       arriving together lands left to right — the same gesture the stage's bottles
       make with `--sdw-rank`, at a smaller size. */
    let enterRank = 0;

    /* Flat index across every set, so the one tab stop and the arrow keys are
       computed over the whole group rather than per set. */
    let flatIndex = 0;

    const appendCell = (parent, cell, set) => {
      const idx = flatIndex;
      flatIndex += 1;

      if (cell.kind === 'miss') {
        const missCard = buildMissCard(cell.item);
        const missKey = `miss:${cell.item.round}:${cell.item.style}`;
        if (!enteredUrls.has(missKey)) {
          enteredUrls.add(missKey);
          missCard.classList.add('is-entering');
          missCard.style.setProperty('--sdw-card-rank', String(enterRank));
          enterRank += 1;
        }
        parent.appendChild(missCard);
        return;
      }

      const checked = !markedSelected && cell.item.url === selectedImage;
      if (checked) markedSelected = true;
      const card = buildLabelCard(cell.item, {
        checked,
        tabbable: checked || (!selectedImage && idx === firstImageIndex),
        set,
        showSet: groups.length > 1,
      });
      /* **The entrance plays once per card, and being pushed along the row is not
         a second arrival.** Every card is rebuilt as a new element on every
         render, but its url is already in `enteredUrls`, so the cards that were
         already there sit still at their new positions while the three that just
         arrived play. That is the correct reading of the moment: three came, and
         the rest shifted along to make room. */
      if (!enteredUrls.has(cell.item.url)) {
        enteredUrls.add(cell.item.url);
        card.classList.add('is-entering');
        card.style.setProperty('--sdw-card-rank', String(enterRank));
        enterRank += 1;
      }
      parent.appendChild(card);
    };

    /* **One row, one size, every label in it** (`D-162`). `cells` is already the
       flat sequence — newest set first, then each earlier one — so the row and
       the order the arrow keys walk are the same list rather than two views of
       it. The strips are gone with the second row: there is no "earlier draw"
       tier any more, only a label further along.

       The "Other options" heading went with them, by instruction. It was
       `aria-hidden`, so nothing was reading it aloud; what it said to the eye is
       now said by position, and each card still carries its set in its accessible
       name once there is a second set to be confused with. */
    const row = buildCarousel('labels', 'ai-carousel__track');
    cells.forEach((cell) => appendCell(row.track, cell, cell.set));
    grid.appendChild(row.root);

    wireCarousel(row.root);

    /* The cards were built with the selection the render saw; this is what keeps
       them right when a later selection does not rebuild them. Run on both paths
       so there is one statement of the checked state rather than two. */
    paintCheckedState();

    /* Focus was on a card that this rebuild destroyed. Put it back on the same
       one, or the arrow keys move the selection once and then strand the visitor
       at the top of the document. */
  };

  /* ------------------------------------------------------------------ */
  /* D-113 — the grid is a radio group, so the arrow keys have to work     */
  /* ------------------------------------------------------------------ */
  /*
    **This is a regression `D-113` would have shipped without it.**

    The grid has carried `role="radio"`, `aria-checked` and a roving tabindex since
    `P7-01`, but never arrow-key traversal — it did not need it while the *style*
    pills were the decision on this rail and carried the full pattern themselves.
    Deleting the pills made the grid the only radio group in the flow, and a
    radiogroup with a roving tabindex and no arrow keys is the worse half of the
    pattern on its own: one tab stop, on the checked card, and no way to reach the
    other two. A keyboard visitor could see three labels and choose only the one
    the script picked for them.

    So it is bound here rather than in `renderGridIfNeeded`: the grid's children are
    rebuilt from scratch on every selection change, and a listener per card would be
    re-added on every render. One delegated listener on the container outlives all
    of it.

    Moving *is* selecting, as it is in a native radio group — which is why this
    calls the same click path a pointer does rather than a quieter one. That path
    puts the label on the stage, so arrowing through the three previews them on the
    bottle exactly as clicking through them does.
  */
  if (grid) {
    on(grid, 'keydown', (e) => {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!keys.includes(e.key)) return;

      /* Misses sit in the same container and are not part of the group. */
      const options = Array.from(grid.querySelectorAll('.ai-option'));
      const current = options.indexOf(document.activeElement);
      if (current === -1) return;

      e.preventDefault();

      let next;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = options.length - 1;
      else {
        const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
        next = (current + step + options.length) % options.length;
      }

      const target = options[next];
      if (!target) return;
      /* **Focus is moved here rather than carried across a rebuild.** It used to be
         handed to `focusAfterRender`, because choosing a card tore the grid down and
         the element under the caret was destroyed by the very click that selected
         it. A selection no longer rebuilds anything — the checked state is painted
         onto the cards in place — so the card is still there afterwards and can
         simply take the caret.

         After the click, because the click is what makes this card the tabbable one:
         `paintCheckedState` moves the group's single tab stop to the answer, and
         focusing before that would put the caret on a card that is about to be
         given `tabindex="-1"`. */
      target.click();
      try { target.focus({ preventScroll: true }); } catch (_) {}
    });
  }

  /* ------------------------------------------------------------------ */
  /* Mobile modal                                                         */
  /* ------------------------------------------------------------------ */
  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

  const getMobileModal = () => {
    const rootEl = document.getElementById('ai-mobile-preview-modal'); if (!rootEl) return null;
    const imgEl    = rootEl.querySelector('[data-ai-modal-img]');
    const closeBtn = rootEl.querySelector('[data-ai-modal-close]');
    const backdrop = rootEl.querySelector('[data-ai-modal-backdrop]');
    if (!imgEl || !closeBtn || !backdrop) return null;
    return { rootEl, imgEl, closeBtn, backdrop };
  };

  const scrollToProductInfo = () => {
    const target = document.getElementById('product-information-home'); if (!target) return;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - 12, behavior: 'smooth' });
  };

  const closeMobilePreview = () => {
    const m = getMobileModal(); if (!m) return;
    m.rootEl.hidden = true; m.rootEl.setAttribute('aria-hidden', 'true'); document.documentElement.classList.remove('ai-modal-open'); scrollToProductInfo();
  };

  const openMobilePreview = (url) => {
    if (!url || !isMobile()) return;
    const m = getMobileModal(); if (!m) return;
    m.imgEl.src = url; m.rootEl.hidden = false; m.rootEl.setAttribute('aria-hidden', 'false'); document.documentElement.classList.add('ai-modal-open');
  };

  (() => {
    const m = getMobileModal(); if (!m || m.rootEl.dataset.bound === '1') return;
    m.rootEl.dataset.bound = '1';
    m.closeBtn.addEventListener('click', closeMobilePreview); m.backdrop.addEventListener('click', closeMobilePreview);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !m.rootEl.hidden) closeMobilePreview(); }, true);
  })();

  /* ------------------------------------------------------------------ */
  /* hydrate from LS                                                      */
  /* ------------------------------------------------------------------ */
  (function hydrateFromLS() {
    const saved = readLS(); if (!saved) return;
    hydrating = true;
    if (nameInput   && typeof saved.petName === 'string') nameInput.value = saved.petName || '';

    /* `D-143`. Both come back before anything paints. `landedText` is what the
       stage's band says and it re-lands from `stage()?.rehydrate()` at the foot of
       this restore, which reads the same bucket; `sameDogActive` is what the label
       step says, and it is applied below once the grid is settled. */
    landedText = (saved.landed && typeof saved.landed.text === 'string') ? saved.landed.text : '';
    sameDogActive = saved.sameDog === true;

    /* **State written by the shipped version exists in real browsers, and this must
       not throw on it.** Before `D-113` an entry was `{ url, createdAt }` with no
       `style` and no `round`, and the style was a single top-level key. A visitor
       who was mid-flow when this deploy landed is a real person, not an edge case:
       their entries take the saved top-level style, then `bold_portrait`, and all
       land in round 0 — one trio-shaped group in the grid, which is what they had.

       The same tolerance is what the morph re-hydrate path (`resyncAfterRerender`,
       `D-50`) and `assets/bottle-stage.js` need, because all three read this bucket. */
    const fallbackStyle =
      (typeof saved.style === 'string' && STYLE_ORDER.includes(saved.style) && saved.style) || STYLE_ORDER[0];

    const imgs = Array.isArray(saved.generatedImages) ? saved.generatedImages : [];
    imgs.forEach((it) => {
      if (!it?.url) return;
      generatedImages.push({
        url: it.url,
        style: typeof it.style === 'string' && it.style ? it.style : fallbackStyle,
        round: Number.isFinite(Number(it.round)) ? Number(it.round) : 0,
        createdAt: it.createdAt || Date.now(),
      });
    });
    nextRound = generatedImages.reduce((max, item) => Math.max(max, (Number(item.round) || 0) + 1), 0);

    /* **A restored card has already arrived, so it does not arrive again.**
       `enteredUrls` is what makes the entrance play once per card, and it is empty
       on a fresh page — so every label restored from storage played the arrival
       animation on load, as if it had just been drawn. That was three cards before
       `D-114` and is up to twelve after it, which is an orchestrated page-load
       sequence in front of a visitor who came back to finish a purchase.

       Seeding the set here rather than suppressing the class keeps one mechanism:
       the entrance still means *this card was not on screen a moment ago*, and a
       reload is not that. */
    for (const item of generatedImages) enteredUrls.add(item.url);
    generationCount = typeof saved.generationCount === 'number' ? saved.generationCount : generatedImages.length;
    if (saved.selectedImage && generatedImages.some((x) => x.url === saved.selectedImage)) selectedImage = saved.selectedImage;
    else selectedImage = generatedImages[0]?.url || null;
    syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, true);
    if (selectedImage) {
      const retry = () => { const g = getHorizonGallery(); if (!g) return; const idx = hzGetSlides(g).findIndex((s) => (s.getAttribute('data-main-src') || '') === selectedImage); if (idx >= 0) hzMarkActive(g, idx, true); };
      setTimeout(retry, 500); setTimeout(retry, 1200);
    }
    syncPropertiesToAllForms(); renderGridIfNeeded(); scheduleRefresh();
    if (selectedImage) syncAllFilesToForms().catch(() => {});
    updateRedrawState();
    /* After `updateRedrawState`, because that is what re-opens the collapsed fields
       when no label exists — and the line this writes sits above them. */
    try { applySameDogState(); } catch (_) {}
    /* The guard comes off before the last two lines, so the write below is the one
       write this restore makes and the stage reads the state it has just settled. */
    hydrating = false;
    /* **What is on screen is what produced these labels, as far as anyone can
       tell.** A file input cannot be restored, so the photograph that drew them is
       gone; taking the signature here is what makes an untouched return advance on
       Next instead of failing validation, and what makes a newly attached photo
       read as the change it is. */
    markInputsDrawn();
    announceGenState();
    saveStateToLS();
    /* The stage reads the same storage and can rebuild itself, but it may have
       booted before this restore ran — the two scripts are in two columns and
       neither waits for the other. Asking it once here means a returning visitor's
       bottles are already wearing their label rather than filling in a frame
       later. Never with a settle: that plays at the reveal and nowhere else. */
    stage()?.rehydrate();
  })();

  /* **The cart's "Create a second bottle" arrives here already pressed. `D-259`.**
     That link (`snippets/gift-second-label.liquid`) is a route back to this page,
     and the restore just above re-lands the stage from storage — "May is in your cart."
     with *Create another bottle* under it — so the buyer pressed a button that
     said *create a second bottle* and was shown another button saying the same
     thing. The client's recording of 2026-10-08 shows exactly that double press.

     The link now carries `sdw_next=1`, and this finishes the job it started, but
     only when the restored state is the landed one. A buyer who had already begun
     a second bottle — labels drawn, not yet added — and then looked at the cart
     has unbought work in storage, and a link in the cart is no licence to throw it
     away; for them the flag does nothing and the page restores as it always has.

     The parameter is taken off the address afterwards, so a reload or a shared
     URL does not repeat it; the `#product-AI-agent` hash stays, and the browser's
     own anchor scroll is what puts the step on screen. */
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('sdw_next') === '1') {
      url.searchParams.delete('sdw_next');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      /* Unconditional since `D-272`. `D-259` started over only when the restored
         state was landed, to spare a second bottle begun and not yet added; the
         operator's direction is that this button always starts over, so it does. */
      startNextBottle();
    }
  } catch (_) {}

  /* ------------------------------------------------------------------ */
  /* resync on variant switch                                             */
  /* ------------------------------------------------------------------ */
  const resyncAfterRerender = rafThrottle(() => {
    if (_aiSyncing) return;
    /* Misses count as state worth re-rendering. A round where every style failed
       leaves three miss cards and no images, and returning early on the image count
       alone would drop the retries from the grid on the next package change. */
    if (!generatedImages.length && !misses.length) return;
    syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, false);
    syncPropertiesToAllForms(); renderGridIfNeeded(); scheduleRefresh();
    if (selectedImage) syncAllFilesToForms().catch(() => {});
  });

  const scheduleResyncAfterVariantUpdate = () => { setTimeout(() => { if (signal.aborted) return; resyncAfterRerender(); }, 0); };
  on(section.closest('.shopify-section') || document, 'variant:update', scheduleResyncAfterVariantUpdate);

  const isWithinGeneratorUi = (node) => {
    if (!(node instanceof Node)) return false;
    const modalRoot = getMobileModal()?.rootEl || null;
    return [section, loadingBox, modalRoot].some((rootEl) => rootEl instanceof Node && (node === rootEl || rootEl.contains(node)));
  };

  const getMutationElement = (node, fallback) => {
    if (node instanceof Element) return node; if (node?.parentElement instanceof Element) return node.parentElement;
    if (fallback instanceof Element) return fallback; if (fallback?.parentElement instanceof Element) return fallback.parentElement;
    return null;
  };

  const isRelevantProductMutation = (mutation) => {
    const touched = [mutation.target, ...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)].map((n) => getMutationElement(n, mutation.target)).filter(Boolean);
    if (!touched.length) return false;
    /* The Forms embed came off this list with the gate: nothing re-attaches to it
       on a re-render any more, so a mutation inside it is not a reason to re-sync
       the configurator. What is left is the three things a morph actually
       replaces and this file has to answer for. */
    return touched.some((el) => { if (isWithinGeneratorUi(el)) return false; return !!el.closest?.('product-form-component, media-gallery, zoom-dialog'); });
  };

  const watchNode = (node) => {
    if (!node) return null;
    const mo = new MutationObserver((mutations) => { if (!mutations.some(isRelevantProductMutation)) return; resyncAfterRerender(); });
    mo.observe(node, { childList: true, subtree: true });
    signal.addEventListener('abort', () => mo.disconnect(), { once: true });
    return mo;
  };

  watchNode(document.querySelector('product-info') || scopeNode);

  /* ------------------------------------------------------------------ */
  /* D-113 — the round: one press, three styles                           */
  /* ------------------------------------------------------------------ */
  /*
    A **round** is one press of Draw and the three requests it fires, one per
    style, to the same unchanged endpoint. Everything below exists because two
    things about that endpoint are unknown and one is certain.

    Certain: it is a third party's app (`HQ-106`) and nothing on its side changes
    for this to ship — `style` is already a request field.

    Unknown, and unanswered as of writing: whether it tolerates three requests from
    one visitor at once (question 1 of the email in
    `docs/GEMINI_APP_HANDOVER_EMAIL.md`). So the parallel path is built *and* the
    sequential fallback is built, and the answer decides which one runs rather than
    whether the flow works.

    Unknown, and cheaper to assume the worst of: how it behaves when one of three
    fails. Partial failure is the normal case now rather than an exception, so a
    round that returns two labels is a round that succeeded.
  */

  /* Three requests each carrying the photograph is up to 150 MB off a phone at the
     50 MB ceiling this control already enforces (`D-231`). The photograph is downscaled
     **once**, before the round, and the same blob is sent three times.

     1568, not a round number, because it is the app's. Its owner confirmed
     (2026-09-28) that the app resizes every photo to `MAX_INPUT_DIM = 1568` before
     Gemini sees it and exports at 1024, so a larger edge from here buys nothing, and
     1600 meant a second resample of 2% on the far side. Matching it sends the pixels
     the model actually gets. The same message confirmed the app refuses anything
     over 8 MB (`MAX_UPLOAD_BYTES`) — which this downscale is what keeps us under;
     see `APP_REJECTED_TOO_LARGE`. */
  const DOWNSCALE_MAX_EDGE = 1568;
  const DOWNSCALE_QUALITY = 0.85;
  const DOWNSCALE_FLOOR_BYTES = 900 * 1024;

  const REQUEST_TIMEOUT_MS = 90000;
  /* **The round has a deadline as well as each request.** The 90s abort is
     per-request, and three in flight need a round-level one too: without it a
     single hung request holds the overlay up while two finished labels sit behind
     it, ready. The grace arms on the *first* arrival and is the stated expectation
     over again, so a genuinely slower sibling still lands; past it the straggler is
     aborted and becomes a miss the visitor can retry. */
  const ROUND_GRACE_MS = 45000;
  /* The absolute ceiling, armed at the press. It bounds the sequential fallback
     too, which is the one path that can otherwise run three 90s requests end to
     end. */
  const ROUND_MAX_MS = 150000;
  const RETRY_BACKOFF_MS = 1500;
  /* `D-273`: how many silent retries a server error or a dropped connection gets,
     and the pause before each. Longer each time, because the cause most likely to
     pass is a connection coming back or the app catching its breath, and neither
     happens in a second and a half twice running. */
  const TRANSIENT_RETRIES = 2;
  const TRANSIENT_BACKOFF_MS = [1500, 4000];

  /*
    Returns `{ data, name }` — a JPEG small enough to send three times, or the
    original file untouched.

    **Every failure path returns the original rather than failing.** Several
    browsers cannot decode HEIC, which is what the camera on most of this traffic
    produces, and a downscale is an optimisation: a flow that refuses to draw
    because it could not shrink a photograph has traded the product for the
    optimisation. The original is also returned when the re-encode comes out no
    smaller, which is what happens on a photograph that is already small.

    `_Original Photo` on the cart line is **not** this blob: it is prepared by
    `prepareOriginalForCart`, which sends the real file at the real resolution up
    to `CART_FILE_BYTES` and a 4000px re-encode above it (`D-231`). The decode
    machinery both use, and the deadline that bounds it, live beside that function
    now, because it is declared first.
  */
  const preparePhoto = async (file) => {
    if (!file) return null;
    const fallback = { data: file, name: file.name || 'photo' };
    if (file.size <= DOWNSCALE_FLOOR_BYTES) return fallback;

    const blob = await reencodeJpeg(file, DOWNSCALE_MAX_EDGE, DOWNSCALE_QUALITY, DOWNSCALE_TIMEOUT_MS);
    if (!blob || blob.size >= file.size) return fallback;
    return { data: blob, name: 'photo.jpg' };
  };

  /* The named failures, kept as they were: "Generation failed" tells a visitor who
     has just waited nothing about whether to try again, change the photo, or come
     back later, and each of these has a different answer. */
  /* `D-231` — **the app's own "too large" is reworded, because its number would
     contradict the page.** The app refuses a photo over 8 MB with a `400` and
     "Photo too large (max 8MB)" (its owner's words, 2026-09-28). That only reaches
     a visitor when `preparePhoto` could not shrink the file — a HEIC in a browser
     that cannot decode it, or a decode past its deadline — and sent the original.
     Passed through verbatim it tells someone this control just accepted a 20 MB
     file from that there is an 8 MB limit, and nothing they can do about it. The
     honest version: this browser could not shrink it, and a screenshot is small.
     Matched on the words as well as the status, since the exact shape of the body
     is the app's and not ours: the JSON fields if it parsed, the raw text if it did
     not. The raw text is only ever *tested* here, never shown — a non-JSON body can
     be a whole HTML error page. A `413` from anywhere in between means the same. */
  const TOO_LARGE_MESSAGE = 'This browser could not shrink that photo, and it is too large to send as it is. Try a screenshot of it instead.';
  const APP_REJECTED_TOO_LARGE = (statusCode, data, raw) =>
    statusCode === 413 ||
    (statusCode === 400 && /too large/i.test(String((data && (data.error || data.message)) || raw || '')));

  const messageForStatus = (statusCode, data, raw) => {
    if (APP_REJECTED_TOO_LARGE(statusCode, data, raw)) return TOO_LARGE_MESSAGE;
    if (statusCode === 422) return 'We could not find a dog in that photo. Try one with their face forward.';
    if (statusCode === 429) return 'We are making a lot of labels right now. Give it a minute and try again.';
    if (statusCode >= 500) return 'Our end failed, not yours. Try again — your photo and name are still here.';
    return (data && data.error) || 'The label did not come back. Try again.';
  };

  /* One request. Never throws: a round is built out of these and a rejection in one
     must not take the other two with it, so every outcome comes back as a value. */
  const requestOneStyle = async (style, photo, pending) => {
    const ac = typeof AbortController === 'function' ? new AbortController() : null;
    let timedOut = false;
    const handle = ac
      ? setTimeout(() => { timedOut = true; try { ac.abort(); } catch (_) {} }, REQUEST_TIMEOUT_MS)
      : null;
    if (ac) pending.set(style, ac);

    try {
      const fd = new FormData();
      fd.append('photo', photo.data, photo.name);
      fd.append('petName', nameInput?.value || '');
      fd.append('style', style);
      fd.append('productId', getProductId());
      fd.append('productHandle', getProductHandle());

      const res = await fetch('/apps/gemini/generate', {
        method: 'POST',
        body: fd,
        ...(ac ? { signal: ac.signal } : {}),
      });
      const raw = await res.text();
      let data = null;
      try { data = JSON.parse(raw); } catch (_) {}

      if (!res.ok || !data?.ok) {
        return { style, ok: false, status: res.status, message: messageForStatus(res.status, data, raw) };
      }
      const url = data.images?.[0]?.url;
      if (!url) return { style, ok: false, status: res.status, message: 'The label did not come back.' };
      return { style, ok: true, url };
    } catch (err) {
      const aborted = err?.name === 'AbortError' || err?.name === 'TimeoutError';
      return {
        style,
        ok: false,
        status: 0,
        aborted,
        message: aborted
          ? (timedOut
              ? 'It took too long and we stopped waiting.'
              : 'Your other labels were ready, so we stopped waiting for this one.')
          : 'We could not reach our end. Check your connection and try again.',
      };
    } finally {
      if (handle) clearTimeout(handle);
      pending.delete(style);
    }
  };

  /* Only one round at a time. The button is disabled for the length of one, and the
     miss cards' retries are buttons too — this is what keeps a fast double-tap on a
     retry from firing the same style twice into the same round. */
  let roundInFlight = false;

  const recordArrival = (result, round) => {
    generatedImages.push({ url: result.url, style: result.style, round, createdAt: Date.now() });
  };

  /* A failure worth trying again without asking (`D-263`): the server's (`5xx`),
     or a connection that dropped (`status` 0 without an abort). Not `422` (no dog),
     `413` (too large) or `429` (which has its own queue), and not an abort. */
  const isTransientMiss = (r) => !!r && !r.ok && !r.aborted && (r.status >= 500 || r.status === 0);

  const recordMiss = (result, round) => {
    misses.push({ round, style: result.style, message: result.message || '', status: result.status || 0 });
  };

  /*
    Runs one round and returns a `Map` of style to outcome.

    The shape is: fire every style at once; a style that comes back `429` is **not
    settled** — it is queued and redrawn one at a time with a backoff, because
    retrying all three in parallel is retrying into the same wall. Everything else
    settles where it lands. The bar advances once per style, at the moment that
    style is finally settled, which is why a queued 429 does not advance it twice.
  */
  const runRound = async (styles, round, photo) => {
    const results = new Map();
    const pending = new Map();
    const queued = [];
    /* `D-263`: styles that failed on the server or the connection, held for one
       more try together. Keyed to the first failure, which is what settles if the
       round runs out of time before the second try can go. */
    const again = new Map();

    let expired = false;
    let graceTimer = null;

    const abortPending = () => {
      for (const ac of pending.values()) { try { ac.abort(); } catch (_) {} }
    };

    const maxTimer = setTimeout(() => { expired = true; abortPending(); }, ROUND_MAX_MS);

    const armGrace = () => {
      if (graceTimer || styles.length < 2) return;
      graceTimer = setTimeout(() => { expired = true; abortPending(); }, ROUND_GRACE_MS);
    };

    const settle = (result) => {
      results.set(result.style, result);
      markProgress();
      if (result.ok) { recordArrival(result, round); armGrace(); }
      else recordMiss(result, round);
    };

    await Promise.all(
      styles.map(async (style) => {
        const result = await requestOneStyle(style, photo, pending);
        /* A refusal is the one outcome worth a second attempt, and it has to be a
           sequential one. Held back rather than settled, so the bar does not count
           it twice and the grid does not flash a miss that is about to be retried. */
        if (result.status === 429 && !expired) { queued.push(style); return; }
        if (isTransientMiss(result) && !expired) { again.set(style, result); return; }
        settle(result);
      })
    );

    /* **A server error or a dropped connection gets one more try, together, before
       anyone sees a retry card. `D-263` (2026-10-08).** The client's phone came
       back with one label and two "Our end failed, not yours" — `5xx` from the app
       for two of three simultaneous requests. Nobody here can see why (the app is
       a third party's, `HQ-106`): the image model refusing three at once and the
       app reporting it as a `500`, a prompt that returned no image, a timeout
       behind the proxy. Several of those pass on a second try, and the visitor's
       own retry was exactly that second try, one card at a time.

       So the theme makes it, once, with the overlay still up: the failed styles
       go again *together* after the same backoff the `429` queue uses, because
       the client wants the three drawn side by side whenever that works. If the
       second try is itself a `429`, that style joins the one-at-a-time queue
       below, which is the established answer to a refusal. Anything else settles
       where it lands — a second `5xx` is a retry card, and a full draw where
       nothing landed at all stays on screen 1 (`D-262`).

       Held back rather than settled, as the `429`s are, so the bar counts each
       style once and the grid never flashes a miss that is about to be retried.
       An abort is not transient: it is the round's own deadline or the visitor's
       other labels arriving, and retrying it would defeat the deadline. */
    /* **Two silent retries now, not one. `D-273` (2026-10-09).** The operator was
       still seeing "We could not reach our end" — which is the second failure under
       `D-263` — and asked for invisible retries with the error shown only once they
       had failed too. So a style that fails on the server or the connection goes
       again up to `TRANSIENT_RETRIES` times, together with whichever others failed
       the same way, after a longer pause each time (`TRANSIENT_BACKOFF_MS`), with
       the overlay up and its estimate raised (`extendEstimate`). Only a style that
       has failed every attempt settles as a miss, and only then does the visitor see
       the error (on screen 1 if nothing landed, `D-262`, or a retry card). The round's
       own deadlines still hold: a style still failing when `expired` is set settles
       with its last failure rather than waiting for another try. */
    let retries = 0;
    while (again.size && retries < TRANSIENT_RETRIES) {
      retries += 1;
      if (retries === 1) announce('A couple of labels need another go.', { quiet: true });
      extendEstimate(retries);
      if (!expired) await sleep(TRANSIENT_BACKOFF_MS[retries - 1] ?? RETRY_BACKOFF_MS);
      const wave = [...again.entries()];
      again.clear();
      await Promise.all(
        wave.map(async ([style, last]) => {
          if (expired) { settle(last); return; }
          const result = await requestOneStyle(style, photo, pending);
          if (result.status === 429 && !expired) { queued.push(style); return; }
          if (isTransientMiss(result) && !expired && retries < TRANSIENT_RETRIES) { again.set(style, result); return; }
          settle(result);
        })
      );
    }
    for (const last of again.values()) settle(last);
    again.clear();

    if (queued.length) {
      queued.sort((a, b) => styleRank(a) - styleRank(b));
      announce('Making the rest one at a time.');
      for (const style of queued) {
        if (expired) {
          settle({ style, ok: false, status: 429, message: 'We ran out of time waiting for a turn. Try this one again.' });
          continue;
        }
        await sleep(RETRY_BACKOFF_MS);
        if (expired) {
          settle({ style, ok: false, status: 429, message: 'We ran out of time waiting for a turn. Try this one again.' });
          continue;
        }
        settle(await requestOneStyle(style, photo, pending));
      }
    }

    clearTimeout(maxTimer);
    if (graceTimer) clearTimeout(graceTimer);
    return results;
  };

  /*
    The whole press: validate, prepare the photograph once, run the round, and
    resolve onto the bottle.

    `styles` is the three for a Draw and one for a miss card's retry, which is why
    this is one function rather than two — a retry is a round of one and everything
    about how a round resolves is the same.
  */
  /* **The round landing is the transition to the results screen**, and it has two
     callers because a round can end two ways.

     On a phone the labels have a screen of their own — `data-sdw-step="10"` on
     `.ai-results` — and the point of Next being the generate press is that the wait
     *is* the move between screens: the overlay comes down on the labels rather than
     on the form that asked for them.

     **It is called on the all-failed path too, and that was a defect.** This lived
     inline in the success branch, below an early `return` taken when no style came
     back — so a round where all three failed rendered its three retry cards onto a
     screen the visitor was not on and left them looking at the form, needing a
     press of Next to find out what happened. Reported from the flow. The brief's
     own note says that screen must be reachable on that path; it is the path that
     most needs to be.

     `renderGridIfNeeded()` has run by both call sites, so `.ai-results` has lost
     its `hidden` and `liveSteps()` counts the screen for the first time. Above the
     breakpoint, and with the flow off, nothing listens. */
  const revealResults = () => {
    if (!results || !document.documentElement.hasAttribute('data-sdw-flow')) return false;
    try {
      results.dispatchEvent(new CustomEvent('sdw:reveal', { bubbles: true, detail: { scroll: true } }));
      return true;
    } catch (_) {
      return false;
    }
  };

  const draw = async ({ styles, round, retryOf = null }) => {
    if (roundInFlight) return;
    if (!validateBeforeGenerate()) return;

    roundInFlight = true;

    /* **A full press closes the fields again; a miss card's retry does not.**
       The visitor has just used the photo and the name, and what they are waiting
       for is the three labels — so the step goes back to leading with the choice
       the moment this round lands. A retry never opened the fields, so it has
       nothing to put back. `D-115`. */
    if (!retryOf) fieldsExpanded = false;

    if (btn) btn.disabled = true;
    for (const el of section.querySelectorAll('.ai-miss__retry')) el.disabled = true;
    if (chooseLabel) chooseLabel.hidden = true;
    clearStatus();

    /* Said before the overlay goes up, so a screen-reader user is told the wait has
       started rather than losing the page to a silent full-viewport layer. */
    announce(
      retryOf
        ? `Making the ${styleLabel(retryOf)} label again.`
        : 'Three label designs, on the way. About 20 seconds.'
    );
    showLoading(styles.length);

    /*
      **A Draw replaces the selection; a retry does not.** A press of Draw produces
      the three the visitor is about to choose between, so the old choice steps
      aside. A miss card's retry is refilling one cell of a trio they may already
      have chosen from — taking their choice away to redraw a card they did not
      pick would be the flow overruling them.

      Either way the previous selection is *held*, not discarded: a round where
      every style fails must leave the visitor exactly where they were, with the
      label they had still on the bottle and still in the cart properties. Losing a
      chosen label to a failed redraw is the worst outcome this flow has.
    */
    const previousSelection = selectedImage;
    if (!retryOf) {
      selectedImage = null;
      _selectedImageFile = null; _selectedImageFileUrl = null; _selectedImageFilePromise = null;
      _selectedPdfFile   = null; _selectedPdfFileUrl   = null;
    }

    syncPropertiesToAllForms(); saveStateToLS();

    try {
      const photo = await preparePhoto(photoInput?.files?.[0]);
      if (!photo) throw new Error('Add your dog’s photo.');

      const results = await runRound(styles, round, photo);
      /* The round is over and these fields are what it ran on, whatever it
         returned. Recorded before the branch below, so a round where every style
         failed does not leave `pending()` true against inputs that have already
         been spent — the visitor retries a miss, or changes the dog, rather than
         pressing Next into the same request. */
      markInputsDrawn();
      announceGenState();

      const landed = styles
        .map((style) => results.get(style))
        .filter((r) => r && r.ok)
        .sort((a, b) => styleRank(a.style) - styleRank(b.style));
      const failed = styles
        .map((style) => results.get(style))
        .filter((r) => r && !r.ok)
        .sort((a, b) => styleRank(a.style) - styleRank(b.style));

      /* **Only the failure paths take the overlay down here. `D-261`.** A round that
         landed keeps it up until `revealResults()` has moved the phone to the results
         screen, further down; see the note there. */
      if (!landed.length) hideLoading();

      /* The dog-only rejection is about the photograph, not about a style, so it is
         only true when *every* style came back with it. One 422 beside two labels
         would be the app disagreeing with itself, and popping the dog panel over
         two finished labels would be worse than saying nothing. */
      if (landed.length === 0 && failed.some((r) => r.status === 422)) showDogOnlyPopup(4000);

      /* **No dog: the press goes nowhere, and the photograph goes. `D-260`.**
         This used to fall through to the all-failed path below, which moves to the
         results screen and lays out three retry cards — each saying "We could not
         find a dog in that photo" and each offering to send the same photograph
         again, which can only be refused again. The client, trying it with a photo
         of something else, read that as being sent to the labels with an error, and
         asked for the opposite: stay on the photo step and clear it.

         So the round leaves no trace. Its misses are dropped (they are the retry
         cards), the selection a previous round made is put back as the failure
         path below does, and the file goes through `rejectFile` — the same
         clearing a file the browser cannot read gets, with the reason on the photo
         field where the next act is. The name stays; it was not what was wrong.
         `revealResults` is not called, so a phone stays on screen 1, and the
         popup is still the first thing said.

         **A miss card's retry is excluded.** That photograph has already drawn
         labels, so a 422 on one style is the app disagreeing with itself, not a
         photo of a cat, and clearing the field out from under the results the
         visitor is looking at would be the wrong answer to it. */
      if (!retryOf && landed.length === 0 && failed.some((r) => r.status === 422)) {
        for (let i = misses.length - 1; i >= 0; i--) {
          if ((Number(misses[i]?.round) || 0) === round) misses.splice(i, 1);
        }
        selectedImage = previousSelection;
        syncPropertiesToAllForms();
        renderGridIfNeeded();
        if (selectedImage) syncAllFilesToForms().catch(() => {});
        rejectFile(null, failed[0]?.message || 'We could not find a dog in that photo. Try one with their face forward.');
        saveStateToLS();
        window.SDW_TRACK?.('generation_complete', {
          drawn: generatedImages.length,
          styles: [],
          failed: failed.length,
          round,
          rejected: 'no_dog',
        });
        return;
      }

      if (!landed.length) {
        /* Nothing came back. The existing error path, unchanged in kind: the
           overlay is down, the named failure is in the live region, and focus goes
           to the button the visitor will reach for.

           **The selection goes back first.** The visitor may have had a label
           chosen before they pressed Draw again, and a failed round is not a reason
           to empty their cart properties — they should be able to walk away from
           the failure and buy the label they already liked. */
        selectedImage = previousSelection;

        /* **A full draw that got nothing back stays on screen 1. `D-262`
           (2026-10-08).** It used to move a phone to the results screen and lay out
           three retry cards there, one per style — "We could not reach our end"
           three times over, each offering to redraw its own style alone. The
           client, on a phone that lost the connection, asked for the opposite: only
           move to the labels once labels exist, and when trying again, try all
           three together.

           So the round leaves no trace, as the no-dog path above does, but the
           photograph and the name stay, because nothing was wrong with them. Its
           misses are dropped, so `.ai-results` stays hidden and is not a live step.
           The input signature is forgotten, so `SDW_GENERATE.pending()` is true
           again and the next press of Next is a fresh round of three rather than an
           advance: the `markInputsDrawn()` above is right for a round that produced
           something to advance to, and this one did not. The error is said in
           `.ai-status`, which lives on screen 1, under the button that retries.

           A miss card's retry keeps the old shape: the visitor is already on the
           results screen, and the card that failed is the one to press again. */
        if (!retryOf) {
          for (let i = misses.length - 1; i >= 0; i--) {
            if ((Number(misses[i]?.round) || 0) === round) misses.splice(i, 1);
          }
          drawnSignature = null;
          announceGenState();
        }

        syncPropertiesToAllForms();
        renderGridIfNeeded();
        saveStateToLS();
        if (selectedImage) syncAllFilesToForms().catch(() => {});
        showError(failed[0]?.message || 'The labels did not come back. Try again.');
        /* A retry's cards are on the results screen, so that is where the visitor
           has to be to use them; a full draw's error is on screen 1, beside the
           button that sends the three again. */
        if (retryOf) {
          if (!revealResults()) { try { btn?.focus({ preventScroll: true }); } catch (_) {} }
        } else {
          try { btn?.focus({ preventScroll: true }); } catch (_) {}
          /* Screen 1 is taller than a phone: the photo and the name push
             `.ai-status` down under the pinned step bar, where the one sentence
             that says what happened was half hidden (measured at 390 x 844). */
          try { status?.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' }); } catch (_) {}
        }
        window.SDW_TRACK?.('generation_complete', {
          drawn: generatedImages.length,
          styles: [],
          failed: failed.length,
          round,
        });
        return;
      }

      /* **The reveal resolves onto the visitor's own bottles**, and what lands is
         the first style in *display* order that succeeded — never the one that
         happened to answer first. The settle plays once, here. Choosing a different
         card afterwards is a comparison and swaps instantly.

         A retry that lands beside a label the visitor already chose does **not**
         take the bottle: it fills its cell and waits to be picked. The reveal is
         the moment a flow with no label gets one, and that has already happened. */
      const winner = landed[0];
      const adopt = !selectedImage;
      if (adopt) selectedImage = winner.url;

      if (adopt) {
        if (stage()) {
          stage().setStyle(winner.style || NEUTRAL_GROUND);
          stage().setLabel(winner.url, { settle: true });
        } else {
          openMobilePreview(selectedImage);
          setTimeout(closeMobilePreview, 5000);
        }
      }

      syncPropertiesToAllForms();
      syncAiImagesToHorizonCarousel(generatedImages.map((x) => x.url), selectedImage, true);

      /* `await syncAllFilesToForms()` stood here and now runs last. `D-261`. */

      generationCount = generatedImages.length;
      saveStateToLS();
      renderGridIfNeeded(); scheduleRefresh();

      showGenerationToast({ contactUrl: window.__AI_CONTACT_URL || '/pages/contact' });

      /* P7-01 — the result gets the focus, and it gets announced. A generated label
         used to appear in a grid below the button with no focus move and nothing
         said, on the one moment `PRODUCT.md` calls the emotional peak of the
         product. Focus goes to the card the script has just selected, so the next
         Tab is the package choice rather than a walk back through the form.

         **Partial failure is said plainly, and it names the style.** A visitor who
         asked for three and got two is owed the arithmetic; leaving them to count
         the cards is how a partial round reads as a broken one. */
      if (failed.length) {
        const names = failed.map((r) => styleLabel(r.style)).join(' and ');
        announce(
          `${landed.length} of ${styles.length} came back. ${names} didn’t — you can try ${failed.length > 1 ? 'them' : 'it'} again.`
        );
      } else if (styles.length > 1) {
        /* **Spoken, not printed.** `results_title` is the whole sentence now —
           "Your labels are ready! Swipe through to pick your favorite." — and it
           renders directly above the grid on this exact event, so a visible status
           line here was the same news twice, forty pixels apart. It is still said,
           because the heading is not a live region and a screen-reader user is owed
           the end of a forty-second wait; `quiet` is what keeps it in the accessibility tree
           and off the page. */
        announce(chooseLabelText(), { quiet: true });
      } else {
        announce(
          adopt
            ? `Your ${styleLabel(winner.style)} label is ready.`
            : `Your ${styleLabel(winner.style)} label is ready, beside the one you chose.`
        );
      }

      /* **The round landing is the transition to the results screen.** On a phone
         the labels have a screen of their own — `data-sdw-step="10"` on
         `.ai-results` — and the whole point of Next being the generate press is
         that the wait *is* the move between screens: the overlay comes down on the
         labels rather than on the form that asked for them.

         `renderGridIfNeeded()` above has just cleared `.ai-results`'s `hidden`, so
         `liveSteps()` counts the screen for the first time and this is the earliest
         moment the step can be set at all. `sdw:reveal` is the hook that already
         exists for exactly this — the add-to-cart guard and the recap's goto links
         use it — and `assets/buy-flow-steps.js` answers it by bringing the step up.
         `scroll` is asked for here and by nothing else, because this is the one
         caller with no control to scroll to afterwards: the screen is the
         destination.

         Above the breakpoint, and with the flow off, nothing listens and this is an
         event into an empty room. A retry from a miss card dispatches it too and it
         is a no-op — the visitor is already on this screen and `setStep` only
         announces and scrolls on a change. */
      const steppedToResults = revealResults();

      /* **The overlay comes down after the step has changed, not before. `D-261`
         (2026-10-08).** It came down the moment the labels arrived, and the screen
         change waited behind `await syncAllFilesToForms()` — fetching the chosen
         label, waiting on the pack composite, building the PDF — so on a phone the
         visitor was left on screen 1 for a couple of seconds after the wait had
         visibly ended, and then jumped. The client reported exactly that.

         Two changes, one cause. The sync moved to the end of this branch: nothing
         between here and there reads the files — the grid, the stage, the
         properties and the step all run off `selectedImage` — and the add to cart
         already waits on a sync still in flight, the same reason the card-click
         handler stopped awaiting it first. And the overlay comes down here, after
         `sdw:reveal` has brought the results screen up beneath it, so what it lifts
         off is the labels. That is what the note above `revealResults` always said
         should happen. */
      hideLoading();

      const chosenCard =
        section.querySelector('.ai-option[aria-checked="true"]') || section.querySelector('.ai-option');
      if (chosenCard) {
        try { chosenCard.focus({ preventScroll: true }); } catch (_) { try { chosenCard.focus(); } catch (_) {} }
        /* **Skipped when the step change has just placed the screen.** Two scrolls
           on one press fight: the step change puts the top of the flow on screen
           and this would immediately drag it back to centre a card that is already
           the only thing on it. The focus still moves — `preventScroll` is what
           makes that safe — so the next Tab is still the package choice. */
        if (!steppedToResults) {
          try {
            chosenCard.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          } catch (_) {}
        }
      }

      updateRedrawState();
      window.SDW_TRACK?.('generation_complete', {
        drawn: generatedImages.length,
        styles: landed.map((r) => r.style),
        failed: failed.length,
        round,
      });

      /* Last, behind the results screen. Still awaited, so the round stays in
         flight (`finally` below) until the cart's files match the label on screen. */
      await syncAllFilesToForms();
    } catch (err) {
      hideLoading();
      showError(err?.message || 'Something went wrong. Try again.');
      try { btn?.focus({ preventScroll: true }); } catch (_) {}
    } finally {
      roundInFlight = false;
      /* Unconditional. The only reason this button is ever disabled now is a round
         already in flight, and this is where that round ends — success or failure. */
      if (btn) btn.disabled = false;
      /* The grid is re-rendered from scratch on every settle, so the retries this
         re-enables are the ones the current state actually has. */
      renderGridIfNeeded();
      for (const el of section.querySelectorAll('.ai-miss__retry')) el.disabled = false;
    }
  };

  /* A miss card's retry. It redraws **only** that style, and the result lands back
     in the trio it belongs to rather than starting a new one — which is what makes
     a retried cell fill its own hole instead of appearing at the top of the grid
     away from the two it was drawn beside. */
  redrawStyle = async (miss) => {
    if (roundInFlight) return;
    const index = misses.indexOf(miss);
    if (index >= 0) misses.splice(index, 1);
    renderGridIfNeeded();
    await draw({ styles: [miss.style], round: Number(miss.round) || 0, retryOf: miss.style });
  };

  /* **Nothing but the visitor's own two fields stands between here and the draw**,
     and that is unchanged: `validateBeforeGenerate` wants a photo and a name, both
     of which the label cannot be drawn without. What came out is the style question
     — there is nothing to choose and nothing to validate.

     A press appends a trio rather than replacing one. There is no cap, and the grid
     is an N-card radiogroup, so a visitor who liked their second label and wants to
     see other takes keeps it. */
  const pressGenerate = () => {
    /* A `HANDOFF_URL` branch stood at the top of this function and sent a capture-only
       press to another page instead of drawing. It was here, rather than at each
       caller, so the visible button and the mobile flow's Next
       (`window.SDW_GENERATE.press()` below, the flow's only entry point into a press)
       could not diverge. Both callers now reach the one thing this function does
       (`D-221`), which is the arrangement the branch was protecting. */
    const round = nextRound;
    nextRound += 1;
    void draw({ styles: [...STYLE_ORDER], round });
  };

  if (btn) on(btn, 'click', pressGenerate);

  /* **Coming back to this page must not find the button disabled.** A phone that
     returns here with the back button is handed the browser's own copy of the page:
     the DOM exactly as it was at navigation, scripts not re-run. `draw` disables this
     control while a round is in flight, so a restore taken mid-draw hands back a dead
     button with nothing left running to re-enable it — from the page's point of view
     nothing has happened.

     `pageshow` is the one event that fires on a restore as well as on a load, and it
     is answered unconditionally rather than behind `event.persisted`: on an ordinary
     load the button is enabled already and the write is a no-op, so testing for the
     restore only narrows what this can repair.

     **Back to its pre-`D-218` form** (`D-221`). `D-218` routed this through
     `setHandoffBusy` so the spinner and the busy wording came off with the `disabled`;
     with the hand-off deleted there is no busy state to clear and the one line is the
     whole of what a restore needs. The reason the handler exists at all has not
     changed, which is why it stays rather than going with the rest of it. */
  on(window, 'pageshow', () => {
    if (btn) btn.disabled = false;
  });

  /* ------------------------------------------------------------------ */
  /* the stepped flow's Next, on the first screen                        */
  /* ------------------------------------------------------------------ */
  /*
    On a phone the first screen is the photograph, the name and nothing else, and
    **Next is the generate press**: it validates both fields, fires the three
    requests and lets the full-viewport overlay play over the whole screen, so the
    wait is the transition between screens rather than an event that finishes on a
    screen the visitor then has to leave.

    `assets/buy-flow-steps.js` is deliberately ignorant of generation — it writes
    three attributes on <html> and nothing else — so the press is published here and
    it calls this. Two functions, and the split matters: `pending()` is the question
    the flow asks *before* deciding what Next means, and `press()` is what it does.

    **`pending()` is false the moment any label exists**, which is what makes the
    landed and same-dog states safe. A visitor who has added a bottle to the cart
    still has their photograph and their name on screen (`D-143`) and three labels
    in the grid; pressing Next there must advance to the choice, not spend a draw
    on a decision already made. Only the button on that screen — "Make three more" —
    and a miss card's retry draw again, and both of those are explicit.

    Misses are not enough on their own. A round where all three failed leaves
    `generatedImages` empty and three retry cards on the results screen, so
    `pending()` is true and Next presses again: that is the right answer, because
    the visitor has no label and asking for one is the only thing the screen is for.

    `press()` reports whether the round started, which is the honest signal for a
    caller that has to decide whether to move. `validateBeforeGenerate` inside
    `draw` writes the two field messages in place and `assets/generator.css` exempts
    a step carrying a live `[data-ai-error]` from being hidden, so a rejected press
    is read on the screen it happened on.
  */
  window.SDW_GENERATE = {
    pending: () => generatedImages.length === 0 || inputSignature() !== drawnSignature,
    /* **Whether the first screen's forward control can act**, which is not the same
       question as whether a round is due. Two ways it can: there is nothing left to
       draw, so the press is an ordinary advance; or there is, and both fields the
       draw needs are filled.

       A pure predicate, deliberately — `validateBeforeGenerate` answers the same
       question and *writes the two error messages* doing it, which is right on a
       press and wrong on every keystroke. `assets/buy-flow-steps.js` asks this to
       decide how the button looks; the press still goes through the validating
       one, so the messages appear when the visitor asks for them and not before. */
    ready: () => {
      if (!window.SDW_GENERATE.pending()) return true;
      return !!photoInput?.files?.[0] && !!(nameInput?.value || '').trim();
    },
    press: () => {
      if (roundInFlight) return false;
      if (!validateBeforeGenerate()) return false;
      pressGenerate();
      return true;
    },
    /* *Try again* on the results screen: the same clearing *Use a different dog*
       does, so the visitor lands on an empty first screen rather than on their old
       dog's photograph. It does not touch the labels — those are still theirs until
       a new round appends to them. */
    clearInputs: () => { try { clearDogInputs({ focusPhoto: false, message: '' }); } catch (_) {} },
  };

  /* **The configurator is entered empty, and that is the whole of what stood here.**
     Last in `init` sat the auto-resume: it read a photograph and a name out of the
     hand-off's IndexedDB store, filled the two fields through their own events and
     pressed Generate, so a visitor arriving from the home page's capture step found
     their dog already being drawn. `D-221` removes the step it resumed from, and the
     resume with it — there is no drop-off to read, and a configurator that starts by
     reading storage it no longer writes is a mechanism the next reader has to disprove.

     What the visitor does instead is upload here, which is what the page has always
     been able to do and is now the only thing it does. `hydrateFromLS` still brings
     back labels they have already made; it is the *photograph* that no longer travels,
     because it never could. */

  /* ------------------------------------------------------------------ */
  /* arriving to design a label opens on the photograph                   */
  /* ------------------------------------------------------------------ */
  /*
    **`?pick=1` says "this visitor came here to upload a dog", and this is the whole
    of what it can honestly do about it** (`D-222`).

    `sections/label-cta.liquid` composes the parameter onto the home page's call to
    action. What it asks for is that the configurator open on the photograph, and
    the one thing that cannot be done for it is opening the file dialog. Measured in
    Chrome on arrival, with no gesture in the new document:

        navigator.userActivation  ->  { isActive: false, hasBeenActive: false }
        input.showPicker()        ->  NotAllowedError: requires a user gesture
        input.click()             ->  swallowed, with the console warning
                                      "File chooser dialog can only be shown with a
                                       user activation"

    A click on the previous page does not carry activation across a navigation —
    `hasBeenActive` is false, not merely `isActive` — and Safari and Firefox gate the
    chooser the same way. It is the same boundary that made the deleted hand-off need
    a storage round trip in the first place (`D-221`), and it is not one a theme gets
    to argue with. So nothing here calls `click()` "just in case": a call that is
    guaranteed to be swallowed is a line that only teaches the next reader it works.

    What is left is the tap itself, and this makes that tap the obvious one: focus
    lands on the file input, so a keyboard or screen-reader visitor opens the picker
    with the very next key, and the drop target wears a ring so a thumb has somewhere
    to go. One tap instead of none, and it is the tap the screen was already asking
    for.

    **The parameter is taken off the URL before anything else happens.** It is an
    instruction for one arrival, not a property of the page: left in place it would be
    copied into a share, bookmarked, and sent to analytics as a second URL for the
    same product. `replaceState` rather than `pushState`, so the back button still
    goes to the home page rather than to this page without its parameter, and the
    fragment is carried across so the anchor the visitor arrived at survives the
    rewrite.

    **It is cleaned even when the arming is declined.** The two `return`s below are
    about this visitor's state, not about the URL, and a visitor who already has a
    photograph should not be left holding a link that says they need one.
  */
  (() => {
    let here;
    try { here = new URL(window.location.href); } catch (_) { return; }
    if (here.searchParams.get('pick') !== '1') return;

    here.searchParams.delete('pick');
    try {
      window.history.replaceState(window.history.state, '', `${here.pathname}${here.search}${here.hash}`);
    } catch (_) {}

    if (!input || !dropzone) return;

    /* **Nothing is asked for twice.** A visitor with a photograph in the input has
       done this step; one with labels is past it, and on the desktop rail `D-115`
       has collapsed the fields behind a disclosure, so arming would ring a control
       that is not on screen. The button they pressed to get here says `Choose your
       wines` in exactly that state (`sections/label-cta.liquid`), which is the same
       fact read from the same bucket one page earlier. */
    if (input.files?.[0]) return;
    if (generatedImages.length) return;

    dropzone.setAttribute('data-ai-armed', '');

    /* **It comes off at the first sign of the visitor, whatever that sign is.** The
       ring marks where to go next; once they have gone anywhere it is a decoration
       pointing at a decision already made. `capture` so a control that stops
       propagation cannot leave it lit, and `change` for the path where the picker
       was opened by the keyboard and never produced a pointer event at all.

       Bound before the focus below rather than after it, because the focus is
       deferred and this is what tells it not to bother. */
    let interacted = false;
    const disarm = () => {
      interacted = true;
      dropzone.removeAttribute('data-ai-armed');
    };
    on(input, 'change', disarm);
    on(document, 'pointerdown', disarm, { capture: true, once: true });
    on(document, 'keydown', disarm, { capture: true, once: true });

    /* **The focus waits for the navigation to finish, because the navigation takes
       focus away.** Measured: this file boots from a `defer`red script, so `init`
       runs at `readyState === 'interactive'` — before `DOMContentLoaded`. Focusing
       there works and does not last. The fragment `#product-AI-agent` names a `<div>`,
       which is not focusable, so when the browser processes "scroll to the fragment"
       it sets the sequential focus navigation starting point and resets focus to the
       body — after parsing, and therefore after this ran. The tape was unambiguous:
       `focus()` at 558ms with `document.activeElement` becoming `ai-photo`,
       `DOMContentLoaded` at 686ms, and `document.body` holding focus by the time
       anyone could look.

       So the call goes after `load`, one frame later, which is the other side of
       that reset. The ring is set immediately and only the focus waits — the mark a
       visitor sees does not depend on the slowest image on the page.

       `preventScroll` because the fragment has already put the configurator where it
       belongs, under a sticky header, with the offset `D-39` measured. A focus call
       that scrolls would undo that in the frame the visitor is watching.

       **Two guards, because `load` can be seconds late on a phone.** A visitor who has
       already touched the page has said where they want to be, and moving focus out
       from under them is worse than never having moved it; and focus that has landed
       on something real belongs to whatever put it there. Only a page still resting on
       `body` gets it taken. */
    const takeFocus = () => {
      if (interacted) return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement) return;
      try { input.focus({ preventScroll: true }); } catch (_) { try { input.focus(); } catch (_) {} }
    };
    if (document.readyState === 'complete') window.requestAnimationFrame(takeFocus);
    else on(window, 'load', () => window.requestAnimationFrame(takeFocus), { once: true });
  })();
};

/* Boot */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.AI_PET_GENERATOR.init(document));
} else {
  window.AI_PET_GENERATOR.init(document);
}